import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import JSZip from 'jszip';
import {
  Download, Plus, Trash2, Copy, Check, AlertCircle, Package, Wrench, GitBranch,
  FileText, Settings, Users, Zap, Info, Eye, Layers, Cpu, Box, RefreshCw,
  Upload, AlertTriangle, CheckCircle2, X, FileSpreadsheet, FileJson,
  Cog, Factory, Microscope, Flame, Activity, TrendingUp, PieChart,
  BarChart3, Target, Clock, Database, Server, Cloud, Link2, Unlink, Network,
  Share2, Maximize2, Minimize2, Search, Filter, List, Sliders,
  Gauge, Wifi, Battery, Sun, Moon, Star, Award, BookOpen, ChevronDown, 
  ChevronRight, ChevronUp, ArrowUpRight, ExternalLink, Save, RotateCcw, 
  ZoomIn, ZoomOut, Table2, Code, Home, Move, GitMerge, Workflow, Route,
  MapPin, Navigation, Compass, Anchor, Crosshair, Hash, AlertOctagon, Ban, 
  ShieldOff, Lock, Unlock, Key, Fingerprint, Scan, TreePine, GitFork, 
  ArrowLeftRight, Columns, GanttChartSquare, Kanban, LayoutDashboard, Edit3, 
  Wand2, Sparkles, GitGraph, FolderTree, Shield, ShieldCheck, ShieldAlert, 
  GitPullRequest, GitCommit, FileCode, Archive, FolderArchive, Filter as FilterIcon,
  Search as SearchIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, Maximize, 
  Minimize, Grid, List as ListIcon, TrendingDown, TrendingUp as TrendingUpIcon,
  Activity as ActivityIcon, BarChart, PieChart as PieChartIcon, ArrowUp, ArrowDown, 
  ArrowLeft, ArrowRight, Move as MoveIcon, Compass as CompassIcon, Map, Globe, Flag,
  Navigation as NavigationIcon, Anchor as AnchorIcon, Ship, Truck, Plane, Train, 
  Bus, Car, EyeOff, RotateCw, RotateCcw as RotateCcwIcon, Volume2, VolumeX,
  Play, Pause, SkipForward, SkipBack, History, Timer, Hourglass, Calendar, 
  Clock as ClockIcon, CheckCircle, XCircle, HelpCircle, MessageSquare, 
  Mail, Phone, Video, Camera, Image, Music, Film, Tv, Radio, Mic, Headphones,
  Speaker, Monitor, Printer, Scanner, Fax, Copy as CopyIcon, Clipboard, 
  File, Folder, FolderOpen, HardDrive, Cpu as CpuIcon, MemoryStick, 
  Cloud as CloudIcon, Database as DatabaseIcon, Server as ServerIcon, 
  Globe as GlobeIcon, Wifi as WifiIcon, Bluetooth, Battery as BatteryIcon,
  Sun as SunIcon, Moon as MoonIcon, CloudRain, CloudSnow, CloudLightning,
  Wind, Droplets, Thermometer, Compass as CompassIcon2, MapPin as MapPinIcon,
  Navigation as NavigationIcon2, Crosshair as CrosshairIcon, Target as TargetIcon
} from 'lucide-react';




import defaultProduct from './data/defaultProduct.json';
import defaultWorkPackage from './data/defaultWorkPackage.json';
import defaultEBOM from './data/defaultEBOM.json';
import defaultMBOM from './data/defaultMBOM.json';
import defaultBOP from './data/defaultBOP.json';
import defaultBOE from './data/defaultBOE.json';
import defaultTools from './data/defaultTools.json';
import defaultSkills from './data/defaultSkills.json';
import defaultQualityPlans from './data/defaultQualityPlans.json';
import defaultEWI from './data/defaultEWI.json';
import defaultPlants from './data/defaultPlants.json';
import ValidatePLMXML from './components/validatePLMXML';
import DEManyproduct from './DEManyproduct';
import { AiDem, AiAssistant } from './AiDem';
import Report from './report';

//import { DigitalEngineeringGuardrailDashboard } from './guardrails';
//import { DigitalEngineeringGuardrailDashboard } from './guardrails';
import { DigitalEngineeringGuardrailDashboard, GuardrailDashboard } from './guardrails';




// ============================================
// GLOBAL COUNTER
// ============================================
let globalIdCounter = 10000;
const genId = () => `obj-${globalIdCounter++}`;

// ============================================
// THEME
// ============================================
const T = {
  bg:'#0a0e17', s:'#111827', s2:'#1a2235', s3:'#1e293b', b:'#1e293b', ba:'#3b82f6',
  t:'#e2e8f0', t2:'#94a3b8', t3:'#64748b', p:'#3b82f6', pl:'#60a5fa', g:'#10b981',
  gl:'#34d399', w:'#f59e0b', wl:'#fbbf24', d:'#ef4444', dl:'#f87171',
  pp:'#8b5cf6', ppl:'#a78bfa', te:'#14b8a6', tel:'#2dd4bf', pk:'#ec4899', pkl:'#f472b6',
  cy:'#06b6d4', in:'#6366f1', or:'#f97316',
  conventional:'#64748b', casting:'#f59e0b', cnc:'#3b82f6', printing:'#8b5cf6', robotics:'#10b981'
};

// ============================================
// STYLES
// ============================================
const S = {
  container:{minHeight:'100vh',background:`linear-gradient(135deg,${T.bg} 0%,#1a1f2e 50%,#0f1419 100%)`,fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",color:T.t},
  header:{background:`linear-gradient(135deg,${T.s3} 0%,${T.bg} 100%)`,borderBottom:`2px solid ${T.p}`,padding:'6px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'4px',position:'sticky',paddingTop:'70px',zIndex:100},
  hTitle:{fontSize:'1rem',fontWeight:700,background:`linear-gradient(135deg,${T.pl},${T.tel})`,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'},
  btn:{padding:'4px 9px',borderRadius:'4px',border:'none',cursor:'pointer',fontSize:'0.74rem',fontWeight:600,display:'flex',alignItems:'center',gap:'3px',transition:'all 0.2s',whiteSpace:'nowrap'},
  bPri:{background:`linear-gradient(135deg,${T.p},#2563eb)`,color:'white'},
  bSuc:{background:`linear-gradient(135deg,${T.g},#059669)`,color:'white'},
  bWar:{background:`linear-gradient(135deg,${T.w},#d97706)`,color:'white'},
  bDan:{background:`linear-gradient(135deg,${T.d},#dc2626)`,color:'white'},
  bGho:{background:'transparent',color:T.t2,border:`1px solid ${T.b}`},
  bPP:{background:`linear-gradient(135deg,${T.pp},#7c3aed)`,color:'white'},
  bTeal:{background:`linear-gradient(135deg,${T.te},#0d9488)`,color:'white'},
  bCyan:{background:`linear-gradient(135deg,${T.cy},#0891b2)`,color:'white'},
  bPk:{background:`linear-gradient(135deg,${T.pk},#db2777)`,color:'white'},
  bOr:{background:`linear-gradient(135deg,${T.or},#ea580c)`,color:'white'},
  input:{width:'100%',padding:'4px 7px',background:T.bg,border:`1px solid ${T.b}`,borderRadius:'4px',color:T.t,fontSize:'0.77rem',outline:'none'},
  select:{width:'100%',padding:'4px 7px',background:T.bg,border:`1px solid ${T.b}`,borderRadius:'4px',color:T.t,fontSize:'0.77rem',outline:'none',cursor:'pointer'},
  label:{fontSize:'0.64rem',color:T.t2,marginBottom:'1px',fontWeight:500,textTransform:'uppercase'},
  card:{background:T.s2,border:`1px solid ${T.b}`,borderRadius:'7px',padding:'9px',marginBottom:'6px'},
  cHdr:{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'5px',paddingBottom:'4px',borderBottom:`1px solid ${T.b}`},
  cTtl:{fontSize:'0.82rem',fontWeight:600,display:'flex',alignItems:'center',gap:'4px'},
  badge:(c)=>({display:'inline-block',padding:'1px 4px',borderRadius:'3px',fontSize:'0.58rem',fontWeight:600,background:`${c}22`,color:c}),
  grid:(n)=>({display:'grid',gridTemplateColumns:`repeat(${n},1fr)`,gap:'4px'}),
  tbl:{width:'100%',borderCollapse:'collapse',fontSize:'0.7rem'},
  th:{textAlign:'left',padding:'2px 4px',color:T.t2,fontSize:'0.6rem',fontWeight:600,textTransform:'uppercase',borderBottom:`2px solid ${T.b}`,whiteSpace:'nowrap'},
  td:{padding:'2px 4px',borderBottom:`1px solid ${T.b}`},
  side:{width:'180px',background:T.s,borderRight:`1px solid ${T.b}`,padding:'4px 0',overflowY:'auto',flexShrink:0},
  sItem:(a)=>({display:'flex',alignItems:'center',gap:'4px',padding:'5px 9px',cursor:'pointer',fontSize:'0.72rem',color:a?T.t:T.t2,borderLeft:`3px solid ${a?T.p:'transparent'}`,background:a?`${T.p}10`:'transparent',transition:'all 0.2s'}),
  main:{display:'flex',height:'calc(100vh - 48px)'},
  content:{flex:1,padding:'6px',overflowY:'auto'},
  toast:{position:'fixed',bottom:'10px',right:'10px',padding:'7px 11px',borderRadius:'5px',color:'white',fontWeight:600,fontSize:'0.76rem',zIndex:1000,display:'flex',alignItems:'center',gap:'4px'},
  statCard:{background:T.s2,border:`1px solid ${T.b}`,borderRadius:'5px',padding:'6px',textAlign:'center'},
  statVal:{fontSize:'1.1rem',fontWeight:700,color:T.pl},
  statLbl:{fontSize:'0.55rem',color:T.t3,textTransform:'uppercase'},
  modal:{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200},
  modalC:{background:T.s2,borderRadius:'10px',padding:'12px',maxWidth:'98vw',maxHeight:'96vh',overflow:'auto',border:`1px solid ${T.b}`,boxShadow:'0 10px 30px rgba(0,0,0,0.5)'},
  progressBar:{height:'6px',background:T.b,borderRadius:'3px',overflow:'hidden',marginTop:'4px'},
  progressFill:{height:'100%',borderRadius:'3px',transition:'width 0.3s ease'},
  filterBar:{display:'flex',gap:'6px',padding:'6px 8px',background:T.s,borderRadius:'4px',border:`1px solid ${T.b}`,flexWrap:'wrap',alignItems:'center'},
  legend:{display:'flex',gap:'8px',flexWrap:'wrap',padding:'4px 8px',background:T.s,borderRadius:'4px',marginTop:'4px'},
  dendroTooltip:{position:'absolute',bottom:'100%',left:'50%',transform:'translateX(-50%)',background:T.s3,color:T.t,padding:'6px 10px',borderRadius:'4px',fontSize:'0.55rem',border:`1px solid ${T.b}`,boxShadow:'0 4px 12px rgba(0,0,0,0.5)',zIndex:1000,maxWidth:'350px',whiteSpace:'normal',wordBreak:'break-all',marginBottom:'4px',pointerEvents:'none'}
};

// ============================================
// HELPERS
// ============================================
const esc = (s) => (s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

// ============================================
// ENHANCED BOP EXTRACTION HELPERS
// ============================================

const extractAllOperations = (bopData) => {
  if (!bopData) return [];
  const allOps = [];
  
  if (bopData.productBOP) {
    (bopData.productBOP.processes || []).forEach(process => {
      (process.plantBOPs || []).forEach(plantBOP => {
        (plantBOP.functionalGroups || []).forEach(fg => {
          (fg.operations || []).forEach(op => {
            allOps.push({
              ...op,
              id: op.id || `OP-${allOps.length}`,
              functionalGroup: fg.name,
              plant: plantBOP.plant,
              process: process.name,
              processSequence: process.sequence,
              plantBOPName: plantBOP.name
            });
          });
        });
      });
    });
  }
  
  return allOps;
};

const extractFunctionalGroups = (bopData) => {
  if (!bopData) return [];
  const groups = [];
  
  if (bopData.productBOP) {
    (bopData.productBOP.processes || []).forEach(process => {
      (process.plantBOPs || []).forEach(plantBOP => {
        (plantBOP.functionalGroups || []).forEach(fg => {
          groups.push({
            ...fg,
            id: fg.id || `FG-${groups.length}`,
            plant: plantBOP.plant,
            process: process.name,
            plantBOPName: plantBOP.name
          });
        });
      });
    });
  }
  
  return groups;
};


const extractWorkCenters = (bopData) => {
  const allOps = extractAllOperations(bopData);
  return [...new Set(allOps.map(o => o.workCenter || o.wc).filter(Boolean))];
};

const groupByTechnology = (equipment) => {
  const groups={Conventional:[],Casting:[],CNC:[],'3D Printing':[],Robotics:[]};
  const safeEquip = Array.isArray(equipment) ? equipment : [];
  safeEquip.forEach(eq=>{
    const tech=eq.technology||'Conventional';
    if(groups[tech]) groups[tech].push(eq);
    else groups['Conventional'].push(eq);
  });
  return groups;
};

const hasOperationChildren = (op) => {
  return !!(op.machine || op.tools || op.parameter || 
            op.consumes || op.indirectConsumables || op.byproducts ||
            op.skills || op.setupTimeDetail || op.machineRunTime || op.directLaborTime ||
            op.workInstruction || op.inspection ||
            op.sourceLocation || op.output || op.queTime || op.moveTime);
};

// ============================================
// FILE PARSING
// ============================================
const parseFileContent = (file, callback, errorCallback) => {
  const ext = file.name.split('.').pop().toLowerCase();
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      if (ext === 'json') { 
        const data = JSON.parse(e.target.result); 
        callback({ type: 'json', data, sheets: Object.keys(data).filter(k => typeof data[k] === 'object') }); 
      }
      else if (ext === 'csv') { 
        Papa.parse(e.target.result, { 
          header: true, 
          dynamicTyping: true, 
          skipEmptyLines: true, 
          complete: (r) => { 
            callback({ type: 'csv', data: r.data.filter(d => d.name || d.findNumber || d.sequence || d.level), sheets: ['csv'], rowCount: r.data.length }); 
          }, 
          error: (err) => errorCallback?.(err.message) 
        }); 
      }
      else if (ext === 'xlsx' || ext === 'xls') { 
        const wb = XLSX.read(e.target.result, { type: 'array' }); 
        const result = {}; 
        let tr = 0; 
        wb.SheetNames.forEach(name => { 
          const sd = XLSX.utils.sheet_to_json(wb.Sheets[name], { defval: '' }); 
          result[name.toLowerCase()] = sd; 
          tr += sd.length; 
        }); 
        callback({ type: 'excel', data: result, sheets: wb.SheetNames, rowCount: tr }); 
      }
      else if (ext === 'bmide' || ext === 'xml') { 
        const parser = new DOMParser(); 
        const xmlDoc = parser.parseFromString(e.target.result, 'text/xml'); 
        const types = []; 
        const bos = xmlDoc.getElementsByTagName('BusinessObject') || xmlDoc.getElementsByTagName('Type') || xmlDoc.getElementsByTagName('class'); 
        for (let i = 0; i < bos.length; i++) { 
          const n = bos[i].getAttribute('name') || bos[i].getAttribute('type') || bos[i].textContent; 
          if (n) types.push({ name: n, description: bos[i].getAttribute('desc') || '', parent: bos[i].getAttribute('extends') || bos[i].getAttribute('parent') || '' }); 
        } 
        callback({ type: 'bmide', data: types, sheets: ['BMIDE'], rowCount: types.length }); 
      }
    } catch (err) { errorCallback?.(err.message); }
  };
  reader.onerror = () => errorCallback?.('Error reading file');
  if (ext === 'xlsx' || ext === 'xls') reader.readAsArrayBuffer(file); 
  else reader.readAsText(file);
};

// ============================================
// JOURNEY MAP BUILDER
// ============================================
const buildJourneyMap = (ebom, mbom, bopData, equip, tools, skills, ewiDocs) => {
  const safeEbom = Array.isArray(ebom) ? ebom : [];
  const safeMbom = Array.isArray(mbom) ? mbom : [];
  const safeEquip = Array.isArray(equip) ? equip : [];
  const safeTools = Array.isArray(tools) ? tools : [];
  const safeSkills = Array.isArray(skills) ? skills : [];
  const safeEwiDocs = Array.isArray(ewiDocs) ? ewiDocs : [];
  
  const allOps = extractAllOperations(bopData);
  const journeyMap = {};
  
  safeEbom.forEach(e => {
    const match = safeMbom.find(m => 
      m.name === e.name || 
      m.name === `${e.name}-M` || 
      e.name === m.name?.replace('-M', '')
    );
    if (match) {
      if (!journeyMap[e.id || e.name]) {
        journeyMap[e.id || e.name] = {
          name: e.name || 'Unnamed EBOM',
          type: 'EBOM',
          origin: 'EBOM',
          path: ['EBOM'],
          details: { findNumber: e.findNumber || '', quantity: e.quantity || 1, uom: e.uom || 'EA' }
        };
      }
      journeyMap[e.id || e.name].path.push('MBOM');
      journeyMap[e.id || e.name].mbomRef = match.name;
    }
  });
  
  safeMbom.forEach(m => {
    const op = allOps.find(o => 
      (o.sequence || o.seq) === (m.operationReference || m.opRef)
    );
    if (op) {
      if (!journeyMap[m.id || m.name]) {
        journeyMap[m.id || m.name] = {
          name: m.name || 'Unnamed MBOM',
          type: 'MBOM',
          origin: 'MBOM',
          path: ['MBOM'],
          details: { findNumber: m.findNumber || '', quantity: m.quantity || 1, uom: m.uom || 'EA' }
        };
      }
      journeyMap[m.id || m.name].path.push('BOP');
      journeyMap[m.id || m.name].bopRef = op.name;
    }
  });
  
  allOps.forEach(op => {
    const eq = safeEquip.find(e => e.name === (op.workCenter || op.wc));
    if (!journeyMap[op.id || op.name]) {
      journeyMap[op.id || op.name] = {
        name: op.name || 'Unnamed Operation',
        type: 'BOP',
        origin: 'BOP',
        path: ['BOP'],
        details: { 
          sequence: op.sequence || '', 
          workCenter: op.workCenter || '', 
          standardTime: op.standardTime || 0,
          functionalGroup: op.functionalGroup || '',
          plant: op.plant || '',
          process: op.process || ''
        }
      };
    }
    if (eq) {
      journeyMap[op.id || op.name].path.push('BOE');
      journeyMap[op.id || op.name].boeRef = eq.name;
    }
    if (op.tools && safeTools.length > 0) {
      const toolList = Array.isArray(op.tools) ? op.tools : op.tools.split(',').map(t => t.trim());
      const matchedTools = safeTools.filter(t => 
        toolList.some(tl => t.name && (t.name.includes(tl) || tl.includes(t.name)))
      );
      if (matchedTools.length > 0) {
        journeyMap[op.id || op.name].path.push('Tools');
        journeyMap[op.id || op.name].toolsRef = matchedTools.map(t => t.name).join(', ');
      }
    }
    if (op.skills && safeSkills.length > 0) {
      const skillList = Array.isArray(op.skills) ? op.skills : op.skills.split(',').map(s => s.trim());
      const matchedSkills = safeSkills.filter(s => 
        skillList.some(sl => s.name && (s.name.includes(sl) || sl.includes(s.name)))
      );
      if (matchedSkills.length > 0) {
        journeyMap[op.id || op.name].path.push('Skills');
        journeyMap[op.id || op.name].skillsRef = matchedSkills.map(s => s.name).join(', ');
      }
    }
    if (op.workInstruction && safeEwiDocs.length > 0) {
      const matchedEWI = safeEwiDocs.find(e => e.id === op.workInstruction || e.name === op.workInstruction);
      if (matchedEWI) {
        journeyMap[op.id || op.name].path.push('EWI');
        journeyMap[op.id || op.name].ewiRef = matchedEWI.name;
      }
    }
  });
  
  return journeyMap;
};

// ============================================
// TOOLTIP INPUT COMPONENT
// ============================================
const TooltipInput = React.memo(({ 
  value, 
  onChange, 
  placeholder, 
  style, 
  type, 
  options, 
  className 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const localValueRef = useRef(value || '');

  useEffect(() => {
    if (!isFocused) {
      localValueRef.current = value || '';
    }
  }, [value, isFocused]);

  const isOverflowing = () => {
    if (inputRef.current) {
      return inputRef.current.scrollWidth > inputRef.current.clientWidth;
    }
    return false;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    localValueRef.current = newValue;
    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    localValueRef.current = value || '';
  };

  const handleBlur = () => {
    setIsFocused(false);
    setShowTooltip(false);
  };

  if (type === 'select') {
    return (
      <select 
        style={{ ...S.select, padding: '1px 2px', fontSize: '0.66rem', width: style?.width || 'auto' }} 
        value={value || ''} 
        onChange={onChange}
      >
        {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type={type || 'text'}
        style={{ 
          ...S.input, 
          width: style?.width || '100%', 
          padding: '1px 3px', 
          fontSize: '0.66rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          borderColor: isFocused ? T.p : T.b
        }}
        value={isFocused ? localValueRef.current : (value || '')}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        onMouseEnter={() => {
          if (isOverflowing()) setShowTooltip(true);
        }}
        onMouseLeave={() => {
          if (!isFocused) setShowTooltip(false);
        }}
      />
      {showTooltip && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          background: T.s3,
          color: T.t,
          padding: '6px 10px',
          borderRadius: '4px',
          fontSize: '0.62rem',
          border: `1px solid ${T.b}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          zIndex: 1000,
          maxWidth: '400px',
          whiteSpace: 'normal',
          wordBreak: 'break-all',
          marginBottom: '4px'
        }}>
          {localValueRef.current || placeholder || ''}
        </div>
      )}
    </div>
  );
});
TooltipInput.displayName = 'TooltipInput';

// ============================================
// COMPLETELY FIXED EDITTABLE TABLE - NO FOCUS LOSS
// ============================================
const EditableTable = React.memo(({ columns, data, onUpdate, onDelete, onAdd, addLabel, emptyMessage }) => {
  const [localData, setLocalData] = useState(data);
  const timeoutRefs = useRef({});
  const cellRefs = useRef({});
  
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleChange = useCallback((rowIndex, key, value) => {
    setLocalData(prev => {
      const newData = [...prev];
      newData[rowIndex] = { ...newData[rowIndex], [key]: value };
      return newData;
    });
    
    const cellKey = `${rowIndex}-${key}`;
    if (timeoutRefs.current[cellKey]) {
      clearTimeout(timeoutRefs.current[cellKey]);
    }
    
    timeoutRefs.current[cellKey] = setTimeout(() => {
      onUpdate(rowIndex, key, value);
    }, 400);
  }, [onUpdate]);
  
  const handleDelete = useCallback((rowIndex) => {
    onDelete(rowIndex);
  }, [onDelete]);

  const handleKeyDown = useCallback((e, rowIndex, key) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Find next editable field
      const currentCell = cellRefs.current[`${rowIndex}-${key}`];
      if (currentCell) {
        const allCells = Object.keys(cellRefs.current);
        const currentIndex = allCells.indexOf(`${rowIndex}-${key}`);
        if (currentIndex >= 0 && currentIndex < allCells.length - 1) {
          const nextCell = cellRefs.current[allCells[currentIndex + 1]];
          if (nextCell) nextCell.focus();
        }
      }
    }
  }, []);

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={S.tbl}>
          <thead>
            <tr>
              {columns.map(c => <th key={c.key} style={S.th}>{c.label}</th>)}
              <th style={{ ...S.th, width: '24px' }}></th>
            </tr>
          </thead>
          <tbody>
            {localData.map((row, i) => (
              <tr key={row.id || i}>
                {columns.map(c => (
                  <td key={`${row.id || i}-${c.key}`} style={S.td}>
                    {c.editable !== false ? (
                      c.type === 'select' ? (
                        <select 
                          style={{ ...S.select, padding: '1px 2px', fontSize: '0.66rem', width: c.width || 'auto' }} 
                          value={row[c.key] || ''} 
                          onChange={e => handleChange(i, c.key, e.target.value)}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {(c.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input 
                          ref={el => { cellRefs.current[`${i}-${c.key}`] = el; }}
                          type={c.type || 'text'} 
                          style={{ 
                            ...S.input, 
                            width: c.width || '100%', 
                            padding: '1px 3px', 
                            fontSize: '0.66rem',
                            background: T.bg,
                            border: `1px solid ${T.b}`,
                            borderRadius: '4px',
                            color: T.t,
                            outline: 'none'
                          }} 
                          value={row[c.key] || ''} 
                          onChange={e => handleChange(i, c.key, c.type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
                          onKeyDown={e => handleKeyDown(e, i, c.key)}
                          onMouseDown={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                      )
                    ) : (
                      <span style={{ fontSize: '0.68rem' }}>{row[c.key] || '-'}</span>
                    )}
                  </td>
                ))}
                <td style={S.td}>
                  <button onClick={() => handleDelete(i)} style={{ ...S.bGho, padding: '0px 3px', fontSize: '0.56rem' }}>
                    <Trash2 size={8} />
                  </button>
                </td>
              </tr>
            ))}
            {localData.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} style={{ ...S.td, textAlign: 'center', color: T.t3, padding: '10px' }}>
                  {emptyMessage || 'No data'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {onAdd && (
        <button onClick={onAdd} style={{ ...S.btn, ...S.bGho, marginTop: '4px', fontSize: '0.68rem' }}>
          <Plus size={9} /> {addLabel || 'Add'}
        </button>
      )}
    </div>
  );
});
EditableTable.displayName = 'EditableTable';

// ============================================
// COORDINATED SELECTION HOOK
// ============================================
const useCoordinatedSelection = () => {
  const [selId, setSelId] = useState(null);
  const [hlIds, setHlIds] = useState([]);
  const [hovId, setHovId] = useState(null);
  const selectNode = useCallback((id, related) => { 
    if (selId === id) { 
      setSelId(null); 
      setHlIds([]); 
    } else { 
      setSelId(id); 
      setHlIds(related || [id]); 
    } 
  }, [selId]);
  const clearSelection = useCallback(() => { 
    setSelId(null); 
    setHlIds([]); 
  }, []);
  return { selectedNodeId: selId, highlightedIds: hlIds, hoveredNodeId: hovId, setHoveredNodeId: setHovId, selectNode, clearSelection };
};

// ============================================
// DENDRO NODE COMPONENT
// ============================================
const DendroNode = ({ id, label, color, details, children, depth, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded !== false);
  const [hovered, setHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const hasChildren = children && React.Children.count(children) > 0;
  
  return (
    <div style={{ position: 'relative' }}>
      {depth > 0 && (
        <div style={{
          position: 'absolute',
          left: depth * 16 + 4,
          top: 0,
          bottom: hasChildren && expanded ? '50%' : '50%',
          width: '2px',
          background: `${color}44`
        }} />
      )}
      <div 
        onClick={() => hasChildren && setExpanded(!expanded)} 
        onMouseEnter={() => { 
          setHovered(true); 
          if (!hasChildren && details) setShowTooltip(true);
        }} 
        onMouseLeave={() => { 
          setHovered(false); 
          setShowTooltip(false);
        }} 
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px 8px',
          marginLeft: depth * 16,
          borderRadius: '4px',
          cursor: hasChildren ? 'pointer' : 'default',
          background: hovered ? `${color}15` : 'transparent',
          border: `1px solid ${hovered ? color : 'transparent'}`,
          transition: 'all 0.15s',
          marginBottom: '1px',
          position: 'relative'
        }}
      >
        <span style={{
          width: '12px',
          fontSize: '0.55rem',
          color: hasChildren ? color : T.t3,
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          display: 'inline-block',
          textAlign: 'center'
        }}>
          {hasChildren ? '▶' : '•'}
        </span>
        <span style={{
          fontWeight: hasChildren ? 600 : 500,
          color: T.t,
          fontSize: '0.68rem',
          whiteSpace: 'nowrap',
          maxWidth: '300px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {label}
        </span>
        {hovered && details && !hasChildren && (
          <span style={{
            fontSize: '0.55rem',
            color: T.t3,
            marginLeft: '4px',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '400px'
          }}>
            — {details}
          </span>
        )}
        {hovered && hasChildren && (
          <span style={{
            fontSize: '0.5rem',
            color: T.t3,
            marginLeft: 'auto',
            padding: '1px 6px',
            background: `${T.b}50`,
            borderRadius: '3px'
          }}>
            {React.Children.count(children)} items
          </span>
        )}
      </div>
      {showTooltip && details && !hasChildren && (
        <div style={S.dendroTooltip}>
          {details}
        </div>
      )}
      {hasChildren && expanded && (
        <div style={{
          borderLeft: depth > 0 ? `2px solid ${color}22` : 'none',
          marginLeft: depth * 16 + 8,
          paddingLeft: depth > 0 ? '4px' : '0'
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================
// JOURNEY ALLUVIAL FLOW (Enhanced)
// ============================================
const JourneyAlluvialFlow = ({ cfg, selection, width, height, isFullscreen, onToggleFullscreen }) => {
  const { inc, ebom, mbom, bopData, equip, tools, skills, ewiDocs } = cfg;
  const allOps = extractAllOperations(bopData);
  const functionalGroups = extractFunctionalGroups(bopData);
  
  const [zoom, setZoom] = useState(1);
  const [selectedPath, setSelectedPath] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showDetails, setShowDetails] = useState(true);
  const [filterDomain, setFilterDomain] = useState('all');
  const [traceMode, setTraceMode] = useState('forward');
  const [showJourneyMap, setShowJourneyMap] = useState(true);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const journeyMap = useMemo(() => {
    const safeEbom = Array.isArray(ebom) ? ebom : [];
    const safeMbom = Array.isArray(mbom) ? mbom : [];
    const safeEquip = Array.isArray(equip) ? equip : [];
    const safeTools = Array.isArray(tools) ? tools : [];
    const safeSkills = Array.isArray(skills) ? skills : [];
    const safeEwiDocs = Array.isArray(ewiDocs) ? ewiDocs : [];
    return buildJourneyMap(safeEbom, safeMbom, bopData, safeEquip, safeTools, safeSkills, safeEwiDocs);
  }, [ebom, mbom, bopData, equip, tools, skills, ewiDocs]);
  
  const { columns, flows, itemJourneys } = useMemo(() => {
    const domains = [];
    const itemMap = {};
    
    const safeEbom = Array.isArray(ebom) ? ebom : [];
    const safeMbom = Array.isArray(mbom) ? mbom : [];
    const safeEquip = Array.isArray(equip) ? equip : [];
    const safeTools = Array.isArray(tools) ? tools : [];
    const safeSkills = Array.isArray(skills) ? skills : [];
    const safeEwiDocs = Array.isArray(ewiDocs) ? ewiDocs : [];
    const safeInc = inc || {};
    
    if (safeInc.ebom) {
      const items = safeEbom.map(e => {
        const id = genId();
        const journey = journeyMap[e.id || e.name] || { 
          path: ['EBOM'], 
          origin: 'EBOM', 
          name: e.name || 'Unnamed',
          details: { findNumber: e.findNumber || '', quantity: e.quantity || 1, uom: e.uom || 'EA' }
        };
        itemMap[id] = { ...e, domain: 'EBOM', journey, originalId: e.id || e.name };
        return { id, name: e.name || 'Unnamed', value: e.quantity || 1, journey, findNumber: e.findNumber || '' };
      });
      domains.push({ name: 'EBOM', color: T.cy, icon: '📐', items });
    }
    
    if (safeInc.mbom) {
      const items = safeMbom.map(m => {
        const id = genId();
        const journey = journeyMap[m.id || m.name] || { 
          path: ['MBOM'], 
          origin: 'MBOM', 
          name: m.name || 'Unnamed',
          details: { findNumber: m.findNumber || '', quantity: m.quantity || 1, uom: m.uom || 'EA' }
        };
        itemMap[id] = { ...m, domain: 'MBOM', journey, originalId: m.id || m.name };
        return { id, name: m.name || 'Unnamed', value: m.quantity || 1, journey, findNumber: m.findNumber || '' };
      });
      domains.push({ name: 'MBOM', color: T.pk, icon: '🔧', items });
    }
    
    if (safeInc.bop && allOps.length > 0) {
      const items = allOps.map(op => {
        const id = genId();
        const journey = journeyMap[op.id || op.name] || { 
          path: ['BOP'], 
          origin: 'BOP', 
          name: op.name || 'Unnamed',
          details: { 
            sequence: op.sequence || '', 
            workCenter: op.workCenter || '', 
            standardTime: op.standardTime || 0,
            functionalGroup: op.functionalGroup || '',
            plant: op.plant || '',
            process: op.process || ''
          }
        };
        itemMap[id] = { ...op, domain: 'BOP', journey, originalId: op.id || op.name };
        return { id, name: op.name || 'Unnamed', value: 1, journey, sequence: op.sequence || '', workCenter: op.workCenter || '' };
      });
      domains.push({ name: 'BOP', color: T.w, icon: '⚙️', items });
    }
    
    if (safeInc.boe) {
      const items = safeEquip.filter(e => e.level === 'WorkUnit').map(eq => {
        const id = genId();
        const journey = journeyMap[eq.id || eq.name] || { 
          path: ['BOE'], 
          origin: 'BOE', 
          name: eq.name || 'Unnamed',
          details: { level: eq.level || '', technology: eq.technology || '', costRate: eq.costRate || 0 }
        };
        itemMap[id] = { ...eq, domain: 'BOE', journey, originalId: eq.id || eq.name };
        return { id, name: eq.name || 'Unnamed', value: 1, journey, technology: eq.technology || '' };
      });
      domains.push({ name: 'BOE', color: T.pp, icon: '🏗️', items });
    }
    
    const flows = [];
    
    if (safeInc.ebom && safeInc.mbom) {
      safeEbom.forEach(e => {
        const m = safeMbom.find(x => 
          x.name === e.name || 
          x.name === `${e.name}-M` || 
          e.name === (x.name || '').replace('-M', '')
        );
        if (m) {
          const si = domains[0]?.items.find(i => i.name === e.name);
          const ti = domains[1]?.items.find(i => i.name === m.name);
          if (si && ti) {
            flows.push({
              sourceId: si.id,
              targetId: ti.id,
              value: e.quantity || 1,
              sc: 0,
              tc: 1,
              sourceName: e.name,
              targetName: m.name,
              journey: [si.journey, ti.journey],
              tracePath: [si.journey?.origin || 'EBOM', 'MBOM'],
              color: T.pk,
              type: 'EBOM→MBOM'
            });
          }
        }
      });
    }
    
    if (safeInc.mbom && safeInc.bop) {
      safeMbom.forEach(m => {
        const op = allOps.find(o => (o.sequence || o.seq) === (m.operationReference || m.opRef));
        if (op) {
          const si = domains[1]?.items.find(i => i.name === m.name);
          const ti = domains[2]?.items.find(i => i.name === op.name);
          if (si && ti) {
            flows.push({
              sourceId: si.id,
              targetId: ti.id,
              value: m.quantity || 1,
              sc: 1,
              tc: 2,
              sourceName: m.name,
              targetName: op.name,
              journey: [si.journey, ti.journey],
              tracePath: [si.journey?.origin || 'MBOM', 'BOP'],
              color: T.w,
              type: 'MBOM→BOP'
            });
          }
        }
      });
    }
    
    if (safeInc.bop && safeInc.boe) {
      allOps.forEach(op => {
        const eq = safeEquip.find(e => e.name === (op.workCenter || op.wc));
        if (eq) {
          const si = domains[2]?.items.find(i => i.name === op.name);
          const ti = domains[3]?.items.find(i => i.name === eq.name);
          if (si && ti) {
            flows.push({
              sourceId: si.id,
              targetId: ti.id,
              value: 1,
              sc: 2,
              tc: 3,
              sourceName: op.name,
              targetName: eq.name,
              journey: [si.journey, ti.journey],
              tracePath: [si.journey?.origin || 'BOP', 'BOE'],
              color: T.pp,
              type: 'BOP→BOE'
            });
          }
        }
      });
    }
    
    if (safeInc.bop && safeInc.tools) {
      allOps.forEach(op => {
        if (op.tools) {
          const toolList = Array.isArray(op.tools) ? op.tools : op.tools.split(',').map(t => t.trim());
          const matchedTools = safeTools.filter(t => toolList.some(tl => t.name && (t.name.includes(tl) || tl.includes(t.name))));
          matchedTools.forEach(tool => {
            const si = domains[2]?.items.find(i => i.name === op.name);
            if (si) {
              const existingFlow = flows.find(f => f.sourceId === si.id && f.type === 'BOP→Tools');
              if (existingFlow) {
                existingFlow.toolRefs = existingFlow.toolRefs || [];
                existingFlow.toolRefs.push(tool.name);
              } else {
                flows.push({
                  sourceId: si.id,
                  targetId: null,
                  value: 1,
                  sc: 2,
                  tc: -1,
                  sourceName: op.name,
                  targetName: 'Tools',
                  journey: [si.journey],
                  tracePath: [si.journey?.origin || 'BOP', 'Tools'],
                  color: T.te,
                  type: 'BOP→Tools',
                  toolRefs: [tool.name],
                  isMetadata: true
                });
              }
            }
          });
        }
      });
    }
    
    return { columns: domains, flows, itemJourneys: itemMap };
  }, [inc, ebom, mbom, allOps, equip, tools, skills, ewiDocs, journeyMap]);

  const W = isFullscreen ? window.innerWidth - 40 : width;
  const H = isFullscreen ? window.innerHeight - 120 : height;
  const cw = W / Math.max(columns.length, 1);
  const nh = Math.min(18, Math.max(12, (H - 80) / Math.max(...columns.map(c => c.items.length), 1)));
  
  const filteredColumns = useMemo(() => {
    let cols = columns;
    if (filterDomain !== 'all') {
      cols = cols.map(col => ({
        ...col,
        items: col.items.filter(item => item.domain === filterDomain.toUpperCase())
      })).filter(col => col.items.length > 0);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      cols = cols.map(col => ({
        ...col,
        items: col.items.filter(item => 
          item.name.toLowerCase().includes(term) || 
          (item.findNumber && item.findNumber.toLowerCase().includes(term)) ||
          (item.sequence && item.sequence.toLowerCase().includes(term)) ||
          (item.workCenter && item.workCenter.toLowerCase().includes(term))
        )
      })).filter(col => col.items.length > 0);
    }
    return cols;
  }, [columns, filterDomain, searchTerm]);

  const resetView = useCallback(() => {
    setZoom(1);
    setSelectedPath(null);
    setSelectedItemDetails(null);
    setHoveredItem(null);
    setFilterDomain('all');
    setTraceMode('forward');
    setShowJourneyMap(true);
    setSearchTerm('');
  }, []);

  const handleItemSelect = useCallback((itemId) => {
    if (selectedPath === itemId) {
      setSelectedPath(null);
      setSelectedItemDetails(null);
      selection?.clearSelection?.();
    } else {
      setSelectedPath(itemId);
      const item = Object.values(itemJourneys).find(i => i.id === itemId);
      if (item) {
        setSelectedItemDetails(item);
        selection?.selectNode?.(itemId, [itemId]);
      }
    }
  }, [selectedPath, itemJourneys, selection]);

  return (
    <div style={{ position: 'relative', width: W, height: H, overflow: 'auto', background: T.bg }}>
      <div style={{ 
        position: 'sticky', top: 0, zIndex: 20, 
        background: `${T.s}CC`, 
        padding: '4px 8px',
        borderBottom: `1px solid ${T.b}`,
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
        alignItems: 'center',
        backdropFilter: 'blur(8px)'
      }}>
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.6rem', color: T.t2, fontWeight: 600 }}>🔍 Journey Trace:</span>
          <button 
            onClick={() => setTraceMode('forward')} 
            style={{ ...S.btn, ...(traceMode === 'forward' ? S.bPri : S.bGho), padding: '2px 6px', fontSize: '0.55rem' }}
          >
            Forward
          </button>
          <button 
            onClick={() => setTraceMode('backward')} 
            style={{ ...S.btn, ...(traceMode === 'backward' ? S.bPri : S.bGho), padding: '2px 6px', fontSize: '0.55rem' }}
          >
            Backward
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            <ZoomOut size={9} />
          </button>
          <span style={{ fontSize: '0.55rem', color: T.t3 }}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            <ZoomIn size={9} />
          </button>
          <button onClick={resetView} style={{ ...S.btn, ...S.bWar, padding: '2px 6px', fontSize: '0.55rem' }}>
            <RefreshCw size={9} /> Reset View
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center', marginLeft: 'auto' }}>
          <input
            type="text"
            placeholder="🔍 Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ ...S.input, padding: '2px 6px', fontSize: '0.6rem', width: '120px' }}
          />
          <button onClick={() => setShowJourneyMap(!showJourneyMap)} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            {showJourneyMap ? <Eye size={9} /> : <EyeOff size={9} />}
          </button>
          <button onClick={() => setShowDetails(!showDetails)} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            {showDetails ? <Info size={9} /> : <Info size={9} />}
          </button>
          <select 
            value={filterDomain} 
            onChange={e => setFilterDomain(e.target.value)}
            style={{ ...S.select, padding: '2px 4px', fontSize: '0.55rem', width: '90px' }}
          >
            <option value="all">All Domains</option>
            {columns.map(c => <option key={c.name} value={c.name.toLowerCase()}>{c.icon} {c.name}</option>)}
          </select>
          {onToggleFullscreen && (
            <button onClick={onToggleFullscreen} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
              {isFullscreen ? <Minimize2 size={9} /> : <Maximize2 size={9} />}
            </button>
          )}
        </div>
      </div>

      {showJourneyMap && (
        <div style={{ ...S.legend, position: 'sticky', top: '36px', zIndex: 15, background: `${T.s}CC`, backdropFilter: 'blur(8px)', borderBottom: `1px solid ${T.b}` }}>
          {columns.map(col => (
            <span key={col.name} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.55rem', color: col.color }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: col.color }} />
              {col.icon} {col.name}
            </span>
          ))}
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.55rem', color: T.te }}>
            <span style={{ width: '8px', height: '2px', borderRadius: '1px', background: T.te }} />
            Tools/Skills/EWI
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.55rem', color: T.t3 }}>
            <span style={{ width: '8px', height: '2px', borderRadius: '1px', background: T.t3 }} />
            Journey Trace
          </span>
        </div>
      )}

      <svg 
        width={W * zoom} 
        height={Math.max(H, Math.max(...filteredColumns.map(c => c.items.length)) * nh + 80) * zoom}
        style={{ transform: `scale(${zoom})`, transformOrigin: '0 0' }}
      >
        {filteredColumns.map((col, ci) => (
          <g key={col.name}>
            <text x={ci * cw + cw / 2} y={20} textAnchor="middle" fill={col.color} fontSize={12} fontWeight={700}>
              {col.icon} {col.name} ({col.items.length})
            </text>
          </g>
        ))}

        {filteredColumns.map((col, ci) => 
          col.items.map((item, ii) => {
            const isSelected = selectedPath === item.id;
            const isHovered = hoveredItem === item.id;
            const isHighlighted = selection?.highlightedIds?.includes(item.id);
            
            return (
              <g key={item.id}>
                <rect
                  x={ci * cw + 5}
                  y={ii * nh + 30}
                  width={cw - 10}
                  height={Math.max(1, nh - 2)}
                  rx={3}
                  fill={col.color}
                  opacity={
                    isSelected ? 0.95 :
                    isHighlighted ? 0.85 :
                    isHovered ? 0.75 :
                    selection?.selectedNodeId && !isHighlighted ? 0.15 : 0.65
                  }
                  stroke={
                    isSelected ? '#fff' :
                    isHighlighted ? '#fff' :
                    isHovered ? col.color : 'transparent'
                  }
                  strokeWidth={isSelected || isHighlighted ? 2 : 1}
                  style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => handleItemSelect(item.id)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                />
                <text
                  x={ci * cw + cw / 2}
                  y={ii * nh + 30 + nh / 2 + 4}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize={Math.min(8, cw / 8)}
                  fontWeight={isSelected ? 700 : 500}
                  style={{ pointerEvents: 'none' }}
                >
                  {item.name.substring(0, Math.floor(cw / 8))}
                </text>
                {showDetails && item.findNumber && (
                  <text
                    x={ci * cw + cw - 5}
                    y={ii * nh + 30 + nh / 2 + 4}
                    textAnchor="end"
                    fill={T.t3}
                    fontSize={6}
                    style={{ pointerEvents: 'none' }}
                  >
                    {item.findNumber}
                  </text>
                )}
                {showJourneyMap && item.journey?.origin && (
                  <text
                    x={ci * cw + 5}
                    y={ii * nh + 30 + nh / 2 + 4}
                    textAnchor="start"
                    fill={T.t3}
                    fontSize={5}
                    style={{ pointerEvents: 'none' }}
                  >
                    {item.journey.origin}
                  </text>
                )}
              </g>
            );
          })
        )}

        {flows.map((f, fi) => {
          const sc = filteredColumns.findIndex(c => c.name === f.sc);
          const tc = filteredColumns.findIndex(c => c.name === f.tc);
          
          if (f.isMetadata) {
            const si = filteredColumns[sc]?.items.findIndex(i => i.id === f.sourceId);
            if (si < 0) return null;
            const sx = sc * cw + cw - 5;
            const sy = si * nh + 30 + nh / 2;
            
            return (
              <g key={fi}>
                <line
                  x1={sx}
                  y1={sy}
                  x2={sx + 30}
                  y2={sy - 10}
                  stroke={f.color}
                  strokeWidth={1}
                  opacity={0.4}
                  strokeDasharray="3,3"
                />
                <text
                  x={sx + 35}
                  y={sy - 12}
                  fill={f.color}
                  fontSize={6}
                  opacity={0.7}
                >
                  {f.toolRefs ? f.toolRefs.join(', ') : f.targetName}
                </text>
              </g>
            );
          }
          
          if (sc < 0 || tc < 0) return null;
          
          const si = filteredColumns[sc]?.items.findIndex(i => i.id === f.sourceId);
          const ti = filteredColumns[tc]?.items.findIndex(i => i.id === f.targetId);
          if (si < 0 || ti < 0) return null;
          
          const sx = sc * cw + cw - 5;
          const sy = si * nh + 30 + nh / 2;
          const tx = tc * cw + 5;
          const ty = ti * nh + 30 + nh / 2;
          const mx = (sx + tx) / 2;
          
          const isActive = selectedPath === f.sourceId || selectedPath === f.targetId ||
                          selection?.highlightedIds?.includes(f.sourceId) ||
                          selection?.highlightedIds?.includes(f.targetId);
          
          return (
            <g key={fi}>
              <path
                d={`M${sx},${sy} C${mx},${sy} ${mx},${ty} ${tx},${ty}`}
                stroke={f.color || T.t3}
                strokeWidth={isActive ? Math.max(2, Math.min(6, f.value * 1.5)) : Math.max(0.5, Math.min(3, f.value))}
                fill="none"
                opacity={isActive ? 1 : (selectedPath ? 0.05 : 0.25)}
                strokeDasharray={isActive ? 'none' : '4,4'}
              />
              {showJourneyMap && f.tracePath && f.tracePath.length > 0 && (
                <circle
                  cx={(sx + tx) / 3}
                  cy={(sy + ty) / 3}
                  r={isActive ? 4 : 2}
                  fill={f.color || T.t3}
                  opacity={isActive ? 0.8 : 0.3}
                />
              )}
              {f.value > 1 && (
                <text
                  x={(sx + tx) / 2}
                  y={(sy + ty) / 2 - 8}
                  textAnchor="middle"
                  fill={T.t3}
                  fontSize={isActive ? 8 : 6}
                  fontWeight={isActive ? 700 : 400}
                  style={{ pointerEvents: 'none' }}
                >
                  {f.value}
                </text>
              )}
              {isActive && f.type && (
                <text
                  x={(sx + tx) / 2}
                  y={(sy + ty) / 2 + 14}
                  textAnchor="middle"
                  fill={T.t2}
                  fontSize={6}
                  style={{ pointerEvents: 'none' }}
                >
                  {f.type}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {selectedItemDetails && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          background: T.s2,
          borderTop: `1px solid ${T.p}44`,
          padding: '8px 12px',
          maxHeight: '200px',
          overflowY: 'auto',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: T.pl, fontSize: '0.75rem' }}>
              📍 Journey Details: {selectedItemDetails.name || 'Unknown'}
              <span style={{ fontSize: '0.6rem', color: T.t2, marginLeft: '8px' }}>
                ({selectedItemDetails.domain || 'Unknown Domain'})
              </span>
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={() => {
                const item = Object.values(itemJourneys).find(i => i.id === selectedPath);
                if (item) {
                  const relatedIds = [];
                  Object.values(itemJourneys).forEach(other => {
                    if (other.originalId === item.originalId || 
                        other.journey?.mbomRef === item.journey?.mbomRef ||
                        other.journey?.bopRef === item.journey?.bopRef) {
                      relatedIds.push(other.id);
                    }
                  });
                  if (relatedIds.length > 0) {
                    selection?.selectNode?.(selectedPath, relatedIds);
                  }
                }
              }} style={{ ...S.btn, ...S.bTeal, padding: '2px 6px', fontSize: '0.55rem' }}>
                <Link2 size={10} /> Trace Relations
              </button>
              <button onClick={() => {
                setSelectedPath(null);
                setSelectedItemDetails(null);
                selection?.clearSelection?.();
              }} style={{ ...S.btn, ...S.bGho, padding: '2px 6px', fontSize: '0.55rem' }}>
                <X size={10} /> Close
              </button>
            </div>
          </div>
          
          {selectedItemDetails.journey?.path && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              {selectedItemDetails.journey.path.map((step, si) => (
                <span key={si} style={{ 
                  ...S.badge(T.cy), 
                  fontSize: '0.55rem',
                  background: si === 0 ? `${T.g}22` : (si === selectedItemDetails.journey.path.length - 1 ? `${T.pp}22` : `${T.cy}22`),
                  color: si === 0 ? T.g : (si === selectedItemDetails.journey.path.length - 1 ? T.pp : T.cy)
                }}>
                  {step} {si < selectedItemDetails.journey.path.length - 1 ? '→' : ''}
                </span>
              ))}
            </div>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px', marginTop: '6px', fontSize: '0.6rem', color: T.t2 }}>
            {selectedItemDetails.journey?.mbomRef && (
              <span>📦 MBOM: <span style={{ color: T.t }}>{selectedItemDetails.journey.mbomRef}</span></span>
            )}
            {selectedItemDetails.journey?.bopRef && (
              <span>⚙️ BOP: <span style={{ color: T.t }}>{selectedItemDetails.journey.bopRef}</span></span>
            )}
            {selectedItemDetails.journey?.boeRef && (
              <span>🏗️ BOE: <span style={{ color: T.t }}>{selectedItemDetails.journey.boeRef}</span></span>
            )}
            {selectedItemDetails.journey?.toolsRef && (
              <span>🔧 Tools: <span style={{ color: T.t }}>{selectedItemDetails.journey.toolsRef}</span></span>
            )}
            {selectedItemDetails.journey?.skillsRef && (
              <span>🛠️ Skills: <span style={{ color: T.t }}>{selectedItemDetails.journey.skillsRef}</span></span>
            )}
            {selectedItemDetails.journey?.ewiRef && (
              <span>📄 EWI: <span style={{ color: T.t }}>{selectedItemDetails.journey.ewiRef}</span></span>
            )}
            {selectedItemDetails.findNumber && (
              <span>🔢 Find Number: <span style={{ color: T.t }}>{selectedItemDetails.findNumber}</span></span>
            )}
            {selectedItemDetails.quantity && (
              <span>📊 Quantity: <span style={{ color: T.t }}>{selectedItemDetails.quantity}</span></span>
            )}
            {selectedItemDetails.workCenter && (
              <span>🏭 Work Center: <span style={{ color: T.t }}>{selectedItemDetails.workCenter}</span></span>
            )}
            {selectedItemDetails.standardTime !== undefined && selectedItemDetails.standardTime > 0 && (
              <span>⏱️ Std Time: <span style={{ color: T.t }}>{selectedItemDetails.standardTime} min</span></span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ICICLE DIAGRAM
// ============================================
const IcicleDiagram = ({ cfg, sel, w, h, fs, otf }) => {
  const { prod, inc, ebom, mbom, bopData, wp, allOperations } = cfg;
  const pn = prod?.name || 'Product';
  const ao = allOperations || extractAllOperations(bopData);
  const [z, sz] = useState(1);
  const [p, sp] = useState({ x: 0, y: 0 });
  const [ip, sip] = useState(false);
  const [hoveredLayer, setHoveredLayer] = useState(null);
  const ps = useRef({ x: 0, y: 0 });

  const layers = useMemo(() => {
    const l = [];
    const safeInc = inc || {};
    const safeEbom = Array.isArray(ebom) ? ebom : [];
    const safeMbom = Array.isArray(mbom) ? mbom : [];
    
    if (safeInc.workPackage) {
      l.push([{ 
        id: 'wp-root', 
        name: wp?.name || 'WP', 
        color: T.g, 
        value: 1, 
        domain: 'WP',
        details: `Plant: ${wp?.plant || 'N/A'}`
      }]);
    }
    
    const l1 = [];
    if (safeInc.ebom && safeEbom.length > 0) {
      l1.push({ 
        id: 'ebom-root', 
        name: 'EBOM', 
        color: T.cy, 
        value: safeEbom.length + 2, 
        domain: 'EBOM',
        details: `${safeEbom.length} components`
      });
    }
    if (safeInc.mbom && safeMbom.length > 0) {
      l1.push({ 
        id: 'mbom-root', 
        name: 'MBOM', 
        color: T.pk, 
        value: safeMbom.length + 2, 
        domain: 'MBOM',
        details: `${safeMbom.length} components`
      });
    }
    if (safeInc.bop && ao.length > 0) {
      l1.push({ 
        id: 'bop-root', 
        name: 'BOP', 
        color: T.w, 
        value: ao.length + 2, 
        domain: 'BOP',
        details: `${ao.length} operations`
      });
    }
    if (l1.length > 0) l.push(l1);
    
    const l2 = [];
    if (safeInc.ebom) {
      safeEbom.forEach(e => l2.push({ 
        id: `ebom-${e.id}`, 
        name: e.name || 'Unnamed', 
        color: T.cy, 
        value: e.quantity || 1, 
        domain: 'EBOM',
        details: `Find: ${e.findNumber || 'N/A'}`
      }));
    }
    if (safeInc.mbom) {
      safeMbom.forEach(m => l2.push({ 
        id: `mbom-${m.id}`, 
        name: m.name || 'Unnamed', 
        color: T.pk, 
        value: m.quantity || 1, 
        domain: 'MBOM',
        details: `Op: ${m.operationReference || 'N/A'}`
      }));
    }
    if (safeInc.bop) {
      ao.slice(0, 50).forEach(op => l2.push({ 
        id: `bop-${op.id}`, 
        name: op.name || 'Unnamed', 
        color: T.or, 
        value: 1, 
        domain: 'BOP',
        details: `Seq: ${op.sequence || 'N/A'} | WC: ${op.workCenter || 'N/A'}`
      }));
      if (ao.length > 50) {
        l2.push({ 
          id: 'bop-more', 
          name: `+${ao.length - 50} more`, 
          color: T.t3, 
          value: 1, 
          domain: 'BOP',
          details: 'Additional operations'
        });
      }
    }
    if (l2.length > 0) l.push(l2);
    
    return l;
  }, [inc, ebom, mbom, bopData, wp, pn, ao]);

  const lh = 24;
  const W = fs ? window.innerWidth - 40 : w;
  const H = fs ? window.innerHeight - 120 : h;

  const resetView = () => { sz(1); sp({ x: 0, y: 0 }); };

  return (
    <div 
      style={{ position: 'relative', width: W, height: H, overflow: 'hidden', cursor: ip ? 'grabbing' : 'grab' }}
      onMouseDown={e => { if (e.button === 0) { sip(true); ps.current = { x: e.clientX - p.x, y: e.clientY - p.y }; } }}
      onMouseMove={e => { if (ip) sp({ x: e.clientX - ps.current.x, y: e.clientY - ps.current.y }); }}
      onMouseUp={() => sip(false)}
      onMouseLeave={() => sip(false)}
    >
      <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 10, display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
        <button onClick={() => sz(z => Math.max(0.3, z - 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          <ZoomOut size={9} />
        </button>
        <span style={{ fontSize: '0.55rem', color: T.t3 }}>{Math.round(z * 100)}%</span>
        <button onClick={() => sz(z => Math.min(3, z + 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          <ZoomIn size={9} />
        </button>
        <button onClick={resetView} style={{ ...S.btn, ...S.bWar, padding: '2px 6px', fontSize: '0.55rem' }}>
          <RefreshCw size={9} /> Reset
        </button>
        {otf && (
          <button onClick={otf} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            {fs ? <Minimize2 size={9} /> : <Maximize2 size={9} />}
          </button>
        )}
      </div>

      <div style={{ ...S.legend, position: 'absolute', top: 32, left: 4, zIndex: 10, background: `${T.s}CC` }}>
        {[{ label: 'WP', color: T.g }, { label: 'EBOM', color: T.cy }, { label: 'MBOM', color: T.pk }, { label: 'BOP', color: T.w }].map(item => (
          <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.5rem', color: T.t3 }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>

      <svg width={W} height={H} style={{ transform: `scale(${z}) translate(${p.x}px,${p.y}px)`, transformOrigin: '0 0' }}>
        {layers.map((layer, li) => {
          const total = layer.reduce((s, d) => s + (d.value || 1), 0) || 1;
          let x = 0;
          return layer.map((item) => {
            const ww = Math.max(28, ((item.value || 1) / total) * W);
            const hl = sel?.highlightedIds?.includes(item.id) || false;
            const isHovered = hoveredLayer === item.id;
            return (
              <g key={item.id}>
                <rect
                  x={x}
                  y={li * (lh + 4)}
                  width={Math.max(1, ww - 2)}
                  height={lh}
                  rx={3}
                  fill={item.color}
                  opacity={sel?.selectedNodeId && !hl ? 0.15 : isHovered ? 0.95 : 0.7}
                  stroke={hl ? '#fff' : isHovered ? item.color : 'transparent'}
                  strokeWidth={hl ? 2 : 1}
                  style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => sel?.selectNode(item.id, [item.id])}
                  onMouseEnter={() => setHoveredLayer(item.id)}
                  onMouseLeave={() => setHoveredLayer(null)}
                />
                {ww > 35 && (
                  <text
                    x={x + ww / 2}
                    y={li * (lh + 4) + lh / 2 + 4}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={Math.min(9, ww / 7)}
                    fontWeight={600}
                    style={{ pointerEvents: 'none' }}
                  >
                    {item.name.substring(0, Math.floor(ww / 6))}
                  </text>
                )}
                {isHovered && item.details && (
                  <text
                    x={x + ww / 2}
                    y={li * (lh + 4) + lh + 12}
                    textAnchor="middle"
                    fill={T.t2}
                    fontSize={6}
                    style={{ pointerEvents: 'none' }}
                  >
                    {item.details}
                  </text>
                )}
              </g>
            );
            x += ww;
          });
        })}
      </svg>
    </div>
  );
};

// ============================================
// GANTT SWIMLANE
// ============================================
const GanttSwimlane = ({ cfg, w, h, fs, otf }) => {
  const { bopData, allOperations } = cfg;
  const ao = allOperations || extractAllOperations(bopData);
  const [z, sz] = useState(1);
  const [filterWC, setFilterWC] = useState('all');
  const [filterFG, setFilterFG] = useState('all');
  const [filterPlant, setFilterPlant] = useState('all');
  const [showLabels, setShowLabels] = useState(true);
  
  const wcs = useMemo(() => ['all', ...new Set(ao.map(o => o.workCenter || o.wc).filter(Boolean))], [ao]);
  const fgs = useMemo(() => ['all', ...new Set(ao.map(o => o.functionalGroup).filter(Boolean))], [ao]);
  const plants = useMemo(() => ['all', ...new Set(ao.map(o => o.plant).filter(Boolean))], [ao]);
  
  const filteredOps = useMemo(() => {
    let ops = ao;
    if (filterWC !== 'all') {
      ops = ops.filter(o => (o.workCenter || o.wc) === filterWC);
    }
    if (filterFG !== 'all') {
      ops = ops.filter(o => o.functionalGroup === filterFG);
    }
    if (filterPlant !== 'all') {
      ops = ops.filter(o => o.plant === filterPlant);
    }
    return ops;
  }, [ao, filterWC, filterFG, filterPlant]);

  const mt = useMemo(() => filteredOps.reduce((s, o) => s + (o.standardTime || o.stdTime || 0) + (o.setupTime || o.setup || 0), 0) || 1, [filteredOps]);
  const wcsFiltered = useMemo(() => ['all', ...new Set(filteredOps.map(o => o.workCenter || o.wc).filter(Boolean))], [filteredOps]);

  const W = fs ? window.innerWidth - 40 : w;
  const H = fs ? window.innerHeight - 120 : h;
  const rh = 20;
  const hh = 24;
  const lw = 130;
  
  let ct = 0;
  const bars = filteredOps.map(op => {
    const wi = Math.max(0, wcsFiltered.indexOf(op.workCenter || op.wc || ''));
    const su = op.setupTime || op.setup || 0;
    const st = op.standardTime || op.stdTime || 0;
    const start = ct;
    ct += su + st;
    return { ...op, wi, start, su, st, id: genId() };
  });

  const resetView = () => { 
    sz(1); 
    setFilterWC('all');
    setFilterFG('all');
    setFilterPlant('all');
  };

  return (
    <div style={{ position: 'relative', width: W, height: H, overflow: 'auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: `${T.s}CC`, padding: '4px 8px', display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', backdropFilter: 'blur(8px)' }}>
        <button onClick={() => sz(z => Math.max(0.3, z - 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          <ZoomOut size={9} />
        </button>
        <span style={{ fontSize: '0.55rem', color: T.t3 }}>{Math.round(z * 100)}%</span>
        <button onClick={() => sz(z => Math.min(3, z + 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          <ZoomIn size={9} />
        </button>
        
        <select
          value={filterWC}
          onChange={e => setFilterWC(e.target.value)}
          style={{ ...S.select, padding: '2px 4px', fontSize: '0.55rem', width: '120px' }}
        >
          <option value="all">All Work Centers</option>
          {wcs.slice(1).map(wc => <option key={wc} value={wc}>{wc}</option>)}
        </select>
        
        <select
          value={filterFG}
          onChange={e => setFilterFG(e.target.value)}
          style={{ ...S.select, padding: '2px 4px', fontSize: '0.55rem', width: '120px' }}
        >
          <option value="all">All FGs</option>
          {fgs.slice(1).map(fg => <option key={fg} value={fg}>{fg}</option>)}
        </select>
        
        <select
          value={filterPlant}
          onChange={e => setFilterPlant(e.target.value)}
          style={{ ...S.select, padding: '2px 4px', fontSize: '0.55rem', width: '100px' }}
        >
          <option value="all">All Plants</option>
          {plants.slice(1).map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        
        <button onClick={() => setShowLabels(!showLabels)} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          {showLabels ? <Eye size={9} /> : <EyeOff size={9} />}
        </button>
        
        <button onClick={resetView} style={{ ...S.btn, ...S.bWar, padding: '2px 6px', fontSize: '0.55rem' }}>
          <RefreshCw size={9} /> Reset
        </button>
        
        {otf && (
          <button onClick={otf} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            {fs ? <Minimize2 size={9} /> : <Maximize2 size={9} />}
          </button>
        )}
      </div>

      <svg 
        width={Math.max(W, lw + mt * 1.5 + 30) * z} 
        height={Math.max(H, (wcsFiltered.length - 1) * rh + hh + 10) * z}
        style={{ transform: `scale(${z})`, transformOrigin: '0 0' }}
      >
        {wcsFiltered.slice(1).map((wc, i) => (
          <text key={wc} x={6} y={hh + i * rh + rh / 2 + 3} fill={T.t2} fontSize={9}>{wc}</text>
        ))}
        
        {bars.map((bar, idx) => {
          const color = bar.functionalGroup ? 
            T[bar.functionalGroup.replace(/[^a-zA-Z]/g, '').toLowerCase()] || T.w : T.w;
          return (
            <g key={bar.id}>
              <rect
                x={lw + bar.start * (W - lw) / mt}
                y={hh + (bar.wi - 1) * rh + 2}
                width={Math.max(2, (bar.su) * (W - lw) / mt)}
                height={rh - 4}
                rx={2}
                fill={color}
                opacity={0.3}
              />
              <rect
                x={lw + (bar.start + bar.su) * (W - lw) / mt}
                y={hh + (bar.wi - 1) * rh + 2}
                width={Math.max(2, bar.st * (W - lw) / mt)}
                height={rh - 4}
                rx={2}
                fill={color}
                opacity={0.8}
              />
              {showLabels && (
                <text
                  x={lw + (bar.start + bar.su / 2 + bar.st / 2) * (W - lw) / mt}
                  y={hh + (bar.wi - 1) * rh + rh / 2 + 4}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize={Math.min(8, rh - 6)}
                  fontWeight={600}
                  style={{ pointerEvents: 'none' }}
                >
                  {bar.sequence || bar.seq || idx + 1}
                </text>
              )}
            </g>
          );
        })}
        
        {[0, 0.25, 0.5, 0.75, 1].map(p => (
          <text key={p} x={lw + p * (W - lw)} y={hh - 6} textAnchor="middle" fill={T.t3} fontSize={7}>
            {Math.round(p * mt)}m
          </text>
        ))}
      </svg>
      
      <div style={{ padding: '4px 8px', fontSize: '0.6rem', color: T.t3, borderTop: `1px solid ${T.b}` }}>
        Showing {filteredOps.length} of {ao.length} operations | Total Time: {Math.round(mt)} min
        {filterWC !== 'all' && ` | WC: ${filterWC}`}
        {filterFG !== 'all' && ` | FG: ${filterFG}`}
        {filterPlant !== 'all' && ` | Plant: ${filterPlant}`}
      </div>
    </div>
  );
};

// ============================================
// FORCE NETWORK
// ============================================
const ForceNetwork = ({ cfg, sel, w, h, fs, otf }) => {
  const { inc, ebom, mbom, bopData, equip, wp, prod, allOperations } = cfg;
  const pn = prod?.name || 'Product';
  const ao = allOperations || extractAllOperations(bopData);
  const [z, sz] = useState(1);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [filterDomain, setFilterDomain] = useState('all');

  const W = fs ? window.innerWidth - 40 : w;
  const H = fs ? window.innerHeight - 120 : h;

  const { nodes, links } = useMemo(() => {
    const n = [];
    const l = [];
    const safeInc = inc || {};
    const safeEbom = Array.isArray(ebom) ? ebom : [];
    const safeMbom = Array.isArray(mbom) ? mbom : [];
    const safeEquip = Array.isArray(equip) ? equip : [];
    
    const an = (name, domain, color, value, group, details = {}) => {
      const node = { id: genId(), name, domain, color, value: value || 1, group: group || 0, details };
      n.push(node);
      return node;
    };
    const al = (s, t, v, color) => l.push({ sid: s.id, tid: t.id, val: v || 1, color });

    let wn, en, mn, bn;
    if (safeInc.workPackage) wn = an(wp?.name || 'WP', 'WP', T.g, 5, 0, { type: 'Work Package' });
    if (safeInc.ebom && safeEbom.length > 0) {
      en = an('EBOM', 'EBOM', T.cy, 3, 1, { type: 'Engineering BOM', count: safeEbom.length });
      if (wn) al(wn, en, 1, T.g);
    }
    if (safeInc.mbom && safeMbom.length > 0) {
      mn = an('MBOM', 'MBOM', T.pk, 3, 1, { type: 'Manufacturing BOM', count: safeMbom.length });
      if (wn) al(wn, mn, 1, T.g);
    }
    if (safeInc.bop && ao.length > 0) {
      bn = an('BOP', 'BOP', T.w, 3, 1, { type: 'Bill of Process', count: ao.length });
      if (wn) al(wn, bn, 1, T.g);
    }

    if (safeInc.ebom) {
      safeEbom.slice(0, 30).forEach(e => {
        const en2 = an(e.name || 'Unnamed', 'EBOM', T.cy, e.quantity || 1, 2, { findNumber: e.findNumber, type: 'EBOM Item' });
        if (en) al(en, en2, e.quantity || 1, T.cy);
      });
      if (safeEbom.length > 30) {
        an(`+${safeEbom.length - 30} more`, 'EBOM', T.t3, 1, 2, { type: 'Additional Items' });
      }
    }

    if (safeInc.mbom) {
      safeMbom.slice(0, 30).forEach(m => {
        const mn2 = an(m.name || 'Unnamed', 'MBOM', T.pk, m.quantity || 1, 2, { findNumber: m.findNumber, type: 'MBOM Item' });
        if (mn) al(mn, mn2, m.quantity || 1, T.pk);
      });
      if (safeMbom.length > 30) {
        an(`+${safeMbom.length - 30} more`, 'MBOM', T.t3, 1, 2, { type: 'Additional Items' });
      }
    }

    if (safeInc.bop) {
      const opsToShow = ao.slice(0, 40);
      opsToShow.forEach(op => {
        const opn = an(op.name || 'Unnamed', 'BOP', T.or, 1, 3, { 
          sequence: op.sequence, 
          workCenter: op.workCenter,
          functionalGroup: op.functionalGroup,
          type: 'Operation'
        });
        if (bn) al(bn, opn, 1, T.w);
      });
      if (ao.length > 40) {
        an(`+${ao.length - 40} more`, 'BOP', T.t3, 1, 3, { type: 'Additional Operations' });
      }
    }

    if (safeInc.boe) {
      const workUnits = safeEquip.filter(e => e.level === 'WorkUnit').slice(0, 20);
      workUnits.forEach(eq => {
        const eqn = an(eq.name || 'Unnamed', 'BOE', T.pp, 1, 4, { 
          level: eq.level, 
          technology: eq.technology,
          type: 'Work Unit'
        });
        if (safeInc.bop) {
          ao.forEach(op => {
            if ((op.workCenter || op.wc) === eq.name) {
              const opNode = n.find(x => x.name === op.name && x.domain === 'BOP');
              if (opNode) al(opNode, eqn, 1, T.pp);
            }
          });
        }
      });
    }

    const groups = {};
    n.forEach(nd => {
      if (!groups[nd.group]) groups[nd.group] = [];
      groups[nd.group].push(nd);
    });

    const cx = W / 2;
    const cy = H / 2;
    Object.entries(groups).forEach(([gr, nds]) => {
      const r = (parseInt(gr) + 1) * Math.min(60, W / 10);
      nds.forEach((nd, i) => {
        const a = (i / nds.length) * Math.PI * 2 - Math.PI / 2;
        nd.x = cx + r * Math.cos(a);
        nd.y = cy + r * Math.sin(a);
        nd.x += (Math.random() - 0.5) * 10;
        nd.y += (Math.random() - 0.5) * 10;
      });
    });

    return { nodes: n, links: l };
  }, [inc, ebom, mbom, bopData, equip, wp, prod, pn, ao, W, H]);

  const filteredNodes = useMemo(() => {
    if (filterDomain === 'all') return nodes;
    return nodes.filter(n => n.domain === filterDomain.toUpperCase());
  }, [nodes, filterDomain]);

  const resetView = () => { sz(1); setFilterDomain('all'); setShowAllLabels(false); };

  return (
    <div style={{ position: 'relative', width: W, height: H, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 10, display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
        <button onClick={() => sz(z => Math.max(0.3, z - 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          <ZoomOut size={9} />
        </button>
        <span style={{ fontSize: '0.55rem', color: T.t3 }}>{Math.round(z * 100)}%</span>
        <button onClick={() => sz(z => Math.min(3, z + 0.1))} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          <ZoomIn size={9} />
        </button>
        <button onClick={() => setShowAllLabels(!showAllLabels)} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
          {showAllLabels ? <Eye size={9} /> : <EyeOff size={9} />}
        </button>
        <select
          value={filterDomain}
          onChange={e => setFilterDomain(e.target.value)}
          style={{ ...S.select, padding: '2px 4px', fontSize: '0.55rem', width: '100px' }}
        >
          <option value="all">All Domains</option>
          {[...new Set(nodes.map(n => n.domain))].filter(d => d).map(d => (
            <option key={d} value={d.toLowerCase()}>{d}</option>
          ))}
        </select>
        <button onClick={resetView} style={{ ...S.btn, ...S.bWar, padding: '2px 6px', fontSize: '0.55rem' }}>
          <RefreshCw size={9} /> Reset
        </button>
        {otf && (
          <button onClick={otf} style={{ ...S.btn, ...S.bGho, padding: '2px 4px', fontSize: '0.55rem' }}>
            {fs ? <Minimize2 size={9} /> : <Maximize2 size={9} />}
          </button>
        )}
      </div>

      <div style={{ ...S.legend, position: 'absolute', top: 32, left: 4, zIndex: 10, background: `${T.s}CC` }}>
        {[{ label: 'WP', color: T.g }, { label: 'EBOM', color: T.cy }, { label: 'MBOM', color: T.pk }, 
          { label: 'BOP', color: T.w }, { label: 'BOE', color: T.pp }].map(item => (
          <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.5rem', color: T.t3 }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>

      <svg width={W} height={H} style={{ transform: `scale(${z})`, transformOrigin: 'center' }}>
        {links.map((l, i) => {
          const s = filteredNodes.find(n => n.id === l.sid);
          const t = filteredNodes.find(n => n.id === l.tid);
          if (!s || !t) return null;
          const isActive = sel?.highlightedIds?.includes(l.sid) || sel?.highlightedIds?.includes(l.tid) || 
                          hoveredNode === l.sid || hoveredNode === l.tid;
          return (
            <line
              key={i}
              x1={s.x}
              y1={s.y}
              x2={t.x}
              y2={t.y}
              stroke={l.color}
              strokeWidth={Math.max(0.3, Math.min(3, l.val))}
              opacity={isActive ? 0.9 : (sel?.selectedNodeId ? 0.03 : 0.15)}
              strokeDasharray={isActive ? 'none' : '4,4'}
            />
          );
        })}
        
        {filteredNodes.map(nd => {
          const hl = sel?.highlightedIds?.includes(nd.id) || false;
          const isHov = hoveredNode === nd.id;
          const r = Math.max(4, Math.min(12, nd.value * 1.8));
          return (
            <g
              key={nd.id}
              style={{ cursor: 'pointer' }}
              onClick={() => sel?.selectNode(nd.id, [nd.id])}
              onMouseEnter={() => setHoveredNode(nd.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <circle
                cx={nd.x}
                cy={nd.y}
                r={r}
                fill={nd.color}
                opacity={sel?.selectedNodeId && !hl ? 0.15 : isHov ? 1 : 0.65}
                stroke={hl ? '#fff' : isHov ? nd.color : 'transparent'}
                strokeWidth={hl ? 2 : 1}
              />
              {(showAllLabels || isHov || hl) && (
                <text
                  x={nd.x}
                  y={nd.y + r + 8}
                  textAnchor="middle"
                  fill={isHov ? T.t : T.t2}
                  fontSize={Math.min(7, r + 2)}
                  fontWeight={isHov ? 700 : 400}
                  style={{ pointerEvents: 'none' }}
                >
                  {nd.name.length > 10 ? nd.name.substring(0, 10) + '...' : nd.name}
                </text>
              )}
              {isHov && nd.details && (
                <text
                  x={nd.x}
                  y={nd.y + r + 18}
                  textAnchor="middle"
                  fill={T.t3}
                  fontSize={5}
                  style={{ pointerEvents: 'none' }}
                >
                  {nd.details.type || ''}
                  {nd.details.count ? ` (${nd.details.count})` : ''}
                  {nd.details.workCenter ? ` WC: ${nd.details.workCenter}` : ''}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ============================================
// TEAMCENTER 2512 COMPATIBLE ZIP CREATOR
// ============================================
const createTeamcenterCompatiblePackage = async (cfg, xmlContent, bmideTypes) => {
  const { prod, inc, ebom, mbom, bopData, equip, tools, skills, wp, qualityPlans, ewiDocs, bt } = cfg;
  const zip = new JSZip();
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const baseName = (prod?.name || 'PRODUCT').replace(/[^a-zA-Z0-9]/g, '_');
  const allOps = extractAllOperations(bopData);
  const safeEbom = Array.isArray(ebom) ? ebom : [];
  const safeMbom = Array.isArray(mbom) ? mbom : [];
  const safeEquip = Array.isArray(equip) ? equip : [];
  const safeTools = Array.isArray(tools) ? tools : [];
  const safeSkills = Array.isArray(skills) ? skills : [];
  const safeQualityPlans = Array.isArray(qualityPlans) ? qualityPlans : [];
  const safeEwiDocs = Array.isArray(ewiDocs) ? ewiDocs : [];
  
  zip.file(`${baseName}.plmxml`, xmlContent);
  
  const dataFolder = zip.folder(`${baseName}_Data`);
  
  // EBOM CSV
  const ebomData = safeEbom.length > 0 ? safeEbom.map(d => ({
    'Find Number': d.findNumber || '',
    'Component Name': d.name || '',
    Description: d.description || '',
    Quantity: d.quantity || 1,
    UOM: d.uom || 'EA',
    Revision: d.revision || 'A',
    'Make/Buy': d.makeBuy || 'BUY',
    'Material': d.material || '',
    'Weight (KG)': d.weight || 0
  })) : [{ 'Find Number': 'N/A', 'Component Name': 'No Data', Description: 'Empty EBOM', Quantity: 0, UOM: 'EA', Revision: 'A', 'Make/Buy': 'N/A', 'Material': '', 'Weight (KG)': 0 }];
  dataFolder.file('EBOM.csv', Papa.unparse(ebomData));
  
  const mbomData = safeMbom.length > 0 ? safeMbom.map(d => ({
    'Find Number': d.findNumber || '',
    'Component Name': d.name || '',
    Description: d.description || '',
    Quantity: d.quantity || 1,
    UOM: d.uom || 'EA',
    Revision: d.revision || 'A',
    'Make/Buy': d.makeBuy || 'BUY',
    'Operation Ref': d.operationReference || '',
    'Material': d.material || '',
    'Weight (KG)': d.weight || 0
  })) : [{ 'Find Number': 'N/A', 'Component Name': 'No Data', Description: 'Empty MBOM', Quantity: 0, UOM: 'EA', Revision: 'A', 'Make/Buy': 'N/A', 'Operation Ref': '', 'Material': '', 'Weight (KG)': 0 }];
  dataFolder.file('MBOM.csv', Papa.unparse(mbomData));
  
  // BOP CSV - with all operation details
  const bopDataRows = allOps.length > 0 ? allOps.map(op => ({
    Sequence: op.sequence || op.seq || '',
    'Operation Name': op.name || '',
    Description: op.description || op.desc || '',
    'Work Center': op.workCenter || op.wc || '',
    'Std Time (min)': op.standardTime || op.stdTime || 0,
    'Setup Time (min)': op.setupTime || op.setup || 0,
    Tools: Array.isArray(op.tools) ? op.tools.join(',') : (op.tools || ''),
    Skills: Array.isArray(op.skills) ? op.skills.join(',') : (op.skills || ''),
    'Control Key': op.controlKey || '',
    Consumes: op.consumes || '',
    Byproducts: op.byproducts || '',
    Parameter: op.parameter || '',
    Inspection: op.inspection || '',
    Output: op.output || '',
    'Queue Time': op.queTime || 0,
    'Functional Group': op.functionalGroup || '',
    'Plant': op.plant || '',
    'Process': op.process || '',
    'Machine': op.machine || '',
    'Tooling': op.tooling || '',
    'Direct MBOM': op.directMBOM || '',
    'Indirect Consumables': op.indirectConsumables || '',
    'Scrap': op.scrap || '',
    'Setup Time Detail': op.setupTimeDetail || '',
    'Machine Run Time': op.machineRunTime || '',
    'Direct Labor Time': op.directLaborTime || '',
    'Skill Required': op.skillRequired || '',
    'Source Location': op.sourceLocation || '',
    'Move Time': op.moveTime || '',
    'Precedes': op.precedes || '',
    'MBOM Ref': op.mbomRef || '',
    'EBOM Ref': op.ebomRef || '',
    'Quality Plan Ref': op.qualityPlanRef || '',
    'EWI Ref': op.ewiRef || '',
    'Operation Type': op.operationType || '',
    'Category': op.category || '',
    'Sub Category': op.subCategory || ''
  })) : [{ Sequence: 'N/A', 'Operation Name': 'No Data', Description: 'Empty BOP', 'Work Center': 'N/A', 'Std Time (min)': 0, 'Setup Time (min)': 0, Tools: '', Skills: '', 'Control Key': '', Consumes: '', Byproducts: '', Parameter: '', Inspection: '', Output: '', 'Queue Time': 0, 'Functional Group': '', 'Plant': '', 'Process': '' }];
  dataFolder.file('BOP.csv', Papa.unparse(bopDataRows));
  
  const boeData = safeEquip.length > 0 ? safeEquip.map(d => ({
    Name: d.name || '',
    Level: d.level || 'WorkUnit',
    Description: d.description || d.desc || '',
    Parent: d.parent || '',
    Technology: d.technology || 'Conventional',
    'Cost Rate ($/h)': d.costRate || 0,
    'Capacity (hrs)': d.capacity || 0,
    'BMIDE Type': d.bmideType || 'MESWorkUnit'
  })) : [{ Name: 'No Data', Level: 'WorkUnit', Description: 'Empty BOE', Parent: '', Technology: 'Conventional', 'Cost Rate ($/h)': 0, 'Capacity (hrs)': 0, 'BMIDE Type': 'MESWorkUnit' }];
  dataFolder.file('BOE.csv', Papa.unparse(boeData));
  
  const toolsData = safeTools.length > 0 ? safeTools.map(d => ({
    Name: d.name || '',
    Type: d.type || '',
    Description: d.description || d.desc || '',
    Quantity: d.quantity || 1,
    'Calibration Due': d.calibrationDue || '',
    Manufacturer: d.manufacturer || '',
    Cost: d.cost || 0
  })) : [{ Name: 'No Data', Type: 'N/A', Description: 'Empty Tools', Quantity: 0, 'Calibration Due': '', Manufacturer: '', Cost: 0 }];
  dataFolder.file('Tools.csv', Papa.unparse(toolsData));
  
  const skillsData = safeSkills.length > 0 ? safeSkills.map(d => ({
    Name: d.name || '',
    Description: d.description || d.desc || '',
    Level: d.level || 1,
    'Validity (months)': d.validityMonths || 24,
    'Certification Body': d.certificationBody || '',
    'Requires Recert': d.requiresRecert || false
  })) : [{ Name: 'No Data', Description: 'Empty Skills', Level: 0, 'Validity (months)': 0, 'Certification Body': '', 'Requires Recert': false }];
  dataFolder.file('Skills.csv', Papa.unparse(skillsData));
  
  const qualityData = safeQualityPlans.length > 0 ? safeQualityPlans.map(qp => ({
    'Plan Name': qp.name || '',
    Description: qp.description || '',
    'Target Operation': qp.targetOperation || '',
    'Inspection Frequency': qp.inspectionFrequency || '',
    Characteristics: (qp.characteristics || []).map(c => c.name).join('; ')
  })) : [{ 'Plan Name': 'No Data', Description: 'Empty Quality Plans', 'Target Operation': '', 'Inspection Frequency': '', Characteristics: '' }];
  dataFolder.file('QualityPlans.csv', Papa.unparse(qualityData));
  
  const ewiData = safeEwiDocs.length > 0 ? safeEwiDocs.map(d => ({
    'Document ID': d.id || '',
    Name: d.name || '',
    Type: d.type || 'PDF',
    Description: d.description || '',
    'Target Operation': d.targetOperation || '',
    Revision: d.revision || 'A',
    Author: d.author || '',
    'Approval Date': d.approvalDate || ''
  })) : [{ 'Document ID': 'N/A', Name: 'No Data', Type: 'PDF', Description: 'Empty EWI', 'Target Operation': '', Revision: 'A', Author: '', 'Approval Date': '' }];
  dataFolder.file('EWI.csv', Papa.unparse(ewiData));
  
  // Excel file
  try {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ebomData), 'EBOM');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(mbomData), 'MBOM');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bopDataRows), 'BOP');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(boeData), 'BOE');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toolsData), 'Tools');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(skillsData), 'Skills');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(qualityData), 'QualityPlans');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ewiData), 'EWI');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    dataFolder.file(`${baseName}_Data.xlsx`, excelBuffer);
  } catch (e) {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, [{ 'Status': 'Excel generation failed' }], 'Info');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    dataFolder.file(`${baseName}_Data.xlsx`, excelBuffer);
  }
  
  // Metadata
  const metaFolder = dataFolder.folder('metadata');
  const safeBt = bt || {};
  const config = {
    product: prod || {},
    workPackage: wp || {},
    includes: inc || {},
    bmideTypes: safeBt,
    generatedDate: new Date().toISOString(),
    version: '15.0',
    generator: 'PLMXML Studio v15.0 Final',
    teamcenterVersion: '2512',
    objectCounts: {
      ebom: safeEbom.length,
      mbom: safeMbom.length,
      operations: allOps.length,
      functionalGroups: extractFunctionalGroups(bopData).length,
      equipment: safeEquip.length,
      tools: safeTools.length,
      skills: safeSkills.length,
      qualityPlans: safeQualityPlans.length,
      ewiDocs: safeEwiDocs.length,
      relations: (xmlContent.match(/<ImanRelation/g) || []).length
    }
  };
  metaFolder.file('config.json', JSON.stringify(config, null, 2));
  
  const journeyMap = buildJourneyMap(safeEbom, safeMbom, bopData, safeEquip, safeTools, safeSkills, safeEwiDocs);
  metaFolder.file('journey_map.json', JSON.stringify(journeyMap, null, 2));
  
  // README
  const readme = `
===========================================
TEAMCENTER 2512 - PLMXML IMPORT PACKAGE
===========================================
Generated: ${new Date().toLocaleString()}
Product: ${prod?.name || 'PRODUCT'}
Version: v15.0

===========================================
IMPORT INSTRUCTIONS
===========================================
1. Extract the ZIP file
2. Import ${baseName}.plmxml using Active Workspace
3. Verify objects were created correctly

===========================================
OBJECT COUNTS
===========================================
EBOM: ${safeEbom.length}
MBOM: ${safeMbom.length}
Operations: ${allOps.length}
Functional Groups: ${extractFunctionalGroups(bopData).length}
Equipment: ${safeEquip.length}
Tools: ${safeTools.length}
Skills: ${safeSkills.length}
Quality Plans: ${safeQualityPlans.length}
EWI Documents: ${safeEwiDocs.length}
Relations: ${(xmlContent.match(/<ImanRelation/g) || []).length}
===========================================
`;
  dataFolder.file('import_readme.txt', readme);
  
  return await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
};

// ============================================
// PLMXML GENERATOR - TEAMCENTER 2512
// ============================================
const generateCompletePLMXML = (cfg) => {
  const { prod, inc, ebom, mbom, bopData, equip, tools, skills, wp, qualityPlans, ewiDocs, bt } = cfg;
  const today = new Date().toISOString().split('T')[0];
  const pn = prod?.name || prod?.productName || 'PRODUCT';
  const pd = prod?.description || prod?.desc || '';
  const safeEbom = Array.isArray(ebom) ? ebom : [];
  const safeMbom = Array.isArray(mbom) ? mbom : [];
  const safeEquip = Array.isArray(equip) ? equip : [];
  const safeTools = Array.isArray(tools) ? tools : [];
  const safeSkills = Array.isArray(skills) ? skills : [];
  const safeQualityPlans = Array.isArray(qualityPlans) ? qualityPlans : [];
  const safeEwiDocs = Array.isArray(ewiDocs) ? ewiDocs : [];
  const safeBt = bt || {};
  const safeInc = inc || {};
  const safeWp = wp || {};
  const allOps = extractAllOperations(bopData);
  const functionalGroups = extractFunctionalGroups(bopData);

  let x = `<?xml version="1.0" encoding="UTF-8"?>\n<PLMXML xmlns="http://www.plmxml.org/Schemas/PLMXMLSchema" language="en-us" time="08:00:00" schemaVersion="6" author="Teamcenter V2512 - PLMXML Studio v15.0" date="${today}">\n`;
  let c = 0;

  // HEADER & SITE
  x += ` <Header id="HDR" traverseRootRefs="#PV-EBOM #PV-MBOM #PV-BOP-WP" transferContext="incremental_import">\n`;
  x += `  <Description>${esc(pn)} - Complete Digital Thread with BOP Hierarchy</Description>\n`;
  x += ` </Header>\n`;
  x += ` <Site id="S" name="IMC--1629097399" siteId="-1629097399">\n`;
  x += `  <ApplicationRef application="Teamcenter" label="gOIAAAAEp71LUC" version="gOIAAAAEp71LUC"/>\n`;
  x += `  <UserData id="UD-S"><UserValue title="dbms" value="1"/></UserData>\n`;
  x += ` </Site>\n`;
  x += ` <AccessIntent id="A" ownerRefs="#S" intent="reference"/>\n`;
  x += ` <RevisionRule id="RR" name="Latest Working">\n`;
  x += `  <ApplicationRef application="Teamcenter" label="QAIAAAA4p71LUC" version="QAIAAAA4p71LUC"/>\n`;
  x += ` </RevisionRule>\n`;
  c += 5;

  // EBOM
  if (safeInc.ebom && safeEbom.length > 0) {
    x += `\n\n <!-- EBOM -->`;
    x += `\n <Product id="EBOM-R" name="${esc(pn)}" subType="${safeBt.ebomItem || 'Part'}" accessRefs="#A" designRequired="false" attributeRefs="AD AP">`;
    x += `\n  <Description>${esc(pd)}</Description>`;
    x += `\n </Product>`;
    c++;
    
    x += `\n <ProductRevision id="EBOM-RR" name="${esc(pn)}" subType="${safeBt.ebomRevision || 'Part Revision'}" accessRefs="#A" masterRef="#EBOM-R" revision="${prod?.revision || prod?.rev || 'A'}" source="unset" attributeRefs="AR">`;
    x += `\n  <UserData id="UD-ER"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
    x += `\n </ProductRevision>`;
    c++;
    
    safeEbom.forEach((it, i) => {
      x += `\n <Product id="EBOM-${i}" name="${esc(it.name || 'Unnamed')}" subType="${safeBt.ebomItem || 'Part'}" accessRefs="#A" designRequired="false" attributeRefs="AD AP">`;
      x += `\n  <Description>${esc(it.description || it.desc || '')}</Description>`;
      x += `\n </Product>`;
      c++;
      
      x += `\n <ProductRevision id="EBOM-R${i}" name="${esc(it.name || 'Unnamed')}" subType="${safeBt.ebomRevision || 'Part Revision'}" accessRefs="#A" masterRef="#EBOM-${i}" revision="${it.revision || it.rev || 'A'}" source="unset" attributeRefs="AR">`;
      x += `\n  <UserData id="UD-E${i}"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
      x += `\n </ProductRevision>`;
      c++;
    });
    
    x += `\n <ProductDef id="PDEF-E">`;
    x += `\n  <InstanceGraph id="IG-E">`;
    x += `\n   <UserData id="UD-IGE" type="TC Specific Properties"><UserValue title="BOM_precision_type" value="imprecise"/></UserData>`;
    safeEbom.forEach((it, i) => {
      x += `\n   <ProductInstance id="PI-E${i}" sequenceNumber="${it.findNumber || it.findNo || '0010'}" transformRef="TR-PI-E${i}" partRef="#PRV-E${i}">`;
      x += `\n    <Transform id="TR-PI-E${i}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
      x += `\n   </ProductInstance>`;
      c++;
    });
    safeEbom.forEach((it, i) => {
      x += `\n   <ProductRevisionView id="PRV-E${i}" revisionRef="#EBOM-R${i}" accessRefs="#A"/>`;
      c++;
    });
    x += `\n  </InstanceGraph>`;
    x += `\n </ProductDef>`;
    c++;
    
    const occRefs = safeEbom.map((_, i) => `OCC-E${i}`).join(' ');
    x += `\n <ProductView id="PV-EBOM" rootRefs="OCC-E-ROOT" primaryOccurrenceRef="OCC-E-ROOT" ruleRefs="#RR">`;
    x += `\n  <UserData id="UD-PVE" type="TC Specific Properties"><UserValue title="BOM_precision_type" value="imprecise"/></UserData>`;
    x += `\n  <Occurrence id="OCC-E-ROOT" occurrenceRefs="${occRefs}" instancedRef="#EBOM-RR">`;
    x += `\n   <Transform id="TR-E">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
    x += `\n  </Occurrence>`;
    safeEbom.forEach((it, i) => {
      x += `\n  <Occurrence id="OCC-E${i}" instancedRef="#EBOM-R${i}" parentRef="#OCC-E-ROOT" instanceRefs="#PI-E${i}">`;
      x += `\n   <Transform id="TR-OCC-E${i}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
      x += `\n  </Occurrence>`;
      c++;
    });
    x += `\n </ProductView>`;
    c++;
  }

  // MBOM
  if (safeInc.mbom && safeMbom.length > 0) {
    const mn = prod?.mbomName || `${pn}-M`;
    x += `\n\n <!-- MBOM -->`;
    x += `\n <Product id="MBOM-R" name="${esc(mn)}" subType="${safeBt.mbomItem || 'ARA4MPart'}" accessRefs="#A" designRequired="false" attributeRefs="ADM APM">`;
    x += `\n  <Description>${esc(pd)} - Mfg ${prod?.mbomRev || '000'}</Description>`;
    x += `\n </Product>`;
    c++;
    
    x += `\n <ProductRevision id="MBOM-RR" name="${esc(mn)}" subType="${safeBt.mbomRevision || 'ARA4MPartRevision'}" accessRefs="#A" masterRef="#MBOM-R" revision="${prod?.mbomRev || '000'}" source="unset" attributeRefs="ARM">`;
    x += `\n  <UserData id="UD-MR"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
    x += `\n </ProductRevision>`;
    c++;
    
    safeMbom.forEach((it, i) => {
      x += `\n <Product id="MBOM-${i}" name="${esc(it.name || 'Unnamed')}" subType="${safeBt.mbomItem || 'ARA4MPart'}" accessRefs="#A" designRequired="false" attributeRefs="ADM APM">`;
      x += `\n  <Description>${esc(it.description || it.desc || '')}</Description>`;
      x += `\n </Product>`;
      c++;
      
      x += `\n <ProductRevision id="MBOM-R${i}" name="${esc(it.name || 'Unnamed')}" subType="${safeBt.mbomRevision || 'ARA4MPartRevision'}" accessRefs="#A" masterRef="#MBOM-${i}" revision="${it.revision || it.rev || 'A'}" source="unset" attributeRefs="ARM">`;
      x += `\n  <UserData id="UD-M${i}"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
      x += `\n </ProductRevision>`;
      c++;
    });
    
    x += `\n <ProductDef id="PDEF-M">`;
    x += `\n  <InstanceGraph id="IG-M">`;
    x += `\n   <UserData id="UD-IGM" type="TC Specific Properties"><UserValue title="BOM_precision_type" value="imprecise"/></UserData>`;
    safeMbom.forEach((it, i) => {
      x += `\n   <ProductInstance id="PI-M${i}" sequenceNumber="${it.findNumber || it.findNo || '0010'}" transformRef="TR-PI-M${i}" partRef="#PRV-M${i}">`;
      x += `\n    <Transform id="TR-PI-M${i}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
      x += `\n   </ProductInstance>`;
      c++;
    });
    safeMbom.forEach((it, i) => {
      x += `\n   <ProductRevisionView id="PRV-M${i}" revisionRef="#MBOM-R${i}" accessRefs="#A"/>`;
      c++;
    });
    x += `\n  </InstanceGraph>`;
    x += `\n </ProductDef>`;
    c++;
    
    const mOccRefs = safeMbom.map((_, i) => `OCC-M${i}`).join(' ');
    x += `\n <ProductView id="PV-MBOM" rootRefs="OCC-M-ROOT" primaryOccurrenceRef="OCC-M-ROOT" ruleRefs="#RR">`;
    x += `\n  <UserData id="UD-PVM" type="TC Specific Properties"><UserValue title="BOM_precision_type" value="imprecise"/></UserData>`;
    x += `\n  <Occurrence id="OCC-M-ROOT" occurrenceRefs="${mOccRefs}" instancedRef="#MBOM-RR">`;
    x += `\n   <Transform id="TR-M">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
    x += `\n  </Occurrence>`;
    safeMbom.forEach((it, i) => {
      x += `\n  <Occurrence id="OCC-M${i}" instancedRef="#MBOM-R${i}" parentRef="#OCC-M-ROOT" instanceRefs="#PI-M${i}">`;
      x += `\n   <Transform id="TR-OCC-M${i}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
      x += `\n  </Occurrence>`;
      c++;
    });
    x += `\n </ProductView>`;
    c++;
  }

  // BOP
  if (safeInc.bop && (allOps.length > 0 || functionalGroups.length > 0)) {
    const bn = prod?.bopName || `BOP-${pn}`;
    
    x += `\n\n <!-- BOP ROOT -->`;
    x += `\n <Product id="BOP-R" name="${esc(bn)}" subType="${safeBt.bopItem || 'MEProductBOP'}" accessRefs="#A">`;
    x += `\n  <Description>Manufacturing Process Plan | ${allOps.length} Operations</Description>`;
    x += `\n </Product>`;
    c++;
    
    x += `\n <ProductRevision id="BOP-RR" name="${esc(bn)}" subType="${safeBt.bopRevision || 'MEProductBOPRevision'}" accessRefs="#A" masterRef="#BOP-R" revision="A">`;
    x += `\n  <UserData id="UD-BR"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
    x += `\n </ProductRevision>`;
    c++;
    
    if (bopData?.productBOP) {
      const processes = bopData.productBOP.processes || [];
      
      processes.forEach((process, pi) => {
        const procId = `PROC-${pi}`;
        x += `\n <!-- Process: ${process.name || 'Unnamed'} -->`;
        x += `\n <ProcessBOP id="${procId}" name="${esc(process.name || 'Process-' + (pi + 1))}" subType="MEProcessBOP" accessRefs="#A">`;
        x += `\n  <Description>${esc(process.description || '')} | Sequence: ${process.sequence || 'N/A'}</Description>`;
        x += `\n </ProcessBOP>`;
        c++;
        
        const prodProcRelId = `PROD-PROC-${pi}`;
        x += `\n <ProductBOPProcess id="${prodProcRelId}" parentRef="#BOP-R" childRef="#${procId}" relationType="ProductBOPProcess"/>`;
        c++;
        
        const plantBOPs = process.plantBOPs || [];
        plantBOPs.forEach((plantBOP, pbi) => {
          const plantId = `PBOP-${pi}-${pbi}`;
          x += `\n <!-- Plant BOP: ${plantBOP.name || 'Unnamed'} -->`;
          x += `\n <PlantBOP id="${plantId}" name="${esc(plantBOP.name || 'Plant-BOP-' + (pbi + 1))}" subType="MEPlantBOP" accessRefs="#A">`;
          x += `\n  <Description>${esc(plantBOP.description || '')} | Plant: ${plantBOP.plant || 'N/A'}</Description>`;
          x += `\n </PlantBOP>`;
          c++;
          
          const procPlantRelId = `PROC-PLANT-${pi}-${pbi}`;
          x += `\n <ProcessPlantBOP id="${procPlantRelId}" parentRef="#${procId}" childRef="#${plantId}" relationType="ProcessPlantBOP"/>`;
          c++;
          
          const fgs = plantBOP.functionalGroups || [];
          fgs.forEach((fg, fgi) => {
            const fgId = `FG-${pi}-${pbi}-${fgi}`;
            x += `\n <!-- Functional Group: ${fg.name || 'Unnamed'} -->`;
            x += `\n <FunctionalGroup id="${fgId}" name="${esc(fg.name || 'FG-' + (fgi + 1))}" subType="${safeBt.funcGroup || 'Mbc0FuncGroup'}" accessRefs="#A">`;
            x += `\n  <Description>${esc(fg.description || '')} | Sequence: ${fg.sequence || 'N/A'}</Description>`;
            x += `\n </FunctionalGroup>`;
            c++;
            
            const fgRevId = `${fgId}-R`;
            x += `\n <FunctionalGroupRevision id="${fgRevId}" name="${esc(fg.name || 'FG-' + (fgi + 1))}" subType="${safeBt.funcGroupRevision || 'Mbc0FuncGroupRevision'}" accessRefs="#A" masterRef="#${fgId}" revision="A">`;
            x += `\n  <UserData id="UD-${fgId}-R"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
            x += `\n </FunctionalGroupRevision>`;
            c++;

            const plantFgRelId = `PLANT-FG-${pi}-${pbi}-${fgi}`;
            x += `\n <PlantBOPFunctionalGroup id="${plantFgRelId}" parentRef="#${plantId}" childRef="#${fgId}" relationType="PlantBOPFunctionalGroup"/>`;
            c++;
            
            const ops = fg.operations || [];
            ops.forEach((op, oi) => {
              const opId = `OP-${pi}-${pbi}-${fgi}-${oi}`;
              const opSeq = op.sequence || op.seq || String((oi + 1) * 10);
              
              x += `\n <!-- Operation: ${op.name || 'Unnamed'} -->`;
              x += `\n <Operation id="${opId}" name="${esc(op.name || 'Op-' + (oi + 1))}" subType="${safeBt.operation || 'MEOP'}" accessRefs="#A">`;
              x += `\n  <Description>${esc(op.description || op.desc || '')} | WC:${op.workCenter || op.wc || 'N/A'} | T:${op.standardTime || op.stdTime || 0}m</Description>`;
              
              x += `\n  <UserData id="UD-${opId}" type="TC Specific Properties">`;
              if (op.controlKey) x += `\n   <UserValue title="controlKey" value="${esc(op.controlKey)}"/>`;
              if (op.workCenter || op.wc) x += `\n   <UserValue title="workCenter" value="${esc(op.workCenter || op.wc)}"/>`;
              if (op.standardTime || op.stdTime) x += `\n   <UserValue title="standardTime" value="${op.standardTime || op.stdTime}"/>`;
              if (op.setupTime || op.setup) x += `\n   <UserValue title="setupTime" value="${op.setupTime || op.setup}"/>`;
              if (op.tools) x += `\n   <UserValue title="tools" value="${esc(op.tools)}"/>`;
              if (op.skills) x += `\n   <UserValue title="skills" value="${esc(op.skills)}"/>`;
              if (op.machine) x += `\n   <UserValue title="machine" value="${esc(op.machine)}"/>`;
              if (op.tooling) x += `\n   <UserValue title="tooling" value="${esc(op.tooling)}"/>`;
              if (op.parameter) x += `\n   <UserValue title="parameter" value="${esc(op.parameter)}"/>`;
              if (op.consumes) x += `\n   <UserValue title="consumes" value="${esc(op.consumes)}"/>`;
              if (op.indirectConsumables) x += `\n   <UserValue title="indirectConsumables" value="${esc(op.indirectConsumables)}"/>`;
              if (op.byproducts) x += `\n   <UserValue title="byproducts" value="${esc(op.byproducts)}"/>`;
              if (op.setupTimeDetail) x += `\n   <UserValue title="setupTimeDetail" value="${esc(op.setupTimeDetail)}"/>`;
              if (op.machineRunTime) x += `\n   <UserValue title="machineRunTime" value="${esc(op.machineRunTime)}"/>`;
              if (op.directLaborTime) x += `\n   <UserValue title="directLaborTime" value="${esc(op.directLaborTime)}"/>`;
              if (op.skillRequired) x += `\n   <UserValue title="skillRequired" value="${esc(op.skillRequired)}"/>`;
              if (op.workInstruction) x += `\n   <UserValue title="workInstruction" value="${esc(op.workInstruction)}"/>`;
              if (op.inspection) x += `\n   <UserValue title="inspection" value="${esc(op.inspection)}"/>`;
              if (op.sourceLocation) x += `\n   <UserValue title="sourceLocation" value="${esc(op.sourceLocation)}"/>`;
              if (op.output) x += `\n   <UserValue title="output" value="${esc(op.output)}"/>`;
              if (op.queTime !== undefined) x += `\n   <UserValue title="queTime" value="${op.queTime}"/>`;
              if (op.moveTime !== undefined) x += `\n   <UserValue title="moveTime" value="${op.moveTime}"/>`;
              if (op.precedes) x += `\n   <UserValue title="precedes" value="${esc(op.precedes)}"/>`;
              if (op.mbomRef) x += `\n   <UserValue title="mbomRef" value="${esc(op.mbomRef)}"/>`;
              if (op.ebomRef) x += `\n   <UserValue title="ebomRef" value="${esc(op.ebomRef)}"/>`;
              if (op.qualityPlanRef) x += `\n   <UserValue title="qualityPlanRef" value="${esc(op.qualityPlanRef)}"/>`;
              if (op.ewiRef) x += `\n   <UserValue title="ewiRef" value="${esc(op.ewiRef)}"/>`;
              if (op.operationType) x += `\n   <UserValue title="operationType" value="${esc(op.operationType)}"/>`;
              if (op.category) x += `\n   <UserValue title="category" value="${esc(op.category)}"/>`;
              if (op.subCategory) x += `\n   <UserValue title="subCategory" value="${esc(op.subCategory)}"/>`;
              if (op.directMBOM) x += `\n   <UserValue title="directMBOM" value="${esc(op.directMBOM)}"/>`;
              if (op.scrap) x += `\n   <UserValue title="scrap" value="${esc(op.scrap)}"/>`;
              x += `\n  </UserData>`;
              x += `\n </Operation>`;
              c++;
              
              const opRevId = `${opId}-R`;
              x += `\n <OperationRevision id="${opRevId}" name="${esc(op.name || 'Op-' + (oi + 1))}" subType="${safeBt.operationRevision || 'MEOPRevision'}" accessRefs="#A" masterRef="#${opId}" revision="A">`;
              x += `\n  <UserData id="UD-${opRevId}"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`;
              x += `\n </OperationRevision>`;
              c++;
              
              const fgOpRelId = `FG-OP-${pi}-${pbi}-${fgi}-${oi}`;
              x += `\n <FunctionalGroupOperation id="${fgOpRelId}" parentRef="#${fgId}" childRef="#${opId}" relationType="FunctionalGroupOperation">`;
              x += `\n  <UserData><UserValue title="sequence" value="${opSeq}"/></UserData>`;
              x += `\n </FunctionalGroupOperation>`;
              c++;
            });
          });
        });
      });
      
      x += `\n\n <!-- BOP VIEW - Hierarchical Structure -->`;
      x += `\n <ProductView id="PV-BOP-WP" rootRefs="OCC-BOP-ROOT" primaryOccurrenceRef="OCC-BOP-ROOT" ruleRefs="#RR">`;
      x += `\n  <UserData id="UD-PV-BOP" type="TC Specific Properties">`;
      x += `\n   <UserValue title="BOM_precision_type" value="imprecise"/>`;
      x += `\n  </UserData>`;
      
      x += `\n  <Occurrence id="OCC-BOP-ROOT" instancedRef="#BOP-RR">`;
      x += `\n   <Transform id="TR-BOP-ROOT">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
      c++;
      
      processes.forEach((process, pi) => {
        const procOccId = `OCC-PROC-${pi}`;
        x += `\n   <Occurrence id="${procOccId}" instancedRef="#PROC-${pi}" parentRef="#OCC-BOP-ROOT">`;
        x += `\n    <Transform id="TR-${procOccId}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
        c++;
        
        const plantBOPs = process.plantBOPs || [];
        plantBOPs.forEach((plantBOP, pbi) => {
          const plantOccId = `OCC-PLANT-${pi}-${pbi}`;
          x += `\n    <Occurrence id="${plantOccId}" instancedRef="#PBOP-${pi}-${pbi}" parentRef="#${procOccId}">`;
          x += `\n     <Transform id="TR-${plantOccId}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
          c++;
          
          const fgs = plantBOP.functionalGroups || [];
          fgs.forEach((fg, fgi) => {
            const fgOccId = `OCC-FG-${pi}-${pbi}-${fgi}`;
            x += `\n     <Occurrence id="${fgOccId}" instancedRef="#FG-${pi}-${pbi}-${fgi}" parentRef="#${plantOccId}">`;
            x += `\n      <Transform id="TR-${fgOccId}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
            c++;
            
            const ops = fg.operations || [];
            ops.forEach((op, oi) => {
              const opOccId = `OCC-OP-${pi}-${pbi}-${fgi}-${oi}`;
              const opId = `OP-${pi}-${pbi}-${fgi}-${oi}`;
              x += `\n      <Occurrence id="${opOccId}" instancedRef="#${opId}" parentRef="#${fgOccId}">`;
              x += `\n       <Transform id="TR-${opOccId}">1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1</Transform>`;
              x += `\n      </Occurrence>`;
              c++;
            });
            
            x += `\n     </Occurrence>`;
          });
          
          x += `\n    </Occurrence>`;
        });
        
        x += `\n   </Occurrence>`;
      });
      
      x += `\n  </Occurrence>`;
      x += `\n </ProductView>`;
      c++;
    }

    // WORK PACKAGE
    if (safeInc.workPackage) {
      x += `\n\n <!-- WORK PACKAGE -->`;
      x += `\n <CollaborationContext id="${safeWp.id || 'WP-DEFAULT'}" name="${esc(safeWp.name || 'Work Package')}" subType="${safeBt.workPackage || 'MECollaborationContext'}" accessRefs="#A">`;
      x += `\n  <Description>Work Package | Plant:${safeWp.plant || 'N/A'} | Owner:${safeWp.owner || 'N/A'}</Description>`;
      x += `\n </CollaborationContext>`;
      c++;
      
      if (safeInc.ebom) {
        x += `\n <ImanRelation id="WP-EBOM" subType="ImanRelation" accessRefs="#A" relationType="WorkPackageEBOM">`;
        x += `\n  <ClassifiedObjectRef idref="${safeWp.id || 'WP-DEFAULT'}"/>`;
        x += `\n  <ClassifiedByRef idref="#EBOM-RR"/>`;
        x += `\n </ImanRelation>`;
        c++;
      }
      if (safeInc.mbom) {
        x += `\n <ImanRelation id="WP-MBOM" subType="ImanRelation" accessRefs="#A" relationType="WorkPackageMBOM">`;
        x += `\n  <ClassifiedObjectRef idref="${safeWp.id || 'WP-DEFAULT'}"/>`;
        x += `\n  <ClassifiedByRef idref="#MBOM-RR"/>`;
        x += `\n </ImanRelation>`;
        c++;
      }
      if (safeInc.bop) {
        x += `\n <ImanRelation id="WP-BOP" subType="ImanRelation" accessRefs="#A" relationType="WorkPackageBOP">`;
        x += `\n  <ClassifiedObjectRef idref="${safeWp.id || 'WP-DEFAULT'}"/>`;
        x += `\n  <ClassifiedByRef idref="#BOP-RR"/>`;
        x += `\n </ImanRelation>`;
        c++;
      }
    }
  }

  // BOE
  if (safeInc.boe) {
    x += `\n\n <!-- BOE -->`;
    safeEquip.forEach((eq, i) => {
      x += `\n <Equipment id="BOE-${i}" name="${esc(eq.name || 'Unnamed')}" subType="${eq.bmideType || 'MESWorkUnit'}" accessRefs="#A">`;
      x += `\n  <Description>${esc(eq.description || eq.desc || '')}</Description>`;
      x += `\n </Equipment>`;
      c++;
    });
    safeEquip.forEach((eq, i) => {
      if (eq.parent) {
        const pi = safeEquip.findIndex(e => e.name === eq.parent);
        if (pi >= 0) {
          x += `\n <EquipmentHierarchy id="EH-${i}" parentRef="#BOE-${pi}" childRef="#BOE-${i}"/>`;
          c++;
        }
      }
    });
  }

  // TOOLS
  if (safeInc.tools) {
    x += `\n\n <!-- TOOLS -->`;
    safeTools.forEach((t, i) => {
      x += `\n <Resource id="T-${i}" name="${esc(t.name || 'Unnamed')}" subType="${safeBt.tools || 'MESResource'}" accessRefs="#A">`;
      x += `\n  <Description>${esc(t.description || t.desc || '')} - ${t.type || 'N/A'}</Description>`;
      x += `\n </Resource>`;
      c++;
    });
  }

  // SKILLS
  if (safeInc.skills) {
    x += `\n\n <!-- SKILLS -->`;
    safeSkills.forEach((sk, i) => {
      x += `\n <Skill id="SK-${i}" name="${esc(sk.name || 'Unnamed')}" subType="${safeBt.skill || 'MESSkill'}" accessRefs="#A">`;
      x += `\n  <Description>${esc(sk.description || sk.desc || '')} - L${sk.level || 1}</Description>`;
      x += `\n </Skill>`;
      c++;
    });
  }

  // QUALITY
  if (safeInc.quality) {
    x += `\n\n <!-- QUALITY -->`;
    safeQualityPlans.forEach((qp, qi) => {
      x += `\n <InspectionPlan id="IP-${qi}" name="${esc(qp.name || 'Unnamed')}" subType="MESInspectionPlan" accessRefs="#A" revision="A">`;
      x += `\n  <Description>${esc(qp.description || '')}</Description>`;
      x += `\n </InspectionPlan>`;
      c++;
      (qp.characteristics || []).forEach((qc, qci) => {
        x += `\n <Characteristic id="QC-${qi}-${qci}" name="${esc(qc.name || 'Unnamed')}" subType="MESQualityCharacteristic" accessRefs="#A">`;
        x += `\n  <Description>${esc(qc.description || '')}</Description>`;
        x += `\n </Characteristic>`;
        c++;
      });
    });
  }

  // EWI
  if (safeInc.ewi) {
    x += `\n\n <!-- EWI -->`;
    safeEwiDocs.forEach((ewi, i) => {
      x += `\n <Document id="EWI-${i}" name="${esc(ewi.name || ewi.id || 'Unnamed')}" subType="${safeBt.document || 'Document'}" accessRefs="#A">`;
      x += `\n  <Description>${esc(ewi.description || '')}</Description>`;
      x += `\n </Document>`;
      c++;
    });
  }

  x += `\n</PLMXML>`;
  return { xml: x, count: c };
};

// ============================================
// INTEGRATED DASHBOARD
// ============================================
const IntegratedDashboard = ({ cfg }) => {
  const sel = useCoordinatedSelection();
  const [fs, setFs] = useState(null);
  const [vizTab, setVizTab] = useState('all');
  const [dashMode, setDashMode] = useState('grid');
  const [viewCount, setViewCount] = useState(0);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const panelStyle = {
    border: `1px solid ${T.b}`,
    borderRadius: '6px',
    overflow: 'hidden',
    background: T.s,
    position: 'relative'
  };

  const components = {
    icicle: { title: '📐 Icicle Hierarchy', color: T.cy, Component: IcicleDiagram },
    alluvial: { title: '🔗 Journey Alluvial Flow', color: T.pk, Component: JourneyAlluvialFlow },
    gantt: { title: '⏱️ Gantt Timeline', color: T.w, Component: GanttSwimlane },
    network: { title: '🕸️ Network Relations', color: T.pp, Component: ForceNetwork }
  };

  const resetAllViews = useCallback(() => {
    setVizTab('all');
    setDashMode('grid');
    sel.clearSelection();
    setViewCount(prev => prev + 1);
  }, [sel]);

  if (fs) {
    const Comp = components[fs]?.Component;
    if (!Comp) return null;
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, background: T.bg, padding: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: T.pl }}>{components[fs]?.title}</span>
          <button onClick={() => setFs(null)} style={{ ...S.btn, ...S.bGho, padding: '3px 7px', marginLeft: 'auto' }}>
            <Minimize2 size={12} /> Exit
          </button>
        </div>
        <Comp 
          cfg={cfg} 
          sel={sel} 
          w={windowSize.width - 20} 
          h={windowSize.height - 80} 
          isFullscreen={true} 
          onToggleFullscreen={() => setFs(null)} 
        />
      </div>
    );
  }

  return (
    <div>
      <div style={S.filterBar}>
        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
          {['all', 'icicle', 'alluvial', 'gantt', 'network'].map(id => (
            <button
              key={id}
              onClick={() => setVizTab(id)}
              style={{
                ...S.btn,
                background: vizTab === id ? T.p : T.s,
                color: vizTab === id ? 'white' : T.t2,
                padding: '3px 8px',
                fontSize: '0.62rem'
              }}
            >
              {id === 'all' ? '📊 All Views' : components[id]?.title}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '3px' }}>
          <button onClick={resetAllViews} style={{ ...S.btn, ...S.bWar, padding: '3px 6px', fontSize: '0.58rem' }}>
            <RefreshCw size={12} /> Reset All
          </button>
          <button onClick={() => setDashMode(m => m === 'grid' ? 'list' : 'grid')} style={{ ...S.btn, ...S.bGho, padding: '3px 6px', fontSize: '0.58rem' }}>
            {dashMode === 'grid' ? <List size={12} /> : <Grid size={12} />}
          </button>
        </div>
      </div>

      {vizTab === 'all' ? (
        <div style={{
          display: dashMode === 'grid' ? 'grid' : 'flex',
          gridTemplateColumns: dashMode === 'grid' ? '1fr 1fr' : '1fr',
          gridTemplateRows: dashMode === 'grid' ? '1fr 1fr' : 'auto',
          gap: '6px',
          height: dashMode === 'grid' ? 'calc(100vh - 180px)' : 'auto',
          minHeight: dashMode === 'grid' ? '500px' : 'auto',
          flexDirection: dashMode === 'list' ? 'column' : 'row'
        }}>
          {Object.entries(components).map(([id, { title, color, Component }]) => (
            <div key={id} style={panelStyle}>
              <div style={{
                padding: '3px 7px',
                fontSize: '0.65rem',
                fontWeight: 600,
                color,
                borderBottom: `1px solid ${T.b}`,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                {title}
                <button
                  onClick={() => setFs(id)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: T.t3, cursor: 'pointer', padding: '1px 3px' }}
                >
                  <Maximize2 size={10} />
                </button>
              </div>
              <Component 
                cfg={cfg} 
                selection={sel} 
                width={dashMode === 'grid' ? windowSize.width / 2 - 40 : windowSize.width - 60} 
                height={dashMode === 'grid' ? 220 : 300} 
                key={`${id}-${viewCount}`}
              />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...panelStyle, height: 'calc(100vh - 180px)', minHeight: '400px' }}>
          <div style={{
            padding: '3px 7px',
            fontSize: '0.65rem',
            fontWeight: 600,
            color: T.pl,
            borderBottom: `1px solid ${T.b}`,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {components[vizTab]?.title}
            <button onClick={() => setFs(vizTab)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: T.t3, cursor: 'pointer', padding: '1px 3px' }}>
              <Maximize2 size={10} />
            </button>
          </div>
          {vizTab === 'icicle' && <IcicleDiagram cfg={cfg} sel={sel} w={windowSize.width - 60} h={windowSize.height - 250} key={`icicle-${viewCount}`} />}
          {vizTab === 'alluvial' && <JourneyAlluvialFlow cfg={cfg} selection={sel} width={windowSize.width - 60} height={windowSize.height - 250} key={`alluvial-${viewCount}`} />}
          {vizTab === 'gantt' && <GanttSwimlane cfg={cfg} w={windowSize.width - 60} h={windowSize.height - 250} key={`gantt-${viewCount}`} />}
          {vizTab === 'network' && <ForceNetwork cfg={cfg} sel={sel} w={windowSize.width - 60} h={windowSize.height - 250} key={`network-${viewCount}`} />}
        </div>
      )}

      {sel.selectedNodeId && (
        <div style={{
          marginTop: '4px',
          padding: '3px 7px',
          background: T.s2,
          borderRadius: '4px',
          fontSize: '0.6rem',
          color: T.t2,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span>Selected: <strong style={{ color: T.pl }}>1 object</strong></span>
          <button onClick={sel.clearSelection} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: T.t3, cursor: 'pointer', fontSize: '0.58rem' }}>
            ✕ Clear
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN APP
// ============================================
export default function Aiplmxml() {
  const [tab, setTab] = useState('dashboard');
  const [subTab, setSubTab] = useState('ebom');
  const [toast, setToast] = useState(null);
  const [xml, setXml] = useState('');
  const [objCount, setObjCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [showImportHelp, setShowImportHelp] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [importPreview, setImportPreview] = useState(false);
  const [importSummary, setImportSummary] = useState({ totalRows: 0, sheets: 0, sheetDetails: {} });
  const [importedData, setImportedData] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [dendroZoom, setDendroZoom] = useState(0.85);
  const [dendroExpanded, setDendroExpanded] = useState(false);
  const [bmideUploaded, setBmideUploaded] = useState(false);
  const [bmideTypes, setBmideTypes] = useState([]);
  const [globalPrefix, setGlobalPrefix] = useState('');
  const [bomLevels, setBomLevels] = useState(3);
  const [bomRowsPerLevel, setBomRowsPerLevel] = useState(3);
  const [prefixApplied, setPrefixApplied] = useState(false);
  const [showValidator, setShowValidator] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [showProductGenerator, setShowProductGenerator] = useState(false);
  const [generatedProductData, setGeneratedProductData] = useState(null);
  
  const fileRef = useRef(null);
  const bmideFileRef = useRef(null);
  const loadFileRef = useRef(null);

  // State with safe defaults
  const [prod, setProd] = useState(defaultProduct || {});
  const [wp, setWp] = useState(defaultWorkPackage || {});
  const [ebom, setEbom] = useState(() => {
    const data = Array.isArray(defaultEBOM) ? defaultEBOM : [];
    return data.map(d => ({
      id: uuidv4(),
      findNumber: d.findNumber || '',
      name: d.name || '',
      description: d.description || '',
      quantity: d.quantity || 1,
      uom: d.uom || 'EA',
      revision: d.revision || 'A',
      makeBuy: d.makeBuy || 'BUY'
    }));
  });
  const [mbom, setMbom] = useState(() => {
    const data = Array.isArray(defaultMBOM) ? defaultMBOM : [];
    return data.map(d => ({
      id: uuidv4(),
      findNumber: d.findNumber || '',
      name: d.name || '',
      description: d.description || '',
      quantity: d.quantity || 1,
      uom: d.uom || 'EA',
      revision: d.revision || 'A',
      makeBuy: d.makeBuy || 'BUY',
      operationReference: d.operationReference || ''
    }));
  });
  const [bopData, setBopData] = useState(() => {
    return defaultBOP || { functionalGroups: [] };
  });
  const [equip, setEquip] = useState(() => {
    const data = Array.isArray(defaultBOE) ? defaultBOE : [];
    return data.map(d => ({ ...d, id: uuidv4() }));
  });
  const [tools, setTools] = useState(() => {
    const data = Array.isArray(defaultTools) ? defaultTools : [];
    return data.map(d => ({ ...d, id: uuidv4() }));
  });
  const [skills, setSkills] = useState(() => {
    const data = Array.isArray(defaultSkills) ? defaultSkills : [];
    return data.map(d => ({ ...d, id: uuidv4() }));
  });
  const [qualityPlans, setQualityPlans] = useState(() => {
    return Array.isArray(defaultQualityPlans) ? defaultQualityPlans : [];
  });
  const [ewiDocs, setEwiDocs] = useState(() => {
    return Array.isArray(defaultEWI) ? defaultEWI : [];
  });
  const [plants, setPlants] = useState(() => {
    const plantData = defaultPlants?.plants || [];
    if (plantData.length > 0 && typeof plantData[0] === 'string') {
      return plantData.map(p => ({ code: p, name: p, description: '', country: '', timezone: '', active: true, capabilities: [] }));
    }
    return plantData;
  });
  const [inc, setInc] = useState({ 
    ebom: true, mbom: true, bop: true, boe: true, 
    workPackage: true, tools: true, skills: true, 
    ewi: true, quality: true 
  });
  const [bt, setBt] = useState({ 
    ebomItem: 'Part', ebomRevision: 'Part Revision', 
    mbomItem: 'ARA4MPart', mbomRevision: 'ARA4MPartRevision',
    bopItem: 'MEProductBOP', bopRevision: 'MEProductBOPRevision',
    operation: 'MEOP', operationRevision: 'MEOPRevision',
    funcGroup: 'Mbc0FuncGroup', funcGroupRevision: 'Mbc0FuncGroupRevision',
    workPackage: 'MECollaborationContext', tools: 'MESResource', 
    skill: 'MESSkill', document: 'Document' 
  });

  const st = useCallback((m, t = 'success') => setToast({ message: m, type: t }), []);

  // Safe derived data
  const allOps = useMemo(() => {
    return extractAllOperations(bopData);
  }, [bopData]);

  const functionalGroups = useMemo(() => {
    return extractFunctionalGroups(bopData);
  }, [bopData]);

  const workCenters = useMemo(() => {
    return extractWorkCenters(bopData);
  }, [bopData]);

  const techGroups = useMemo(() => {
    return groupByTechnology(Array.isArray(equip) ? equip : []);
  }, [equip]);

  const stats = useMemo(() => ({
    ebom: Array.isArray(ebom) ? ebom.length : 0,
    mbom: Array.isArray(mbom) ? mbom.length : 0,
    ops: allOps.length,
    fgs: functionalGroups.length,
    equip: Array.isArray(equip) ? equip.length : 0,
    tools: Array.isArray(tools) ? tools.length : 0,
    skills: Array.isArray(skills) ? skills.length : 0,
    workCenters: workCenters.length
  }), [ebom, mbom, allOps, functionalGroups, equip, tools, skills, workCenters]);

  const vizCfg = useMemo(() => ({
    prod: prod || {},
    inc: inc || {},
    ebom: Array.isArray(ebom) ? ebom : [],
    mbom: Array.isArray(mbom) ? mbom : [],
    bopData: bopData || { functionalGroups: [] },
    equip: Array.isArray(equip) ? equip : [],
    tools: Array.isArray(tools) ? tools : [],
    skills: Array.isArray(skills) ? skills : [],
    ewiDocs: Array.isArray(ewiDocs) ? ewiDocs : [],
    wp: wp || {},
    allOperations: allOps,
    functionalGroups: functionalGroups,
    workCenters: workCenters
  }), [prod, inc, ebom, mbom, bopData, equip, tools, skills, ewiDocs, wp, allOps, functionalGroups, workCenters]);

  // Generate PLMXML
  const generate = useCallback(() => {
    const cfg = { prod, inc, ebom, mbom, bopData, equip, tools, skills, wp, qualityPlans, ewiDocs, bt };
    const result = generateCompletePLMXML(cfg);
    setXml(result.xml);
    setObjCount(result.count);
    setShowPreview(true);
    st(`✅ ${result.count} objects generated with all relations.`);
  }, [prod, inc, ebom, mbom, bopData, equip, tools, skills, wp, qualityPlans, ewiDocs, bt, st]);

  const download = useCallback(() => {
    if (!xml) return;
    saveAs(new Blob([xml], { type: 'application/xml;charset=utf-8' }), `${prod?.name || 'product'}.plmxml`);
    st('📥 PLMXML downloaded!');
  }, [xml, prod?.name, st]);

  const downloadCompletePackage = useCallback(async () => {
    if (!xml) {
      st('⚠️ Generate PLMXML first!', 'warning');
      return;
    }
    
    setIsZipping(true);
    setZipProgress(0);
    
    try {
      const progressInterval = setInterval(() => {
        setZipProgress(prev => Math.min(prev + 8, 90));
      }, 200);
      
      const cfg = { prod, inc, ebom, mbom, bopData, equip, tools, skills, wp, qualityPlans, ewiDocs, bt };
      const zipBlob = await createTeamcenterCompatiblePackage(cfg, xml, bmideTypes);
      
      clearInterval(progressInterval);
      setZipProgress(100);
      
      const baseName = (prod?.name || 'PRODUCT').replace(/[^a-zA-Z0-9]/g, '_');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      saveAs(zipBlob, `${baseName}_TC2512_Import_Package_${timestamp}.zip`);
      
      st(`✅ Teamcenter 2512 compatible ZIP downloaded! (${(zipBlob.size / 1024).toFixed(0)} KB)`);
    } catch (error) {
      console.error('ZIP creation error:', error);
      st(`❌ Error creating ZIP: ${error.message}`, 'error');
    } finally {
      setIsZipping(false);
      setZipProgress(0);
    }
  }, [xml, prod, inc, ebom, mbom, bopData, equip, tools, skills, wp, qualityPlans, ewiDocs, bt, bmideTypes, st]);

  // Apply Global Prefix
  const applyGlobalPrefix = useCallback(() => {
    if (!globalPrefix.trim()) return;
    const px = globalPrefix.trim();
    setProd(p => ({ ...p, name: px + (p?.name || ''), mbomName: px + (p?.mbomName || ''), bopName: px + (p?.bopName || '') }));
    setEbom(prev => prev.map(d => ({ ...d, name: px + (d.name || ''), id: uuidv4() })));
    setMbom(prev => prev.map(d => ({ ...d, name: px + (d.name || ''), id: uuidv4() })));
    setWp(p => ({ ...p, name: px + (p?.name || ''), id: px + (p?.id || '') }));
    
    if (bopData?.productBOP) {
      const newBOP = { ...bopData };
      newBOP.productBOP.name = px + (newBOP.productBOP.name || '');
      (newBOP.productBOP.processes || []).forEach(p => {
        p.name = px + (p.name || '');
        (p.plantBOPs || []).forEach(pb => {
          pb.name = px + (pb.name || '');
          (pb.functionalGroups || []).forEach(fg => {
            fg.name = px + (fg.name || '');
            (fg.operations || []).forEach(op => {
              op.name = px + (op.name || '');
              op.id = uuidv4();
            });
          });
        });
      });
      setBopData(newBOP);
    } else if (bopData?.functionalGroups) {
      const fgs = (bopData.functionalGroups || []).map(fg => ({
        ...fg,
        name: px + (fg.name || ''),
        operations: (fg.operations || []).map(op => ({ ...op, name: px + (op.name || ''), id: uuidv4() }))
      }));
      setBopData({ functionalGroups: fgs });
    }
    
    setPrefixApplied(true);
    setGlobalPrefix('');
    st(`✅ Prefix "${px}" applied globally!`);
  }, [globalPrefix, bopData, st]);

  // Auto Generate BOM
  const autoGenerateBOM = useCallback(() => {
    const levels = Math.max(1, Math.min(10, bomLevels));
    const rows = Math.max(1, Math.min(20, bomRowsPerLevel));
    const newEBOM = [];
    const newMBOM = [];
    const newOps = [];
    const newTools = [];
    const newSkills = [];
    const wcs = ['WC-CNC-01', 'WC-CNC-02', 'WC-ASM-01', 'WC-CMM-01', 'WC-HT-01'];
    for (let l = 1; l <= levels; l++) {
      for (let r = 1; r <= rows; r++) {
        const fn = String(l * 100 + r).padStart(4, '0');
        const name = `COMP-L${l}-R${r}`;
        newEBOM.push({
          id: uuidv4(),
          findNumber: fn,
          name,
          description: `Level ${l} Component ${r}`,
          quantity: l === 1 ? 1 : r,
          uom: 'EA',
          revision: 'A',
          makeBuy: l === 1 ? 'MAKE' : 'BUY'
        });
        newMBOM.push({
          id: uuidv4(),
          findNumber: fn,
          name: name + '-M',
          description: `Level ${l} Component ${r} Mfg`,
          quantity: l === 1 ? 1 : r,
          uom: 'EA',
          revision: 'A',
          makeBuy: l === 1 ? 'MAKE' : 'BUY',
          operationReference: String(l * 10)
        });
      }
      newOps.push({
        id: uuidv4(),
        sequence: String(l * 10),
        name: `OP-L${l}-${rows}`,
        description: `Operation Level ${l} (${rows} parts)`,
        workCenter: wcs[l % wcs.length],
        standardTime: 15 + l * 5,
        setupTime: 5,
        tools: l === 1 ? 'T-001,T-002' : l === levels ? 'T-006,T-008' : '',
        skills: l === 1 ? 'CNC-5AXIS' : l === levels ? 'Quality-Inspector' : 'Assembly-LEVEL2',
        controlKey: `PC-00${l}`,
        consumes: `RAW-${l}`,
        byproducts: `SCRAP-${l}`,
        parameter: `TEMP-${l*100}`,
        inspection: `VISUAL-${l}`,
        output: `OUT-${l}`,
        machine: `MACHINE-${l}`,
        tooling: `TOOLING-${l}`,
        sourceLocation: `LOC-${l}`,
        moveTime: l * 0.5
      });
    }
    newTools.push({
      id: uuidv4(),
      name: 'T-001',
      type: 'Fixture',
      description: 'Auto-generated chuck',
      quantity: levels,
      calibrationDue: '',
      manufacturer: 'AutoGen'
    });
    newSkills.push({
      id: uuidv4(),
      name: 'AutoSkill-' + levels,
      description: `Auto skill for ${levels} levels`,
      level: String(Math.min(levels, 3)),
      validityMonths: '24',
      certificationBody: 'AutoGen'
    });
    setEbom(newEBOM);
    setMbom(newMBOM);
    setBopData({
      productBOP: {
        name: 'PRODUCT-BOP-AUTO',
        description: `Auto-generated ${levels} levels × ${rows} rows`,
        revision: 'A',
        processes: [{
          name: 'Auto Process',
          description: 'Auto-generated process',
          type: 'ProcessBOP',
          sequence: '0010',
          plantBOPs: [{
            name: 'Auto Plant BOP',
            plant: 'JEDDAH',
            description: 'Auto-generated plant BOP',
            functionalGroups: [{
              name: 'FG-AutoGenerated',
              description: `Auto-generated ${levels} levels × ${rows} rows | ${newOps.length} operations`,
              sequence: '10',
              operations: newOps
            }]
          }]
        }]
      }
    });
    setTools(newTools);
    setSkills(newSkills);
    st(`✅ Auto-generated: ${levels} levels × ${rows} rows = ${newEBOM.length} EBOM + ${newMBOM.length} MBOM + ${newOps.length} Ops + Tools + Skills`);
  }, [bomLevels, bomRowsPerLevel, st]);

  // Handle Parsed Data
  const handleParsedData = useCallback((parsed) => {
    const summary = {
      totalRows: parsed.rowCount || (Array.isArray(parsed.data) ? parsed.data.length : 0),
      sheets: parsed.sheets?.length || 1,
      sheetDetails: {}
    };
    if (parsed.type === 'excel' || parsed.type === 'json') {
      Object.entries(parsed.data).forEach(([key, val]) => {
        if (Array.isArray(val)) summary.sheetDetails[key] = val.length;
      });
    } else if (parsed.type === 'csv') {
      summary.sheetDetails['csv'] = parsed.data.length;
    }
    if (parsed.type === 'bmide') {
      setBmideTypes(parsed.data);
      setBmideUploaded(true);
      st(`✅ BMIDE file parsed! ${parsed.data.length} types found.`);
      return;
    }
    setImportSummary(summary);
    setImportedData(parsed);
    setImportPreview(true);
    st(`✅ File parsed! ${summary.totalRows} rows.`);
  }, [st]);

  // Apply Imported Data
  const applyImportedData = useCallback(() => {
    if (!importedData) return;
    const data = importedData.data;
    let ac = 0;
    if (importedData.type === 'csv' && Array.isArray(data)) {
      if (data[0]?.findNumber || data[0]?.findNo) {
        setEbom(data.map(d => ({
          id: uuidv4(),
          findNumber: d.findNumber || d.findNo || '',
          name: d.name || '',
          description: d.description || d.desc || '',
          quantity: d.quantity || d.qty || 1,
          uom: d.uom || 'EA',
          revision: d.revision || d.rev || 'A',
          makeBuy: d.makeBuy || 'BUY'
        })));
        ac += data.length;
      } else if (data[0]?.sequence || data[0]?.seq) {
        setBopData({
          productBOP: {
            name: 'PRODUCT-BOP-IMPORTED',
            description: 'Imported BOP structure',
            revision: 'A',
            processes: [{
              name: 'Imported Process',
              description: 'Process from imported data',
              type: 'ProcessBOP',
              sequence: '0010',
              plantBOPs: [{
                name: 'Imported Plant BOP',
                plant: 'JEDDAH',
                description: 'Plant BOP from imported data',
                functionalGroups: [{
                  name: 'FG-Imported',
                  description: '',
                  sequence: '10',
                  operations: data.map(d => ({
                    id: uuidv4(),
                    sequence: d.sequence || d.seq || '',
                    name: d.name || '',
                    description: d.description || d.desc || '',
                    workCenter: d.workCenter || d.wc || '',
                    standardTime: d.standardTime || d.stdTime || 0,
                    setupTime: d.setupTime || d.setup || 0,
                    tools: d.tools || '',
                    skills: d.skills || '',
                    controlKey: d.controlKey || d.ctrl || '',
                    consumes: d.consumes || '',
                    byproducts: d.byproducts || '',
                    parameter: d.parameter || '',
                    inspection: d.inspection || '',
                    output: d.output || '',
                    queTime: d.queTime || 0,
                    machine: d.machine || '',
                    tooling: d.tooling || '',
                    sourceLocation: d.sourceLocation || '',
                    moveTime: d.moveTime || 0
                  }))
                }]
              }]
            }]
          }
        });
        ac += data.length;
      } else if (data[0]?.level) {
        setEquip(data.map(d => ({ ...d, id: uuidv4() })));
        ac += data.length;
      }
    } else if (typeof data === 'object') {
      if (data.ebom || data.EBOM) {
        const ed = data.ebom || data.EBOM;
        setEbom(ed.map(d => ({
          id: uuidv4(),
          findNumber: d.findNumber || d.findNo || '',
          name: d.name || '',
          description: d.description || d.desc || '',
          quantity: d.quantity || d.qty || 1,
          uom: d.uom || 'EA',
          revision: d.revision || d.rev || 'A',
          makeBuy: d.makeBuy || 'BUY'
        })));
        ac += ed.length;
      }
      if (data.mbom || data.MBOM) {
        const md = data.mbom || data.MBOM;
        setMbom(md.map(d => ({
          id: uuidv4(),
          findNumber: d.findNumber || d.findNo || '',
          name: d.name || '',
          description: d.description || d.desc || '',
          quantity: d.quantity || d.qty || 1,
          uom: d.uom || 'EA',
          revision: d.revision || d.rev || 'A',
          makeBuy: d.makeBuy || 'BUY',
          operationReference: d.operationReference || d.opRef || ''
        })));
        ac += md.length;
      }
      if (data.bop || data.BOP) {
        setBopData(data.bop || data.BOP);
        ac += extractAllOperations(data.bop || data.BOP).length;
      }
      if (data.boe || data.BOE) {
        setEquip((data.boe || data.BOE).map(d => ({ ...d, id: uuidv4() })));
        ac += (data.boe || data.BOE).length;
      }
      if (data.tools || data.Tools) {
        setTools((data.tools || data.Tools).map(d => ({ ...d, id: uuidv4() })));
        ac += (data.tools || data.Tools).length;
      }
      if (data.skills || data.Skills) {
        setSkills((data.skills || data.Skills).map(d => ({ ...d, id: uuidv4() })));
        ac += (data.skills || data.Skills).length;
      }
      if (data.product || data.Product) setProd({ ...prod, ...(data.product || data.Product) });
      if (data.workPackage || data.WorkPackage) setWp({ ...wp, ...(data.workPackage || data.WorkPackage) });
    }
    setImportHistory(prev => [{
      fileName: uploadedFileName,
      timestamp: new Date().toLocaleString(),
      rowsImported: ac,
      sheets: importedData.sheets?.length || 1
    }, ...prev].slice(0, 10));
    setImportPreview(false);
    setUploadedFileName('');
    setImportedData(null);
    st(`✅ Applied ${ac} rows!`);
  }, [importedData, uploadedFileName, prod, wp, st]);

  // Download Template
  const downloadTemplate = useCallback((type) => {
    const safeDefaultEBOM = Array.isArray(defaultEBOM) ? defaultEBOM : [];
    const safeDefaultMBOM = Array.isArray(defaultMBOM) ? defaultMBOM : [];
    const safeDefaultBOP = defaultBOP || { functionalGroups: [] };
    const safeDefaultBOE = Array.isArray(defaultBOE) ? defaultBOE : [];
    const safeDefaultTools = Array.isArray(defaultTools) ? defaultTools : [];
    const safeDefaultSkills = Array.isArray(defaultSkills) ? defaultSkills : [];
    
    if (type === 'full') {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeDefaultEBOM.map(d => ({
        'Find Number': d.findNumber || '',
        'Component Name': d.name || '',
        Description: d.description || '',
        Quantity: d.quantity || 1,
        UOM: d.uom || 'EA',
        Revision: d.revision || 'A',
        'Make/Buy': d.makeBuy || 'BUY'
      }))), 'EBOM');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeDefaultMBOM.map(d => ({
        'Find Number': d.findNumber || '',
        'Component Name': d.name || '',
        Description: d.description || '',
        Quantity: d.quantity || 1,
        UOM: d.uom || 'EA',
        Revision: d.revision || 'A',
        'Make/Buy': d.makeBuy || 'BUY',
        'Operation Ref': d.operationReference || ''
      }))), 'MBOM');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeDefaultBOP.functionalGroups.flatMap(fg => fg.operations.map(op => ({
        Sequence: op.sequence || op.seq || '',
        'Operation Name': op.name || '',
        Description: op.description || op.desc || '',
        'Work Center': op.workCenter || op.wc || '',
        'Std Time': op.standardTime || op.stdTime || 0,
        'Setup Time': op.setupTime || op.setup || 0,
        Tools: Array.isArray(op.tools) ? op.tools.join(',') : (op.tools || ''),
        Skills: Array.isArray(op.skills) ? op.skills.join(',') : (op.skills || ''),
        'Func Group': fg.name || '',
        Consumes: op.consumes || '',
        Byproducts: op.byproducts || '',
        Parameter: op.parameter || '',
        Inspection: op.inspection || '',
        Output: op.output || '',
        'Queue Time': op.queTime || 0,
        Machine: op.machine || '',
        Tooling: op.tooling || '',
        'Source Location': op.sourceLocation || '',
        'Move Time': op.moveTime || 0
      })))), 'BOP');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeDefaultBOE.map(d => ({
        Name: d.name || '',
        Level: d.level || 'WorkUnit',
        Description: d.description || d.desc || '',
        Parent: d.parent || '',
        Technology: d.technology || 'Conventional',
        'Cost Rate': d.costRate || 0,
        Capacity: d.capacity || 0
      }))), 'BOE');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeDefaultTools.map(d => ({
        Name: d.name || '',
        Type: d.type || '',
        Description: d.description || d.desc || '',
        Quantity: d.quantity || 1,
        'Cal Due': d.calibrationDue || '',
        Manufacturer: d.manufacturer || ''
      }))), 'Tools');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(safeDefaultSkills.map(d => ({
        Name: d.name || '',
        Description: d.description || d.desc || '',
        Level: d.level || 1,
        Validity: d.validityMonths || 24,
        'Cert Body': d.certificationBody || ''
      }))), 'Skills');
      XLSX.writeFile(wb, 'PLMXML_Complete_Template.xlsx');
      st('📥 Excel template downloaded!');
    } else {
      const templates = {
        ebom: {
          data: safeDefaultEBOM,
          cols: ['findNumber', 'name', 'description', 'quantity', 'uom', 'revision', 'makeBuy'],
          name: 'EBOM_Template.csv'
        },
        mbom: {
          data: safeDefaultMBOM,
          cols: ['findNumber', 'name', 'description', 'quantity', 'uom', 'revision', 'makeBuy', 'operationReference'],
          name: 'MBOM_Template.csv'
        },
        bop: {
          data: safeDefaultBOP.functionalGroups.flatMap(fg => fg.operations.map(op => ({
            ...op,
            functionalGroup: fg.name || '',
            tools: Array.isArray(op.tools) ? op.tools.join(',') : (op.tools || ''),
            skills: Array.isArray(op.skills) ? op.skills.join(',') : (op.skills || '')
          }))),
          cols: ['sequence', 'name', 'description', 'workCenter', 'standardTime', 'setupTime', 'tools', 'skills', 'controlKey', 'functionalGroup', 'consumes', 'byproducts', 'parameter', 'inspection', 'output', 'queTime', 'machine', 'tooling', 'sourceLocation', 'moveTime'],
          name: 'BOP_Template.csv'
        },
        boe: {
          data: safeDefaultBOE,
          cols: ['name', 'level', 'description', 'parent', 'technology', 'costRate', 'capacity'],
          name: 'BOE_Template.csv'
        },
        tools: {
          data: safeDefaultTools,
          cols: ['name', 'type', 'description', 'quantity', 'calibrationDue', 'manufacturer'],
          name: 'Tools_Template.csv'
        },
        skills: {
          data: safeDefaultSkills,
          cols: ['name', 'description', 'level', 'validityMonths', 'certificationBody'],
          name: 'Skills_Template.csv'
        }
      };
      const t = templates[type];
      if (t) {
        const csv = Papa.unparse(t.data.map(d => {
          const row = {};
          t.cols.forEach(c => { row[c] = d[c] || ''; });
          return row;
        }));
        saveAs(new Blob([csv], { type: 'text/csv' }), t.name);
        st(`📥 ${type.toUpperCase()} template downloaded!`);
      }
    }
  }, [st]);

  // SAVE/LOAD STATE FUNCTIONS
  const saveStateToFile = useCallback(() => {
    const state = {
      version: '15.0',
      timestamp: new Date().toISOString(),
      product: prod,
      workPackage: wp,
      ebom: ebom,
      mbom: mbom,
      bopData: bopData,
      equip: equip,
      tools: tools,
      skills: skills,
      qualityPlans: qualityPlans,
      ewiDocs: ewiDocs,
      plants: plants,
      includes: inc,
      bmideTypes: bt,
      bomLevels: bomLevels,
      bomRowsPerLevel: bomRowsPerLevel
    };
    
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `PLMXML_Studio_State_${new Date().toISOString().slice(0,19).replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    st('✅ State saved successfully!');
  }, [prod, wp, ebom, mbom, bopData, equip, tools, skills, qualityPlans, ewiDocs, plants, inc, bt, bomLevels, bomRowsPerLevel, st]);

  const loadStateFromFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const state = JSON.parse(e.target.result);
        
        if (!state.version) {
          st('❌ Invalid state file format', 'error');
          return;
        }
        
        if (state.product) setProd(state.product);
        if (state.workPackage) setWp(state.workPackage);
        if (state.ebom) setEbom(state.ebom);
        if (state.mbom) setMbom(state.mbom);
        if (state.bopData) setBopData(state.bopData);
        if (state.equip) setEquip(state.equip);
        if (state.tools) setTools(state.tools);
        if (state.skills) setSkills(state.skills);
        if (state.qualityPlans) setQualityPlans(state.qualityPlans);
        if (state.ewiDocs) setEwiDocs(state.ewiDocs);
        if (state.plants) setPlants(state.plants);
        if (state.includes) setInc(state.includes);
        if (state.bmideTypes) setBt(state.bmideTypes);
        if (state.bomLevels !== undefined) setBomLevels(state.bomLevels);
        if (state.bomRowsPerLevel !== undefined) setBomRowsPerLevel(state.bomRowsPerLevel);
        
        st(`✅ State loaded successfully! (v${state.version})`);
      } catch (err) {
        st(`❌ Error loading state: ${err.message}`, 'error');
      }
    };
    reader.readAsText(file);
  }, [st]);

  // Handle Product Data Generated
  const handleProductDataGenerated = useCallback((data) => {
    setGeneratedProductData(data);
    setShowProductGenerator(false);
    
    if (data) {
      setProd(data.product || defaultProduct);
      setWp(data.workPackage || defaultWorkPackage);
      setEbom(data.ebom || defaultEBOM);
      setMbom(data.mbom || defaultMBOM);
      setBopData(data.bopData || defaultBOP);
      setEquip(data.equip || defaultBOE);
      setTools(data.tools || defaultTools);
      setSkills(data.skills || defaultSkills);
      setQualityPlans(data.qualityPlans || defaultQualityPlans);
      setEwiDocs(data.ewiDocs || defaultEWI);
      if (data.includes) setInc(data.includes);
      if (data.bt) setBt(data.bt);
      
      st('✅ Product data loaded successfully!');
    }
  }, [st]);

  // Column Definitions
  const ebomCols = [
    { key: 'findNumber', label: 'Find#', editable: true, width: '42px' },
    { key: 'name', label: 'Name', editable: true, width: '85px' },
    { key: 'description', label: 'Desc', editable: true },
    { key: 'quantity', label: 'Qty', editable: true, type: 'number', width: '40px' },
    { key: 'uom', label: 'UOM', editable: true, type: 'select', options: ['EA', 'KIT', 'SET', 'KG', 'L'], width: '45px' },
    { key: 'revision', label: 'Rev', editable: true, width: '32px' },
    { key: 'makeBuy', label: 'M/B', editable: true, type: 'select', options: ['MAKE', 'BUY', 'RAW', 'CONSUMABLE'], width: '75px' }
  ];
  const mbomCols = [...ebomCols, { key: 'operationReference', label: 'Op', editable: true, width: '38px' }];
  
  const opCols = [
    { key: 'sequence', label: '#', editable: true, width: '32px' },
    { key: 'name', label: 'Name', editable: true, width: '90px' },
    { key: 'description', label: 'Desc', editable: true },
    { key: 'workCenter', label: 'WC', editable: true, width: '68px' },
    { key: 'standardTime', label: 'T', editable: true, type: 'number', width: '38px' },
    { key: 'setupTime', label: 'Su', editable: true, type: 'number', width: '38px' },
    { key: 'tools', label: 'Tools', editable: true, width: '75px' },
    { key: 'skills', label: 'Skills', editable: true, width: '75px' },
    { key: 'controlKey', label: 'Ctrl', editable: true, width: '38px' },
    { key: 'machine', label: 'Machine', editable: true, width: '80px' },
    { key: 'tooling', label: 'Tooling', editable: true, width: '80px' },
    { key: 'parameter', label: 'Parameter', editable: true, width: '80px' },
    { key: 'consumes', label: 'Consumes', editable: true, width: '75px' },
    { key: 'indirectConsumables', label: 'Indirect Cons', editable: true, width: '75px' },
    { key: 'byproducts', label: 'Byproducts', editable: true, width: '75px' },
    { key: 'setupTimeDetail', label: 'Setup Detail', editable: true, width: '80px' },
    { key: 'machineRunTime', label: 'Machine Run', editable: true, width: '75px' },
    { key: 'directLaborTime', label: 'Labor Time', editable: true, width: '75px' },
    { key: 'skillRequired', label: 'Skill Req', editable: true, width: '80px' },
    { key: 'workInstruction', label: 'WI', editable: true, width: '80px' },
    { key: 'inspection', label: 'Inspection', editable: true, width: '80px' },
    { key: 'sourceLocation', label: 'Source Loc', editable: true, width: '80px' },
    { key: 'output', label: 'Output', editable: true, width: '75px' },
    { key: 'queTime', label: 'Queue', editable: true, type: 'number', width: '38px' },
    { key: 'moveTime', label: 'Move Time', editable: true, type: 'number', width: '38px' },
    { key: 'precedes', label: 'Precedes', editable: true, width: '50px' },
    { key: 'mbomRef', label: 'MBOM Ref', editable: true, width: '50px' },
    { key: 'ebomRef', label: 'EBOM Ref', editable: true, width: '50px' },
    { key: 'qualityPlanRef', label: 'Quality Ref', editable: true, width: '60px' },
    { key: 'ewiRef', label: 'EWI Ref', editable: true, width: '60px' },
    { key: 'operationType', label: 'Op Type', editable: true, width: '60px' },
    { key: 'category', label: 'Category', editable: true, width: '60px' },
    { key: 'subCategory', label: 'Sub Category', editable: true, width: '60px' },
    { key: 'scrap', label: 'Scrap', editable: true, width: '75px' },
    { key: 'directMBOM', label: 'Direct MBOM', editable: true, width: '75px' }
  ];
  const equipCols = [
    { key: 'name', label: 'Name', editable: true, width: '80px' },
    { key: 'level', label: 'Level', editable: true, type: 'select', options: ['Enterprise', 'Site', 'Area', 'ProductionLine', 'ProcessCell', 'WorkUnit', 'StorageZone'], width: '80px' },
    { key: 'description', label: 'Desc', editable: true },
    { key: 'parent', label: 'Parent', editable: true, width: '70px' },
    { key: 'technology', label: 'Tech', editable: true, type: 'select', options: ['Conventional', 'Casting', 'CNC', '3D Printing', 'Robotics'], width: '70px' },
    { key: 'costRate', label: '$/h', editable: true, type: 'number', width: '42px' },
    { key: 'capacity', label: 'Hrs', editable: true, type: 'number', width: '38px' }
  ];
  const toolCols = [
    { key: 'name', label: 'Name', editable: true, width: '60px' },
    { key: 'type', label: 'Type', editable: true, type: 'select', options: ['Fixture', 'Cutting Tool', 'Measuring Device', 'Tooling', 'Consumable'], width: '85px' },
    { key: 'description', label: 'Desc', editable: true },
    { key: 'quantity', label: 'Qty', editable: true, type: 'number', width: '38px' },
    { key: 'calibrationDue', label: 'Cal', editable: true, width: '65px' },
    { key: 'manufacturer', label: 'Mfr', editable: true, width: '65px' }
  ];
  const skillCols = [
    { key: 'name', label: 'Name', editable: true, width: '90px' },
    { key: 'description', label: 'Desc', editable: true },
    { key: 'level', label: 'Lv', editable: true, type: 'number', width: '32px' },
    { key: 'validityMonths', label: 'Mo', editable: true, width: '38px' },
    { key: 'certificationBody', label: 'Cert', editable: true, width: '80px' }
  ];

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={12} /> },
    { id: 'product', label: 'Product & WP', icon: <Package size={12} /> },
    { id: 'bom', label: 'BOM', icon: <GitBranch size={12} /> },
    { id: 'bop', label: 'BOP', icon: <Wrench size={12} /> },
    { id: 'boe', label: 'BOE', icon: <Cpu size={12} /> },
    { id: 'tools', label: 'Tools', icon: <Box size={12} /> },
    { id: 'skills', label: 'Skills', icon: <Users size={12} /> },
    { id: 'quality', label: 'Quality', icon: <Target size={12} /> },
    { id: 'ewi', label: 'EWI', icon: <FileText size={12} /> },
    { id: 'import', label: 'Import/Export', icon: <Upload size={12} /> },
    { id: 'integrated', label: 'Integrated Dashboard', icon: <LayoutDashboard size={12} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={12} /> },
    { id: 'productGenerator', label: 'Product Generator', icon: <Sparkles size={12} /> },
    { id: 'digitalGuardrails', label: 'Digital GuardRails', icon: <Shield size={12} /> },
   { id: 'guardrails', label: 'GuardRails', icon: <Shield size={12} /> },
  ];

  return (
    <div style={S.container}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      <div style={S.header}>
        <div>
          <div style={S.hTitle}>🏭 PLMXML Studio v15.0 Final</div>
          <div style={{ fontSize: '0.56rem', color: T.t3 }}>
            Teamcenter 2512 • Journey Tracking • Interactive Visualizations • TC Compatible ZIP
          </div>
        </div>
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={S.badge(T.p)}>v15.0</span>
          <button onClick={generate} style={{ ...S.btn, ...S.bPri }}><Zap size={10} />Generate</button>
          
          {xml && (
            <>
              <button
                onClick={downloadCompletePackage}
                style={{ ...S.btn, ...S.bPP }}
                disabled={isZipping}
                title="Download Teamcenter 2512 Compatible ZIP Package"
              >
                {isZipping ? (
                  <>
                    <RefreshCw size={10} className="spin" />
                    {zipProgress}%
                  </>
                ) : (
                  <>
                    <FolderArchive size={10} /> TC Package
                  </>
                )}
              </button>
              {isZipping && (
                <div style={{ width: '80px', ...S.progressBar }}>
                  <div style={{ ...S.progressFill, width: `${zipProgress}%`, background: T.pp }} />
                </div>
              )}
              <button onClick={download} style={{ ...S.btn, ...S.bSuc }}><FileCode size={10} />PLMXML</button>
              <button
                onClick={() => {
                  if (xml) setShowValidator(true);
                  else st('⚠️ Generate PLMXML first!', 'warning');
                }}
                style={{ ...S.btn, ...S.bTeal }}
                disabled={!xml}
              >
                <Shield size={10} /> Validate
              </button>
              <button onClick={() => { navigator.clipboard.writeText(xml); st('📋 Copied!'); }}
                style={{ ...S.btn, ...S.bGho }}>
                <Copy size={10} />
              </button>
            </>
          )}
          
          <button onClick={saveStateToFile} style={{ ...S.btn, ...S.bTeal }}>
            <Save size={10} /> Save State
          </button>
          <button onClick={() => loadFileRef.current?.click()} style={{ ...S.btn, ...S.bWar }}>
            <Upload size={10} /> Load State
          </button>
          <input 
            ref={loadFileRef} 
            type="file" 
            accept=".json" 
            style={{ display: 'none' }}
            onChange={e => {
              if (e.target.files?.[0]) {
                loadStateFromFile(e.target.files[0]);
                e.target.value = '';
              }
            }} 
          />
          
          <button 
            onClick={() => setShowReport(true)} 
            style={{ ...S.btn, ...S.bPP }}
          >
            <FileText size={10} /> Report
          </button>
        </div>
      </div>

      <div style={S.main}>
        <div style={S.side}>
          {sidebarItems.map(item => (
            <div key={item.id} style={S.sItem(tab === item.id)} onClick={() => setTab(item.id)}>
              {item.icon}<span>{item.label}</span>
            </div>
          ))}
        </div>
        <div style={S.content}>
          {/* DASHBOARD */}
          {tab === 'dashboard' && (
            <div>
              <div style={{ ...S.card, border: `1px solid ${T.pp}44`, background: `${T.pp}05` }}>
                <div style={S.cHdr}>
                  <div style={S.cTtl}><Edit3 size={12} color={T.ppl} />Global Prefix</div>
                  {prefixApplied && <span style={{ ...S.badge(T.g), padding: '2px 6px' }}>✅ Applied</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    style={{ ...S.input, flex: 1 }}
                    placeholder="Enter prefix (e.g., MYPROJECT_)"
                    value={globalPrefix}
                    onChange={e => setGlobalPrefix(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') applyGlobalPrefix(); }}
                  />
                  <button onClick={applyGlobalPrefix} style={{ ...S.btn, ...S.bPP }}>
                    <Wand2 size={10} />Apply Prefix
                  </button>
                </div>
              </div>
              <div style={{ ...S.card, marginTop: '6px', border: `1px solid ${T.pp}33`, background: `${T.pp}05` }}>
                <div style={S.cHdr}>
                  <div style={S.cTtl}><Sparkles size={12} color={T.ppl} />Auto-Generate BOM</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'end', flexWrap: 'wrap' }}>
                  <div>
                    <div style={S.label}>Levels</div>
                    <input
                      type="number"
                      style={{ ...S.input, width: '60px' }}
                      value={bomLevels}
                      onChange={e => setBomLevels(parseInt(e.target.value) || 1)}
                      min={1}
                      max={10}
                    />
                  </div>
                  <div>
                    <div style={S.label}>Rows/Level</div>
                    <input
                      type="number"
                      style={{ ...S.input, width: '60px' }}
                      value={bomRowsPerLevel}
                      onChange={e => setBomRowsPerLevel(parseInt(e.target.value) || 1)}
                      min={1}
                      max={20}
                    />
                  </div>
                  <button onClick={autoGenerateBOM} style={{ ...S.btn, ...S.bPP }}>
                    <Wand2 size={10} />Generate BOM
                  </button>
                  <span style={{ fontSize: '0.6rem', color: T.t3 }}>
                    Creates {bomLevels}×{bomRowsPerLevel} items
                  </span>
                </div>
              </div>
              <div style={{ ...S.grid(6), marginTop: '6px' }}>
                {Object.entries(stats).map(([k, v]) => (
                  <div key={k} style={S.statCard}>
                    <div style={S.statVal}>{v}</div>
                    <div style={S.statLbl}>{k.replace(/([A-Z])/g, ' $1').trim()}</div>
                  </div>
                ))}
              </div>
              <div style={{ ...S.card, marginTop: '6px' }}>
                <div style={S.cHdr}>
                  <div style={S.cTtl}><Factory size={12} color={T.wl} />Equipment by Technology</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(techGroups).map(([tech, eqs]) => eqs.length > 0 && (
                    <div key={tech} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '3px 8px',
                      background: T.s,
                      borderRadius: '4px',
                      border: `1px solid ${T[tech] || T.t3}22`
                    }}>
                      <span style={{ fontWeight: 600, fontSize: '0.72rem', color: T[tech] || T.t3 }}>{tech}</span>
                      <span style={S.badge(T[tech] || T.t3)}>{eqs.length}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Dendrogram */}
              <div style={{ ...S.card, marginTop: '6px' }}>
                <div style={S.cHdr}>
                  <div style={S.cTtl}><GitMerge size={14} color={T.ppl} /><span>Work Package Digital Thread</span></div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button onClick={() => setDendroZoom(d => Math.max(0.4, d - 0.1))}
                      style={{ ...S.btn, ...S.bGho, padding: '2px 5px', fontSize: '0.58rem' }}>
                      <ZoomOut size={10} />
                    </button>
                    <span style={{ fontSize: '0.58rem', color: T.t3, minWidth: '35px', textAlign: 'center' }}>
                      {Math.round(dendroZoom * 100)}%
                    </span>
                    <button onClick={() => setDendroZoom(d => Math.min(2.5, d + 0.1))}
                      style={{ ...S.btn, ...S.bGho, padding: '2px 5px', fontSize: '0.58rem' }}>
                      <ZoomIn size={10} />
                    </button>
                    <button onClick={() => setDendroExpanded(prev => !prev)}
                      style={{ ...S.btn, ...S.bGho, padding: '2px 6px', fontSize: '0.58rem' }}>
                      {dendroExpanded ? <Minimize2 size={10} /> : <Maximize2 size={10} />}
                      {dendroExpanded ? ' Collapse' : ' Expand'}
                    </button>
                  </div>
                </div>
                <div style={{
                  background: T.s,
                  borderRadius: '6px',
                  padding: '8px',
                  maxHeight: dendroExpanded ? 'none' : '450px',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.66rem',
                  lineHeight: '1.5',
                  transform: `scale(${dendroZoom})`,
                  transformOrigin: 'top left',
                  transition: 'transform 0.2s',
                  minWidth: '950px'
                }}>
                  <DendroNode id="wp-root" label={`📋 WORK PACKAGE: ${wp?.name || 'WP-GTC-001'}`}
                    color={T.g} details={`Plant: ${wp?.plant || 'JEDDAH'}`}
                    defaultExpanded={true} depth={0}>
                    
                    <DendroNode id="wp-ebom-link" label="🔗 ImanRelation: WorkPackageEBOM → EBOM"
                      color={T.g} depth={1} defaultExpanded={true}>
                      <DendroNode id="ebom-branch"
                        label={`📐 EBOM: ${prod?.name || 'GAS-TURBINE-COMPRESSOR-001'} (${Array.isArray(ebom) ? ebom.length : 0} comps)`}
                        color={T.cy} depth={2} defaultExpanded={true}>
                        <DendroNode id="ebom-prod" label={`📦 Product → Revision → Def → Graph → View`}
                          color={T.cy} depth={3} defaultExpanded={false}>
                          {(Array.isArray(ebom) ? ebom : []).slice(0, 15).map((item, i) => (
                            <DendroNode key={`ebom-${item.id}`} id={`ebom-item-${i}`}
                              label={`🔩 ${item.findNumber || '--'}: ${item.name || 'Unnamed'}`}
                              color={T.cy} depth={4} defaultExpanded={false} />
                          ))}
                          {(Array.isArray(ebom) ? ebom.length : 0) > 15 && (
                            <DendroNode id="ebom-more"
                              label={`... and ${(Array.isArray(ebom) ? ebom.length : 0) - 15} more`}
                              color={T.cy} depth={4} defaultExpanded={false} />
                          )}
                        </DendroNode>
                      </DendroNode>
                    </DendroNode>

                    <DendroNode id="wp-mbom-link" label="🔗 ImanRelation: WorkPackageMBOM → MBOM"
                      color={T.g} depth={1} defaultExpanded={true}>
                      <DendroNode id="mbom-branch"
                        label={`📦 MBOM: ${prod?.mbomName || 'GAS-TURBINE-COMPRESSOR-001-M'} (${Array.isArray(mbom) ? mbom.length : 0} comps)`}
                        color={T.pk} depth={2} defaultExpanded={true}>
                        <DendroNode id="mbom-prod" label={`📦 MESPart → Revision → Def → Graph → View`}
                          color={T.pk} depth={3} defaultExpanded={false}>
                          {(Array.isArray(mbom) ? mbom : []).slice(0, 15).map((item, i) => (
                            <DendroNode key={`mbom-${item.id}`} id={`mbom-item-${i}`}
                              label={`📦 ${item.findNumber || '--'}: ${item.name || 'Unnamed'}`}
                              color={T.pk} depth={4} defaultExpanded={false} />
                          ))}
                          {(Array.isArray(mbom) ? mbom.length : 0) > 15 && (
                            <DendroNode id="mbom-more"
                              label={`... and ${(Array.isArray(mbom) ? mbom.length : 0) - 15} more`}
                              color={T.pk} depth={4} defaultExpanded={false} />
                          )}
                        </DendroNode>
                      </DendroNode>
                    </DendroNode>

                    <DendroNode id="wp-bop-link" label="🔗 ImanRelation: WorkPackageBOP → BOP"
                      color={T.g} depth={1} defaultExpanded={true}>
                      <DendroNode id="bop-branch" label={`⚙️ BOP: ${prod?.bopName || 'BOP-GAS-TURBINE-COMPRESSOR-001'} (${allOps.length} ops, ${functionalGroups.length} FGs)`}
                        color={T.w} depth={2} defaultExpanded={true}>
                        
                        {(bopData?.productBOP?.processes || []).length > 0 ? (
                          (bopData?.productBOP?.processes || []).map((process, pi) => (
                            <DendroNode key={`process-${pi}`} id={`process-${pi}`}
                              label={`🔄 PROCESS: ${process.name || 'Unnamed'} (${process.plantBOPs?.length || 0} plants)`}
                              color={T.w} depth={3} defaultExpanded={false}>
                              {(process.plantBOPs || []).map((plantBOP, pbi) => (
                                <DendroNode key={`plant-${pi}-${pbi}`} id={`plant-${pi}-${pbi}`}
                                  label={`🏭 PLANT: ${plantBOP.name || 'Unnamed'} (${plantBOP.functionalGroups?.length || 0} FGs)`}
                                  color={T.g} depth={4} defaultExpanded={false}>
                                  {(plantBOP.functionalGroups || []).map((fg, fgi) => (
                                    <DendroNode key={`fg-${pi}-${pbi}-${fgi}`} id={`fg-${pi}-${pbi}-${fgi}`}
                                      label={`📁 FG: ${fg.name || 'Unnamed'} [${(fg.operations || []).length} ops]`}
                                      color={T.pp} depth={5} defaultExpanded={false}>
                                      {(fg.operations || []).map((op, oi) => {
                                        const hasChildren = hasOperationChildren(op);
                                        const details = `WC: ${op.workCenter || op.wc || 'N/A'} | T: ${op.standardTime || op.stdTime || 0}m${op.tools ? ` | Tools: ${op.tools}` : ''}${op.skills ? ` | Skills: ${op.skills}` : ''}${op.consumes ? ` | Consumes: ${op.consumes}` : ''}${op.machine ? ` | Machine: ${op.machine}` : ''}`;
                                        
                                        return (
                                          <DendroNode key={`op-${pi}-${pbi}-${fgi}-${oi}`} id={`op-${pi}-${pbi}-${fgi}-${oi}`}
                                            label={`🔧 ${op.sequence || op.seq || 'N/A'}: ${op.name || 'Unnamed'}`}
                                            color={T.or} depth={6} defaultExpanded={false}
                                            details={details}>
                                            
                                            {(op.machine || op.tools || op.parameter) && (
                                              <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-resources`}
                                                label="🏭 RESOURCES" color={T.cy} depth={7} defaultExpanded={false}>
                                                {op.machine && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-machine`}
                                                    label={`Machine: ${op.machine}`} color={T.cy} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.tooling && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-tooling`}
                                                    label={`Tooling: ${op.tooling}`} color={T.cy} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.parameter && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-parameter`}
                                                    label={`Parameter: ${op.parameter}`} color={T.cy} depth={8} defaultExpanded={false} />
                                                )}
                                              </DendroNode>
                                            )}

                                            {(op.consumes || op.indirectConsumables || op.byproducts) && (
                                              <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-materials`}
                                                label="📦 MATERIALS" color={T.g} depth={7} defaultExpanded={false}>
                                                {op.consumes && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-consumes`}
                                                    label={`Consumes: ${op.consumes}`} color={T.g} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.indirectConsumables && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-indirect`}
                                                    label={`Consumables (Indirect): ${op.indirectConsumables}`} color={T.g} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.byproducts && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-byproducts`}
                                                    label={`By-Product/Scrap: ${op.byproducts}`} color={T.g} depth={8} defaultExpanded={false} />
                                                )}
                                              </DendroNode>
                                            )}

                                            {(op.skills || op.setupTimeDetail || op.machineRunTime || op.directLaborTime) && (
                                              <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-labor`}
                                                label="👥 LABOR" color={T.w} depth={7} defaultExpanded={false}>
                                                {op.skills && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-skills`}
                                                    label={`Skill Required: ${op.skills}`} color={T.w} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.setupTimeDetail && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-setup`}
                                                    label={`Setup Time: ${op.setupTimeDetail}`} color={T.w} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.machineRunTime && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-machineTime`}
                                                    label={`Machine Run Time: ${op.machineRunTime}`} color={T.w} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.directLaborTime && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-laborTime`}
                                                    label={`Direct Labor Time: ${op.directLaborTime}`} color={T.w} depth={8} defaultExpanded={false} />
                                                )}
                                              </DendroNode>
                                            )}

                                            {(op.workInstruction || op.inspection) && (
                                              <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-data`}
                                                label="📄 DATA & QUALITY" color={T.pp} depth={7} defaultExpanded={false}>
                                                {op.workInstruction && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-wi`}
                                                    label={`Work Instruction: ${op.workInstruction}`} color={T.pp} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.inspection && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-inspection`}
                                                    label={`Inspection: ${op.inspection}`} color={T.pp} depth={8} defaultExpanded={false} />
                                                )}
                                              </DendroNode>
                                            )}

                                            {(op.sourceLocation || op.output || op.queTime || op.moveTime || op.byproducts) && (
                                              <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-logistics`}
                                                label="🔗 LOGISTICS" color={T.te} depth={7} defaultExpanded={false}>
                                                {op.sourceLocation && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-source`}
                                                    label={`Source Location: ${op.sourceLocation}`} color={T.te} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.output && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-output`}
                                                    label={`Produces (Output): ${op.output}`} color={T.te} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.moveTime !== undefined && op.moveTime > 0 && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-move`}
                                                    label={`Move Time: ${op.moveTime}m`} color={T.te} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.queTime !== undefined && op.queTime > 0 && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-queue`}
                                                    label={`Queue Time: ${op.queTime}m`} color={T.te} depth={8} defaultExpanded={false} />
                                                )}
                                                {op.byproducts && !op.consumes && (
                                                  <DendroNode id={`op-${pi}-${pbi}-${fgi}-${oi}-byproducts2`}
                                                    label={`By-Product/Scrap: ${op.byproducts}`} color={T.te} depth={8} defaultExpanded={false} />
                                                )}
                                              </DendroNode>
                                            )}
                                          </DendroNode>
                                        );
                                      })}
                                      {((fg.operations || []).length > 10) && (
                                        <DendroNode id={`fg-${pi}-${pbi}-${fgi}-more`}
                                          label={`... and ${(fg.operations || []).length - 10} more operations`}
                                          color={T.t3} depth={6} defaultExpanded={false} />
                                      )}
                                    </DendroNode>
                                  ))}
                                </DendroNode>
                              ))}
                            </DendroNode>
                          ))
                        ) : (
                          functionalGroups.slice(0, 10).map((fg, fi) => (
                            <DendroNode key={`fg-${fi}`} id={`fg-branch-${fi}`}
                              label={`📁 FG: ${fg.name || 'Unnamed'} [${(fg.operations || []).length} ops]`}
                              color={T.w} depth={3} defaultExpanded={false}>
                              {(fg.operations || []).slice(0, 10).map((op, oi) => {
                                const hasChildren = hasOperationChildren(op);
                                const details = `WC: ${op.workCenter || op.wc || 'N/A'} | T: ${op.standardTime || op.stdTime || 0}m${op.tools ? ` | Tools: ${op.tools}` : ''}${op.skills ? ` | Skills: ${op.skills}` : ''}`;
                                
                                return (
                                  <DendroNode key={`op-${fi}-${oi}`} id={`op-item-${fi}-${oi}`}
                                    label={`🔧 ${op.sequence || op.seq || 'N/A'}: ${op.name || 'Unnamed'}`}
                                    color={T.or} depth={4} defaultExpanded={false}
                                    details={details}>
                                    
                                    {(op.machine || op.tools || op.parameter) && (
                                      <DendroNode id={`op-${fi}-${oi}-resources`}
                                        label="🏭 RESOURCES" color={T.cy} depth={5} defaultExpanded={false}>
                                        {op.machine && <DendroNode id={`op-${fi}-${oi}-machine`} label={`Machine: ${op.machine}`} color={T.cy} depth={6} defaultExpanded={false} />}
                                        {op.tooling && <DendroNode id={`op-${fi}-${oi}-tooling`} label={`Tooling: ${op.tooling}`} color={T.cy} depth={6} defaultExpanded={false} />}
                                        {op.parameter && <DendroNode id={`op-${fi}-${oi}-parameter`} label={`Parameter: ${op.parameter}`} color={T.cy} depth={6} defaultExpanded={false} />}
                                      </DendroNode>
                                    )}
                                    {(op.consumes || op.indirectConsumables || op.byproducts) && (
                                      <DendroNode id={`op-${fi}-${oi}-materials`}
                                        label="📦 MATERIALS" color={T.g} depth={5} defaultExpanded={false}>
                                        {op.consumes && <DendroNode id={`op-${fi}-${oi}-consumes`} label={`Consumes: ${op.consumes}`} color={T.g} depth={6} defaultExpanded={false} />}
                                        {op.indirectConsumables && <DendroNode id={`op-${fi}-${oi}-indirect`} label={`Indirect Consumables: ${op.indirectConsumables}`} color={T.g} depth={6} defaultExpanded={false} />}
                                        {op.byproducts && <DendroNode id={`op-${fi}-${oi}-byproducts`} label={`By-Product: ${op.byproducts}`} color={T.g} depth={6} defaultExpanded={false} />}
                                      </DendroNode>
                                    )}
                                    {(op.skills || op.setupTimeDetail || op.machineRunTime || op.directLaborTime) && (
                                      <DendroNode id={`op-${fi}-${oi}-labor`}
                                        label="👥 LABOR" color={T.w} depth={5} defaultExpanded={false}>
                                        {op.skills && <DendroNode id={`op-${fi}-${oi}-skills`} label={`Skills: ${op.skills}`} color={T.w} depth={6} defaultExpanded={false} />}
                                        {op.setupTimeDetail && <DendroNode id={`op-${fi}-${oi}-setup`} label={`Setup: ${op.setupTimeDetail}`} color={T.w} depth={6} defaultExpanded={false} />}
                                        {op.machineRunTime && <DendroNode id={`op-${fi}-${oi}-machineTime`} label={`Machine Run: ${op.machineRunTime}`} color={T.w} depth={6} defaultExpanded={false} />}
                                        {op.directLaborTime && <DendroNode id={`op-${fi}-${oi}-laborTime`} label={`Direct Labor: ${op.directLaborTime}`} color={T.w} depth={6} defaultExpanded={false} />}
                                      </DendroNode>
                                    )}
                                    {(op.workInstruction || op.inspection) && (
                                      <DendroNode id={`op-${fi}-${oi}-data`}
                                        label="📄 DATA & QUALITY" color={T.pp} depth={5} defaultExpanded={false}>
                                        {op.workInstruction && <DendroNode id={`op-${fi}-${oi}-wi`} label={`WI: ${op.workInstruction}`} color={T.pp} depth={6} defaultExpanded={false} />}
                                        {op.inspection && <DendroNode id={`op-${fi}-${oi}-inspection`} label={`Inspection: ${op.inspection}`} color={T.pp} depth={6} defaultExpanded={false} />}
                                      </DendroNode>
                                    )}
                                    {(op.sourceLocation || op.output || op.queTime || op.moveTime) && (
                                      <DendroNode id={`op-${fi}-${oi}-logistics`}
                                        label="🔗 LOGISTICS" color={T.te} depth={5} defaultExpanded={false}>
                                        {op.sourceLocation && <DendroNode id={`op-${fi}-${oi}-source`} label={`Source: ${op.sourceLocation}`} color={T.te} depth={6} defaultExpanded={false} />}
                                        {op.output && <DendroNode id={`op-${fi}-${oi}-output`} label={`Output: ${op.output}`} color={T.te} depth={6} defaultExpanded={false} />}
                                        {op.moveTime !== undefined && op.moveTime > 0 && <DendroNode id={`op-${fi}-${oi}-move`} label={`Move: ${op.moveTime}m`} color={T.te} depth={6} defaultExpanded={false} />}
                                        {op.queTime !== undefined && op.queTime > 0 && <DendroNode id={`op-${fi}-${oi}-queue`} label={`Queue: ${op.queTime}m`} color={T.te} depth={6} defaultExpanded={false} />}
                                      </DendroNode>
                                    )}
                                  </DendroNode>
                                );
                              })}
                              {((fg.operations || []).length > 10) && (
                                <DendroNode id={`fg-${fi}-more`}
                                  label={`... and ${(fg.operations || []).length - 10} more operations`}
                                  color={T.t3} depth={4} defaultExpanded={false} />
                              )}
                            </DendroNode>
                          ))
                        )}
                        {(functionalGroups.length > 10) && (
                          <DendroNode id="fg-more"
                            label={`... and ${functionalGroups.length - 10} more Functional Groups`}
                            color={T.t3} depth={3} defaultExpanded={false} />
                        )}
                      </DendroNode>
                    </DendroNode>

                    <DendroNode id="wp-boe" label={`🏢 BOE: ${Array.isArray(equip) ? equip.length : 0} equipment nodes`}
                      color={T.pp} depth={1} defaultExpanded={false}>
                      {Object.entries(techGroups).map(([tech, eqs]) => eqs.length > 0 && (
                        <DendroNode key={`boe-${tech}`} id={`boe-tech-${tech}`}
                          label={`${tech}: ${eqs.length} nodes`}
                          color={T[tech] || T.t3} depth={2} defaultExpanded={false}>
                          {eqs.slice(0, 6).map(eq => (
                            <DendroNode key={`boe-${eq.name}`} id={`boe-${eq.name}`}
                              label={`📍 ${eq.name || 'Unnamed'} [${eq.level || 'N/A'}]`}
                              color={T[tech] || T.t3} depth={3} defaultExpanded={false} />
                          ))}
                        </DendroNode>
                      ))}
                    </DendroNode>
                  </DendroNode>
                </div>
              </div>

              <div style={{ 
                marginTop: '8px',
                padding: '6px 10px',
                background: T.s2,
                borderRadius: '4px',
                border: `1px solid ${T.b}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '4px'
              }}>
                <div style={{ fontSize: '0.6rem', color: T.t2 }}>
                  💡 <strong>Tip:</strong> Hover over any operation (🔧) to see details
                </div>
                <div style={{ display: 'flex', gap: '6px', fontSize: '0.55rem', color: T.t3 }}>
                  <span>📊 Total: {allOps.length} operations</span>
                  <span>📁 {functionalGroups.length} groups</span>
                  <span>🏭 {workCenters.length} work centers</span>
                </div>
              </div>
            </div>
          )}

          {/* PRODUCT */}
          {tab === 'product' && (
            <div style={S.card}>
              <div style={S.cHdr}><div style={S.cTtl}><Package size={12} color={T.pl} />Product & WP</div></div>
              <div style={S.grid(3)}>
                <div><div style={S.label}>Name</div><TooltipInput value={prod?.name || ''} onChange={e => setProd({ ...prod, name: e.target.value })} placeholder="Product Name" style={{ width: '100%' }} /></div>
                <div><div style={S.label}>Description</div><TooltipInput value={prod?.description || ''} onChange={e => setProd({ ...prod, description: e.target.value })} placeholder="Description" style={{ width: '100%' }} /></div>
                <div><div style={S.label}>Revision</div><TooltipInput value={prod?.revision || 'A'} onChange={e => setProd({ ...prod, revision: e.target.value })} placeholder="Revision" style={{ width: '100%' }} /></div>
              </div>
              <div style={{ ...S.grid(3), marginTop: '4px' }}>
                <div><div style={S.label}>MBOM Name</div><TooltipInput value={prod?.mbomName || ''} onChange={e => setProd({ ...prod, mbomName: e.target.value })} placeholder="MBOM Name" style={{ width: '100%' }} /></div>
                <div><div style={S.label}>MBOM Rev</div><TooltipInput value={prod?.mbomRev || '000'} onChange={e => setProd({ ...prod, mbomRev: e.target.value })} placeholder="MBOM Rev" style={{ width: '100%' }} /></div>
                <div><div style={S.label}>BOP Name</div><TooltipInput value={prod?.bopName || ''} onChange={e => setProd({ ...prod, bopName: e.target.value })} placeholder="BOP Name" style={{ width: '100%' }} /></div>
              </div>
              <div style={{ ...S.grid(4), marginTop: '4px' }}>
                <div><div style={S.label}>WP ID</div><TooltipInput value={wp?.id || ''} onChange={e => setWp({ ...wp, id: e.target.value })} placeholder="WP ID" style={{ width: '100%' }} /></div>
                <div><div style={S.label}>WP Name</div><TooltipInput value={wp?.name || ''} onChange={e => setWp({ ...wp, name: e.target.value })} placeholder="WP Name" style={{ width: '100%' }} /></div>
                <div>
                  <div style={S.label}>Plant</div>
                  <select style={S.select} value={wp?.plant || 'JEDDAH'} onChange={e => setWp({ ...wp, plant: e.target.value })}>
                    {plants.map(p => <option key={p.code || p} value={p.code || p}>{p.name || p}</option>)}
                  </select>
                </div>
                <div><div style={S.label}>Owner</div><TooltipInput value={wp?.owner || ''} onChange={e => setWp({ ...wp, owner: e.target.value })} placeholder="Owner" style={{ width: '100%' }} /></div>
              </div>
              <div style={{ marginTop: '6px' }}>
                <div style={S.grid(5)}>
                  {Object.entries(inc).map(([k, v]) => (
                    <div key={k} onClick={() => setInc({ ...inc, [k]: !v })}
                      style={{
                        padding: '4px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        borderRadius: '4px',
                        border: `1px solid ${v ? T.g : T.b}`,
                        background: v ? `${T.g}08` : T.s
                      }}>
                      <div style={{ fontWeight: 600, fontSize: '0.68rem', color: v ? T.t : T.t3 }}>{k.toUpperCase()}</div>
                      <div style={{ fontSize: '0.54rem', color: v ? T.g : T.d }}>{v ? '✅' : '❌'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* BOM */}
          {tab === 'bom' && (
            <>
              <div style={{ display: 'flex', gap: '3px', marginBottom: '5px' }}>
                <button onClick={() => setSubTab('ebom')}
                  style={{ ...S.btn, background: subTab === 'ebom' ? T.cy : T.s, color: subTab === 'ebom' ? 'white' : T.t2 }}>
                  📐 EBOM ({Array.isArray(ebom) ? ebom.length : 0})
                </button>
                <button onClick={() => setSubTab('mbom')}
                  style={{ ...S.btn, background: subTab === 'mbom' ? T.pk : T.s, color: subTab === 'mbom' ? 'white' : T.t2 }}>
                  📦 MBOM ({Array.isArray(mbom) ? mbom.length : 0})
                </button>
              </div>
              {subTab === 'ebom' && (
                <div style={S.card}>
                  <EditableTable
                    columns={ebomCols}
                    data={ebom}
                    onUpdate={(i, k, v) => {
                      const u = [...ebom];
                      u[i] = { ...u[i], [k]: v };
                      setEbom(u);
                    }}
                    onDelete={i => setEbom(ebom.filter((_, j) => j !== i))}
                    onAdd={() => setEbom([...ebom, { id: uuidv4(), findNumber: '', name: '', description: '', quantity: 1, uom: 'EA', revision: 'A', makeBuy: 'BUY' }])}
                  />
                </div>
              )}
              {subTab === 'mbom' && (
                <div style={S.card}>
                  <EditableTable
                    columns={mbomCols}
                    data={mbom}
                    onUpdate={(i, k, v) => {
                      const u = [...mbom];
                      u[i] = { ...u[i], [k]: v };
                      setMbom(u);
                    }}
                    onDelete={i => setMbom(mbom.filter((_, j) => j !== i))}
                    onAdd={() => setMbom([...mbom, { id: uuidv4(), findNumber: '', name: '', description: '', quantity: 1, uom: 'EA', revision: 'A', makeBuy: 'BUY', operationReference: '' }])}
                  />
                </div>
              )}
            </>
          )}

          {/* BOP */}
          {tab === 'bop' && (
            <div style={S.card}>
              <div style={S.cHdr}>
                <div style={S.cTtl}><Wrench size={12} color={T.wl} />BOP Structure ({allOps.length} operations)</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => {
                      const newBOP = {
                        productBOP: {
                          name: 'PRODUCT-BOP-NEW-001',
                          description: 'New Product BOP',
                          revision: 'A',
                          processes: [
                            {
                              name: 'New Process',
                              description: 'New Process Description',
                              type: 'ProcessBOP',
                              sequence: '0010',
                              plantBOPs: [
                                {
                                  name: 'Default Plant BOP',
                                  plant: 'JEDDAH',
                                  description: 'Default Plant BOP',
                                  functionalGroups: [
                                    {
                                      name: 'New Functional Group',
                                      description: 'New FG Description',
                                      sequence: '0010',
                                      operations: []
                                    }
                                  ]
                                }
                              ]
                            }
                          ]
                        }
                      };
                      setBopData(newBOP);
                    }} 
                    style={{ ...S.btn, ...S.bPri }}
                  >
                    <Plus size={9} /> Add Product BOP
                  </button>
                </div>
              </div>
              
              {bopData?.productBOP ? (
                <div>
                  <div style={{ 
                    padding: '8px 12px', 
                    background: `${T.cy}15`, 
                    borderRadius: '4px', 
                    marginBottom: '8px',
                    border: `1px solid ${T.cy}44`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Package size={14} color={T.cy} />
                      <span style={{ fontWeight: 700, fontSize: '0.8rem', color: T.cy }}>
                        Product BOP: {bopData.productBOP.name || 'Unnamed'}
                      </span>
                      <span style={{ fontSize: '0.6rem', color: T.t3, marginLeft: 'auto' }}>
                        Rev: {bopData.productBOP.revision || 'A'} | Status: {bopData.productBOP.status || 'Draft'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.65rem', color: T.t2, marginTop: '2px' }}>
                      {bopData.productBOP.description || 'No description'} | Total: {allOps.length} operations
                    </div>
                  </div>
                  
                  {(bopData.productBOP.processes || []).map((process, pi) => (
                    <div key={`process-${pi}`} style={{ 
                      marginLeft: '12px', 
                      marginBottom: '8px',
                      borderLeft: `2px solid ${T.w}44`,
                      paddingLeft: '8px'
                    }}>
                      <div style={{ 
                        padding: '6px 10px', 
                        background: `${T.w}15`, 
                        borderRadius: '4px',
                        border: `1px solid ${T.w}33`,
                        marginBottom: '6px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <GitBranch size={12} color={T.w} />
                          <span style={{ fontWeight: 600, fontSize: '0.75rem', color: T.w }}>
                            Process: {process.name || 'Unnamed Process'}
                          </span>
                          <span style={{ fontSize: '0.55rem', color: T.t3, marginLeft: 'auto' }}>
                            Seq: {process.sequence || 'N/A'} | Type: {process.type || 'ProcessBOP'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.6rem', color: T.t2 }}>
                          {process.description || 'No description'}
                        </div>
                      </div>
                      
                      {(process.plantBOPs || []).map((plantBOP, pbi) => (
                        <div key={`plant-${pi}-${pbi}`} style={{ 
                          marginLeft: '16px', 
                          marginBottom: '6px',
                          borderLeft: `2px solid ${T.g}44`,
                          paddingLeft: '8px'
                        }}>
                          <div style={{ 
                            padding: '4px 8px', 
                            background: `${T.g}15`, 
                            borderRadius: '4px',
                            border: `1px solid ${T.g}33`,
                            marginBottom: '4px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Factory size={11} color={T.g} />
                              <span style={{ fontWeight: 600, fontSize: '0.7rem', color: T.g }}>
                                Plant BOP: {plantBOP.name || 'Unnamed Plant'}
                              </span>
                              <span style={{ fontSize: '0.55rem', color: T.t3, marginLeft: 'auto' }}>
                                Plant: {plantBOP.plant || 'N/A'}
                              </span>
                            </div>
                          </div>
                          
                          {(plantBOP.functionalGroups || []).map((fg, fgi) => (
                            <div key={`fg-${pi}-${pbi}-${fgi}`} style={{ 
                              marginLeft: '16px', 
                              marginBottom: '4px',
                              borderLeft: `2px solid ${T.pp}44`,
                              paddingLeft: '8px'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '3px 6px',
                                background: `${T.pp}10`,
                                borderRadius: '3px',
                                marginBottom: '3px'
                              }}>
                                <span style={{ fontWeight: 600, fontSize: '0.7rem', color: T.pp }}>
                                  📁 FG: {fg.name || 'Unnamed'} (Seq: {fg.sequence || 'N/A'})
                                </span>
                                <span style={{ fontSize: '0.55rem', color: T.t3 }}>
                                  {fg.operations?.length || 0} ops
                                </span>
                              </div>
                              
                              <EditableTable
                                columns={opCols}
                                data={fg.operations || []}
                                onUpdate={(i, k, v) => {
                                  const newData = { ...bopData };
                                  const ops = newData.productBOP.processes[pi].plantBOPs[pbi].functionalGroups[fgi].operations;
                                  ops[i] = { ...ops[i], [k]: v };
                                  setBopData(newData);
                                }}
                                onDelete={(i) => {
                                  const newData = { ...bopData };
                                  const ops = newData.productBOP.processes[pi].plantBOPs[pbi].functionalGroups[fgi].operations;
                                  ops.splice(i, 1);
                                  setBopData(newData);
                                }}
                                onAdd={() => {
                                  const newData = { ...bopData };
                                  const ops = newData.productBOP.processes[pi].plantBOPs[pbi].functionalGroups[fgi].operations;
                                  ops.push({
                                    id: uuidv4(),
                                    sequence: '',
                                    name: '',
                                    description: '',
                                    workCenter: '',
                                    standardTime: 0,
                                    setupTime: 0,
                                    tools: '',
                                    skills: '',
                                    controlKey: '',
                                    machine: '',
                                    tooling: '',
                                    parameter: '',
                                    consumes: '',
                                    indirectConsumables: '',
                                    byproducts: '',
                                    setupTimeDetail: '',
                                    machineRunTime: '',
                                    directLaborTime: '',
                                    skillRequired: '',
                                    workInstruction: '',
                                    inspection: '',
                                    sourceLocation: '',
                                    output: '',
                                    queTime: 0,
                                    moveTime: 0,
                                    precedes: '',
                                    mbomRef: '',
                                    ebomRef: '',
                                    qualityPlanRef: '',
                                    ewiRef: '',
                                    operationType: '',
                                    category: '',
                                    subCategory: '',
                                    scrap: '',
                                    directMBOM: ''
                                  });
                                  setBopData(newData);
                                }}
                                addLabel="Add Operation"
                                emptyMessage="No operations in this functional group"
                              />
                            </div>
                          ))}
                          
                          <button 
                            onClick={() => {
                              const newData = { ...bopData };
                              const fgs = newData.productBOP.processes[pi].plantBOPs[pbi].functionalGroups;
                              fgs.push({
                                name: `FG-${fgs.length + 1}`,
                                description: '',
                                sequence: `${(fgs.length + 1) * 10}`,
                                operations: []
                              });
                              setBopData(newData);
                            }}
                            style={{ ...S.btn, ...S.bGho, marginLeft: '16px', marginTop: '4px', fontSize: '0.65rem' }}
                          >
                            <Plus size={8} /> Add FG
                          </button>
                        </div>
                      ))}
                      
                      <button 
                        onClick={() => {
                          const newData = { ...bopData };
                          const plants = newData.productBOP.processes[pi].plantBOPs;
                          plants.push({
                            name: `Plant BOP ${plants.length + 1}`,
                            plant: 'JEDDAH',
                            description: '',
                            functionalGroups: []
                          });
                          setBopData(newData);
                        }}
                        style={{ ...S.btn, ...S.bGho, marginLeft: '12px', marginTop: '4px', fontSize: '0.65rem' }}
                      >
                        <Plus size={8} /> Add Plant BOP
                      </button>
                    </div>
                  ))}
                  
                  <button 
                    onClick={() => {
                      const newData = { ...bopData };
                      if (!newData.productBOP.processes) newData.productBOP.processes = [];
                      newData.productBOP.processes.push({
                        name: `Process ${newData.productBOP.processes.length + 1}`,
                        description: '',
                        type: 'ProcessBOP',
                        sequence: `${(newData.productBOP.processes.length + 1) * 10}`,
                        plantBOPs: []
                      });
                      setBopData(newData);
                    }}
                    style={{ ...S.btn, ...S.bGho, marginTop: '4px' }}
                  >
                    <Plus size={8} /> Add Process
                  </button>
                </div>
              ) : bopData?.functionalGroups ? (
                <div>
                  <div style={{ color: T.w, fontSize: '0.7rem', marginBottom: '6px' }}>
                    ⚠️ Legacy BOP structure detected. Consider migrating to ProductBOP → ProcessBOP → PlantBOP.
                  </div>
                  {(bopData.functionalGroups || []).map((fg, fi) => (
                    <div key={fi} style={{ marginTop: '3px' }}>
                      <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                        <TooltipInput value={fg.name || ''} onChange={e => {
                          const fgs = [...bopData.functionalGroups];
                          fgs[fi] = { ...fgs[fi], name: e.target.value };
                          setBopData({ functionalGroups: fgs });
                        }} placeholder="Group Name" style={{ width: '140px' }} />
                        <TooltipInput value={fg.description || ''} onChange={e => {
                          const fgs = [...bopData.functionalGroups];
                          fgs[fi] = { ...fgs[fi], description: e.target.value };
                          setBopData({ functionalGroups: fgs });
                        }} placeholder="Description" style={{ flex: 1 }} />
                        <button onClick={() => {
                          const fgs = bopData.functionalGroups.filter((_, j) => j !== fi);
                          setBopData({ functionalGroups: fgs });
                        }} style={{ ...S.bGho, padding: '1px 3px' }}><Trash2 size={9} /></button>
                      </div>
                      <EditableTable
                        columns={opCols}
                        data={fg.operations || []}
                        onUpdate={(i, k, v) => {
                          const fgs = [...bopData.functionalGroups];
                          const ops = [...fgs[fi].operations];
                          ops[i] = { ...ops[i], [k]: v };
                          fgs[fi] = { ...fgs[fi], operations: ops };
                          setBopData({ functionalGroups: fgs });
                        }}
                        onDelete={(i) => {
                          const fgs = [...bopData.functionalGroups];
                          const ops = fgs[fi].operations.filter((_, j) => j !== i);
                          fgs[fi] = { ...fgs[fi], operations: ops };
                          setBopData({ functionalGroups: fgs });
                        }}
                        onAdd={() => {
                          const fgs = [...bopData.functionalGroups];
                          const ops = [...(fgs[fi].operations || [])];
                          ops.push({
                            id: uuidv4(),
                            sequence: '',
                            name: '',
                            description: '',
                            workCenter: '',
                            standardTime: 0,
                            setupTime: 0,
                            tools: '',
                            skills: '',
                            controlKey: '',
                            machine: '',
                            tooling: '',
                            parameter: '',
                            consumes: '',
                            indirectConsumables: '',
                            byproducts: '',
                            setupTimeDetail: '',
                            machineRunTime: '',
                            directLaborTime: '',
                            skillRequired: '',
                            workInstruction: '',
                            inspection: '',
                            sourceLocation: '',
                            output: '',
                            queTime: 0,
                            moveTime: 0,
                            precedes: '',
                            mbomRef: '',
                            ebomRef: '',
                            qualityPlanRef: '',
                            ewiRef: '',
                            operationType: '',
                            category: '',
                            subCategory: '',
                            scrap: '',
                            directMBOM: ''
                          });
                          fgs[fi] = { ...fgs[fi], operations: ops };
                          setBopData({ functionalGroups: fgs });
                        }}
                        addLabel="Add Operation"
                        emptyMessage="No operations in this group"
                      />
                    </div>
                  ))}
                  <button 
                    onClick={() => {
                      const fgs = [...(bopData.functionalGroups || [])];
                      fgs.push({ 
                        name: `FG-${fgs.length + 1}`, 
                        description: '', 
                        operations: [] 
                      });
                      setBopData({ functionalGroups: fgs });
                    }}
                    style={{ ...S.btn, ...S.bGho, marginTop: '4px' }}
                  >
                    <Plus size={8} /> Add FG
                  </button>
                </div>
              ) : (
                <div style={{ color: T.t3, padding: '16px', textAlign: 'center' }}>
                  No BOP data found. Click "Add Product BOP" to create one.
                </div>
              )}
            </div>
          )}

          {/* BOE */}
          {tab === 'boe' && (
            <div style={S.card}>
              <div style={S.cHdr}>
                <div style={S.cTtl}><Cpu size={12} color={T.ppl} />BOE ({Array.isArray(equip) ? equip.length : 0})</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button 
                    onClick={() => {
                      const newEquip = [...equip];
                      newEquip.push({
                        id: uuidv4(),
                        name: '',
                        level: 'WorkUnit',
                        description: '',
                        parent: '',
                        technology: 'Conventional',
                        costRate: 0,
                        capacity: 0,
                        bmideType: 'MESWorkUnit'
                      });
                      setEquip(newEquip);
                    }}
                    style={{ ...S.btn, ...S.bPP }}
                  >
                    <Plus size={9} /> Add
                  </button>
                </div>
              </div>
              <EditableTable
                columns={equipCols}
                data={equip}
                onUpdate={(i, k, v) => {
                  const u = [...equip];
                  u[i] = { ...u[i], [k]: v };
                  setEquip(u);
                }}
                onDelete={i => setEquip(equip.filter((_, j) => j !== i))}
              />
            </div>
          )}

          {/* TOOLS */}
          {tab === 'tools' && (
            <div style={S.card}>
              <div style={S.cHdr}>
                <div style={S.cTtl}><Box size={12} color={T.tel} />Tools ({Array.isArray(tools) ? tools.length : 0})</div>
                <button 
                  onClick={() => {
                    const newTools = [...tools];
                    newTools.push({
                      id: uuidv4(),
                      name: '',
                      type: '',
                      description: '',
                      quantity: 1,
                      calibrationDue: '',
                      manufacturer: '',
                      cost: 0
                    });
                    setTools(newTools);
                  }}
                  style={{ ...S.btn, ...S.bTeal }}
                >
                  <Plus size={9} /> Add
                </button>
              </div>
              <EditableTable
                columns={toolCols}
                data={tools}
                onUpdate={(i, k, v) => {
                  const u = [...tools];
                  u[i] = { ...u[i], [k]: v };
                  setTools(u);
                }}
                onDelete={i => setTools(tools.filter((_, j) => j !== i))}
              />
            </div>
          )}

          {/* SKILLS */}
          {tab === 'skills' && (
            <div style={S.card}>
              <div style={S.cHdr}>
                <div style={S.cTtl}><Users size={12} color={T.pl} />Skills ({Array.isArray(skills) ? skills.length : 0})</div>
                <button 
                  onClick={() => {
                    const newSkills = [...skills];
                    newSkills.push({
                      id: uuidv4(),
                      name: '',
                      description: '',
                      level: 1,
                      validityMonths: 24,
                      certificationBody: '',
                      requiresRecert: false
                    });
                    setSkills(newSkills);
                  }}
                  style={{ ...S.btn, ...S.bPri }}
                >
                  <Plus size={9} /> Add
                </button>
              </div>
              <EditableTable
                columns={skillCols}
                data={skills}
                onUpdate={(i, k, v) => {
                  const u = [...skills];
                  u[i] = { ...u[i], [k]: v };
                  setSkills(u);
                }}
                onDelete={i => setSkills(skills.filter((_, j) => j !== i))}
              />
            </div>
          )}

          {/* QUALITY */}
          {tab === 'quality' && (
            <div style={S.card}>
              <div style={S.cHdr}>
                <div style={S.cTtl}><Target size={12} color={T.dl} />Quality Plans ({Array.isArray(qualityPlans) ? qualityPlans.length : 0})</div>
                <button 
                  onClick={() => {
                    setQualityPlans([...qualityPlans, { 
                      name: 'New Quality Plan', 
                      description: '', 
                      targetOperation: '', 
                      inspectionFrequency: '',
                      characteristics: [] 
                    }]);
                  }} 
                  style={{ ...S.btn, ...S.bPri }}
                >
                  <Plus size={9} /> Add Plan
                </button>
              </div>
              {(Array.isArray(qualityPlans) ? qualityPlans : []).map((qp, qi) => (
                <div key={qi} style={{ marginTop: '6px', padding: '8px', background: T.s, borderRadius: '4px', border: `1px solid ${T.b}` }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <div style={S.label}>Plan Name</div>
                      <TooltipInput 
                        value={qp.name || ''} 
                        onChange={e => {
                          const q = [...qualityPlans];
                          q[qi] = { ...q[qi], name: e.target.value };
                          setQualityPlans(q);
                        }} 
                        placeholder="Plan Name" 
                        style={{ width: '100%' }} 
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <div style={S.label}>Description</div>
                      <TooltipInput 
                        value={qp.description || ''} 
                        onChange={e => {
                          const q = [...qualityPlans];
                          q[qi] = { ...q[qi], description: e.target.value };
                          setQualityPlans(q);
                        }} 
                        placeholder="Description" 
                        style={{ width: '100%' }} 
                      />
                    </div>
                    <div style={{ width: '120px' }}>
                      <div style={S.label}>Target Op</div>
                      <TooltipInput 
                        value={qp.targetOperation || ''} 
                        onChange={e => {
                          const q = [...qualityPlans];
                          q[qi] = { ...q[qi], targetOperation: e.target.value };
                          setQualityPlans(q);
                        }} 
                        placeholder="Op" 
                        style={{ width: '100%' }} 
                      />
                    </div>
                    <div style={{ width: '120px' }}>
                      <div style={S.label}>Frequency</div>
                      <TooltipInput 
                        value={qp.inspectionFrequency || ''} 
                        onChange={e => {
                          const q = [...qualityPlans];
                          q[qi] = { ...q[qi], inspectionFrequency: e.target.value };
                          setQualityPlans(q);
                        }} 
                        placeholder="Freq" 
                        style={{ width: '100%' }} 
                      />
                    </div>
                    <button 
                      onClick={() => {
                        const q = [...qualityPlans];
                        q.splice(qi, 1);
                        setQualityPlans(q);
                      }} 
                      style={{ ...S.bGho, padding: '2px 6px', alignSelf: 'flex-end', marginBottom: '2px' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  
                  <div style={{ marginTop: '4px', paddingLeft: '12px', borderLeft: `2px solid ${T.pp}33` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '0.6rem', fontWeight: 600, color: T.t2 }}>Characteristics:</span>
                      <button 
                        onClick={() => {
                          const q = [...qualityPlans];
                          if (!q[qi].characteristics) q[qi].characteristics = [];
                          q[qi].characteristics.push({ 
                            name: 'New Characteristic', 
                            description: '',
                            tolerance: '',
                            method: '',
                            frequency: ''
                          });
                          setQualityPlans(q);
                        }} 
                        style={{ ...S.btn, ...S.bGho, padding: '1px 6px', fontSize: '0.55rem' }}
                      >
                        <Plus size={8} /> Add Char
                      </button>
                      <span style={{ fontSize: '0.5rem', color: T.t3 }}>({qp.characteristics?.length || 0})</span>
                    </div>
                    
                    {(qp.characteristics || []).length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ ...S.tbl, fontSize: '0.6rem' }}>
                          <thead>
                            <tr>
                              <th style={S.th}>Name</th>
                              <th style={S.th}>Description</th>
                              <th style={S.th}>Tolerance</th>
                              <th style={S.th}>Method</th>
                              <th style={S.th}>Frequency</th>
                              <th style={{ ...S.th, width: '30px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(qp.characteristics || []).map((qc, qci) => (
                              <tr key={qci}>
                                <td style={S.td}>
                                  <TooltipInput 
                                    value={qc.name || ''} 
                                    onChange={e => {
                                      const q = [...qualityPlans];
                                      q[qi].characteristics[qci].name = e.target.value;
                                      setQualityPlans(q);
                                    }} 
                                    placeholder="Name" 
                                    style={{ width: '100%', minWidth: '80px' }} 
                                  />
                                </td>
                                <td style={S.td}>
                                  <TooltipInput 
                                    value={qc.description || ''} 
                                    onChange={e => {
                                      const q = [...qualityPlans];
                                      q[qi].characteristics[qci].description = e.target.value;
                                      setQualityPlans(q);
                                    }} 
                                    placeholder="Desc" 
                                    style={{ width: '100%', minWidth: '80px' }} 
                                  />
                                </td>
                                <td style={S.td}>
                                  <TooltipInput 
                                    value={qc.tolerance || ''} 
                                    onChange={e => {
                                      const q = [...qualityPlans];
                                      q[qi].characteristics[qci].tolerance = e.target.value;
                                      setQualityPlans(q);
                                    }} 
                                    placeholder="Tolerance" 
                                    style={{ width: '80px' }} 
                                  />
                                </td>
                                <td style={S.td}>
                                  <TooltipInput 
                                    value={qc.method || ''} 
                                    onChange={e => {
                                      const q = [...qualityPlans];
                                      q[qi].characteristics[qci].method = e.target.value;
                                      setQualityPlans(q);
                                    }} 
                                    placeholder="Method" 
                                    style={{ width: '80px' }} 
                                  />
                                </td>
                                <td style={S.td}>
                                  <TooltipInput 
                                    value={qc.frequency || ''} 
                                    onChange={e => {
                                      const q = [...qualityPlans];
                                      q[qi].characteristics[qci].frequency = e.target.value;
                                      setQualityPlans(q);
                                    }} 
                                    placeholder="Freq" 
                                    style={{ width: '70px' }} 
                                  />
                                </td>
                                <td style={S.td}>
                                  <button 
                                    onClick={() => {
                                      const q = [...qualityPlans];
                                      q[qi].characteristics.splice(qci, 1);
                                      setQualityPlans(q);
                                    }} 
                                    style={{ ...S.bGho, padding: '1px 3px', fontSize: '0.5rem' }}
                                  >
                                    <X size={8} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.55rem', color: T.t3, padding: '4px 0' }}>No characteristics defined</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EWI */}
          {tab === 'ewi' && (
            <div style={S.card}>
              <div style={S.cHdr}>
                <div style={S.cTtl}><FileText size={12} color={T.wl} />EWI ({Array.isArray(ewiDocs) ? ewiDocs.length : 0})</div>
                <button 
                  onClick={() => {
                    const newEwi = [...ewiDocs];
                    newEwi.push({
                      id: `EWI-${ewiDocs.length + 1}`,
                      name: '',
                      type: 'PDF',
                      description: '',
                      targetOperation: '',
                      revision: 'A',
                      author: '',
                      approvalDate: ''
                    });
                    setEwiDocs(newEwi);
                  }}
                  style={{ ...S.btn, ...S.bWar }}
                >
                  <Plus size={9} /> Add
                </button>
              </div>
              <EditableTable
                columns={[
                  { key: 'id', label: 'ID', editable: true },
                  { key: 'name', label: 'Name', editable: true },
                  { key: 'type', label: 'Type', editable: true, type: 'select', options: ['PDF', 'WORD', 'PPT', 'VIDEO', '3D'] },
                  { key: 'description', label: 'Description', editable: true },
                  { key: 'targetOperation', label: 'Target Op', editable: true },
                  { key: 'revision', label: 'Rev', editable: true },
                  { key: 'author', label: 'Author', editable: true },
                  { key: 'approvalDate', label: 'Approval', editable: true }
                ]}
                data={ewiDocs}
                onUpdate={(i, k, v) => {
                  const u = [...ewiDocs];
                  u[i] = { ...u[i], [k]: v };
                  setEwiDocs(u);
                }}
                onDelete={i => setEwiDocs(ewiDocs.filter((_, j) => j !== i))}
              />
            </div>
          )}

          {/* IMPORT/EXPORT */}
          {tab === 'import' && (
            <div>
              <div style={{ ...S.card, marginBottom: '6px' }}>
                <div style={S.cHdr}>
                  <div style={S.cTtl}><Upload size={12} color={T.pl} />Import Data</div>
                  <span style={{ fontSize: '0.6rem', color: T.t3 }}>Supports CSV, Excel, JSON, BMIDE</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button onClick={() => fileRef.current?.click()} style={{ ...S.btn, ...S.bPri }}>
                    <Upload size={10} /> Upload File
                  </button>
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.json,.bmide,.xml"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadedFileName(file.name);
                      parseFileContent(file, handleParsedData, err => st(`❌ ${err}`, 'error'));
                      e.target.value = '';
                    }} />
                  <button onClick={() => setBmideUploaded(false)} style={{ ...S.btn, ...S.bGho }}>
                    <FileJson size={10} /> BMIDE: {bmideUploaded ? '✅' : '❌'}
                  </button>
                  <button onClick={() => downloadTemplate('full')} style={{ ...S.btn, ...S.bTeal }}>
                    <FileSpreadsheet size={10} /> Template
                  </button>
                </div>
              </div>

              {importPreview && importedData && (
                <div style={{ ...S.card, border: `1px solid ${T.pp}44` }}>
                  <div style={S.cHdr}>
                    <div style={S.cTtl}><Eye size={12} color={T.ppl} />Preview: {uploadedFileName}</div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={applyImportedData} style={{ ...S.btn, ...S.bSuc }}><Check size={10} />Apply</button>
                      <button onClick={() => { setImportPreview(false); setImportedData(null); setUploadedFileName(''); }}
                        style={{ ...S.btn, ...S.bGho }}><X size={10} />Cancel</button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: T.t2 }}>
                    <span>📊 {importSummary.totalRows} rows, {importSummary.sheets} sheets</span>
                    {Object.entries(importSummary.sheetDetails).map(([k, v]) => (
                      <span key={k} style={{ marginLeft: '8px' }}>• {k}: {v}</span>
                    ))}
                  </div>
                  <div style={{ maxHeight: '200px', overflow: 'auto', marginTop: '4px', background: T.s, borderRadius: '4px', padding: '4px' }}>
                    <pre style={{ fontSize: '0.6rem', color: T.t2, margin: 0, whiteSpace: 'pre-wrap' }}>
                      {importedData.type === 'json' ? JSON.stringify(importedData.data, null, 2).slice(0, 2000) :
                       importedData.type === 'csv' ? Papa.unparse(importedData.data.slice(0, 10)) :
                       importedData.type === 'excel' ? JSON.stringify(Object.keys(importedData.data), null, 2) :
                       importedData.type === 'bmide' ? JSON.stringify(importedData.data.slice(0, 10), null, 2) :
                       'Preview not available'}
                    </pre>
                  </div>
                </div>
              )}

              {importHistory.length > 0 && (
                <div style={S.card}>
                  <div style={S.cHdr}>
                    <div style={S.cTtl}><Clock size={12} color={T.wl} />Import History</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {importHistory.map((h, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '0.6rem', color: T.t2, padding: '2px 4px', background: i % 2 === 0 ? T.s : 'transparent' }}>
                        <span>{h.timestamp}</span>
                        <span>{h.fileName}</span>
                        <span>{h.rowsImported} rows</span>
                        <span>{h.sheets} sheets</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ ...S.card }}>
                <div style={S.cHdr}>
                  <div style={S.cTtl}><Download size={12} color={T.tel} />Export Templates</div>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {['ebom', 'mbom', 'bop', 'boe', 'tools', 'skills'].map(type => (
                    <button key={type} onClick={() => downloadTemplate(type)} style={{ ...S.btn, ...S.bGho, fontSize: '0.6rem' }}>
                      <FileText size={8} /> {type.toUpperCase()}
                    </button>
                  ))}
                  <button onClick={() => downloadTemplate('full')} style={{ ...S.btn, ...S.bPP }}>
                    <FileSpreadsheet size={10} /> Excel Complete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* INTEGRATED DASHBOARD */}
          {tab === 'integrated' && (
            <IntegratedDashboard cfg={vizCfg} />
          )}

          {/* SETTINGS */}
          {tab === 'settings' && (
            <div>
              <div style={S.card}>
                <div style={S.cHdr}><div style={S.cTtl}><Settings size={12} color={T.pl} />BMIDE Types</div></div>
                <div style={S.grid(3)}>
                  {Object.entries(bt).map(([k, v]) => (
                    <div key={k}>
                      <div style={S.label}>{k.replace(/([A-Z])/g, ' $1').trim()}</div>
                      <TooltipInput value={v || ''} onChange={e => setBt({ ...bt, [k]: e.target.value })} placeholder="Type" style={{ width: '100%' }} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={S.card}>
                <div style={S.cHdr}><div style={S.cTtl}><Factory size={12} color={T.g} />Plants</div></div>
                <EditableTable
                  columns={[
                    { key: 'code', label: 'Code', editable: true },
                    { key: 'name', label: 'Name', editable: true },
                    { key: 'description', label: 'Description', editable: true },
                    { key: 'country', label: 'Country', editable: true },
                    { key: 'timezone', label: 'TZ', editable: true },
                    { key: 'active', label: 'Active', editable: true, type: 'select', options: ['true', 'false'] }
                  ]}
                  data={plants}
                  onUpdate={(i, k, v) => {
                    const u = [...plants];
                    u[i] = { ...u[i], [k]: k === 'active' ? v === 'true' : v };
                    setPlants(u);
                  }}
                  onDelete={i => setPlants(plants.filter((_, j) => j !== i))}
                  onAdd={() => setPlants([...plants, { code: '', name: '', description: '', country: '', timezone: '', active: true, capabilities: [] }])}
                />
              </div>
            </div>
          )}
          
          {/* PRODUCT GENERATOR */}
          {tab === 'productGenerator' && (
            <DEManyproduct onDataGenerated={handleProductDataGenerated} />
          )}
		  
{/* DIGITAL GUARDRAILS */}
{tab === 'digitalGuardrails' && (
  <DigitalEngineeringGuardrailDashboard 
    artifacts={{
      product: prod,
      engineeringSpecs: prod?.engineeringSpecs || {},
      ebom: ebom,
      mbom: mbom,
      bopData: bopData,
      equip: equip,
      tools: tools,
      skills: skills,
      qualityPlans: qualityPlans,
      ewiDocs: ewiDocs
    }}
    onUpdate={(updated) => {
      // Apply all fixes to your state
      if (updated.ebom) setEbom(updated.ebom);
      if (updated.mbom) setMbom(updated.mbom);
      if (updated.bopData) setBopData(updated.bopData);
      if (updated.equip) setEquip(updated.equip);
      if (updated.tools) setTools(updated.tools);
      if (updated.skills) setSkills(updated.skills);
      if (updated.qualityPlans) setQualityPlans(updated.qualityPlans);
      if (updated.ewiDocs) setEwiDocs(updated.ewiDocs);
      if (updated.product) setProd(updated.product);
    }}
  />
)}


{/* ORIGINAL GUARDRAILS (if you want to keep both) */}
{tab === 'guardrails' && (
  <div>
    <GuardrailDashboard 
      artifacts={{
        ebom: ebom,
        mbom: mbom,
        bopData: bopData,
        equip: equip,
        tools: tools,
        skills: skills,
        qualityPlans: qualityPlans,
        ewiDocs: ewiDocs
      }}
      onUpdate={(updated) => {
        if (updated.ebom) setEbom(updated.ebom);
        if (updated.mbom) setMbom(updated.mbom);
        if (updated.bopData) setBopData(updated.bopData);
        if (updated.equip) setEquip(updated.equip);
        if (updated.tools) setTools(updated.tools);
        if (updated.skills) setSkills(updated.skills);
        if (updated.qualityPlans) setQualityPlans(updated.qualityPlans);
        if (updated.ewiDocs) setEwiDocs(updated.ewiDocs);
      }}
    />
  </div>
)}

        </div>
      </div>

      {/* MODALS */}
      {showValidator && xml && (
        <ValidatePLMXML 
          xmlContent={xml}
          onClose={() => setShowValidator(false)}
        />
      )}

      {showPreview && xml && (
        <div style={S.modal}>
          <div style={{ ...S.modalC, width: '90vw', maxWidth: '1100px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontWeight: 700, color: T.pl }}>📄 PLMXML Preview ({objCount} objects)</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={download} style={{ ...S.btn, ...S.bSuc }}><Download size={10} /> Download</button>
                <button onClick={() => setShowPreview(false)} style={{ ...S.btn, ...S.bGho }}><X size={10} /> Close</button>
              </div>
            </div>
            <div style={{ maxHeight: '80vh', overflow: 'auto', background: T.bg, padding: '8px', borderRadius: '4px', border: `1px solid ${T.b}` }}>
              <pre style={{ fontSize: '0.6rem', color: T.t2, margin: 0, whiteSpace: 'pre-wrap' }}>
                {xml}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* REPORT MODAL */}
      {showReport && (
        <Report 
          cfg={vizCfg} 
          onClose={() => setShowReport(false)} 
        />
      )}

      {toast && (
        <div style={{
          ...S.toast,
          background: toast.type === 'error' ? T.d : 
                     toast.type === 'warning' ? T.w : 
                     toast.type === 'info' ? T.cy : T.g,
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.type === 'error' ? <AlertCircle size={12} /> : 
           toast.type === 'warning' ? <AlertTriangle size={12} /> : 
           toast.type === 'info' ? <Info size={12} /> : <CheckCircle2 size={12} />}
          {toast.message}
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0 4px' }}>
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
